// ============================================================================
// SS1 GEOGRAPHY — COMPLETE LEARNING MODULE (22 TOPICS)
// NERDC Scheme of Work — First Term to Third Term
// ============================================================================

export const WAEC_GEOGRAPHY_LEARN_SS1 = [
  {
    "topic": "Introduction to Geography",
    "topicCode": "SS1-GEO-01",
    "module": "Foundations of Geography",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Geography</span> is the study of the Earth's surface, its physical features, human populations, and the interactions between people and their environment. The word comes from the Greek words <span class=\"learn-keyword\">geo</span> (Earth) and <span class=\"learn-keyword\">graphein</span> (to write or describe).\n</div>\n\n<h3 class=\"learn-subheading\">1. Meaning and Definition</h3>\n<p class=\"learn-p\">Geography is defined as the science that studies the Earth's surface, the distribution of natural and human phenomena on it, and the relationships between people and their environment.</p>\n\n<h3 class=\"learn-subheading\">2. Nature of Geography</h3>\n<p class=\"learn-p\">Geography is both a <strong>natural science</strong> (it studies physical features like landforms, climate, and soils) and a <strong>social science</strong> (it studies human activities, population, and settlements). This dual nature makes it a unique and broad discipline.</p>\n\n<h3 class=\"learn-subheading\">3. Scope of Geography</h3>\n<ul class=\"learn-list\">\n  <li><strong>Physical Geography:</strong> Landforms, climate, weather, rivers, soils, vegetation.</li>\n  <li><strong>Human Geography:</strong> Population, settlements, agriculture, transport, industry.</li>\n  <li><strong>Regional Geography:</strong> Detailed study of specific areas or regions.</li>\n  <li><strong>Cartography:</strong> Map-making and interpretation.</li>\n  <li><strong>Environmental Geography:</strong> Human-environment interactions.</li>\n</ul>\n\n<h3 class=\"learn-subheading\">4. Value of Geography</h3>\n<ul class=\"learn-list\">\n  <li>Helps us understand the world around us.</li>\n  <li>Aids in planning and development (infrastructure, agriculture, housing).</li>\n  <li>Informs environmental management and conservation.</li>\n  <li>Useful in navigation and exploration.</li>\n  <li>Essential for understanding climate change and natural disasters.</li>\n  <li>Provides careers in surveying, town planning, meteorology, and cartography.</li>\n</ul>\n\n<h3 class=\"learn-subheading\">5. Branches of Geography</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Branch</th><th>Focus</th></tr></thead>\n    <tbody>\n      <tr><td>Physical Geography</td><td>Natural features: landforms, climate, rivers, soils</td></tr>\n      <tr><td>Human Geography</td><td>People: population, culture, economy, settlements</td></tr>\n      <tr><td>Biogeography</td><td>Distribution of plants and animals</td></tr>\n      <tr><td>Cartography</td><td>Map-making and map reading</td></tr>\n      <tr><td>Climatology</td><td>Long-term atmospheric conditions</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Geography comes from Greek — geo (Earth) + graphein (to write). Know the five branches: physical, human, biogeography, cartography, climatology. Know at least five values of studying geography.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Geography is the science of the Earth's surface, natural features, and human activities. It has two main aspects — physical and human. Its value includes understanding the environment, planning, navigation, and career opportunities.\n</div>\n  ",
    "questions": [
      {
        "q": "The word 'Geography' is derived from Greek words meaning:",
        "o": ["Earth and people", "Earth and write/describe", "Sky and measure", "Land and travel"],
        "a": 1,
        "e": "Geography comes from the Greek words 'geo' (Earth) and 'graphein' (to write or describe). Literally, geography means 'description of the Earth'.",
        "h": "Geo = Earth; graphein = to write/describe.",
        "yr": "SS1"
      },
      {
        "q": "Geography is best defined as:",
        "o": ["The study of rocks and minerals only", "The science that studies the Earth's surface, distribution of phenomena, and human-environment relationships", "The study of weather patterns only", "The study of human history and culture"],
        "a": 1,
        "e": "Geography is the science that studies the Earth's surface, the distribution of natural and human phenomena, and the relationships between people and their environment.",
        "h": "Geography = Earth's surface + distributions + human-environment interactions.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is a branch of Physical Geography?",
        "o": ["Population studies", "Industrial location", "Climatology", "Cultural geography"],
        "a": 2,
        "e": "Climatology (the study of long-term atmospheric conditions) is a branch of physical geography. Population studies and industrial location belong to human geography.",
        "h": "Physical geography includes: landforms, climate, rivers, soils.",
        "yr": "SS1"
      },
      {
        "q": "Human Geography studies:",
        "o": ["Rock formation and soil types", "Weather patterns and climate", "Population, settlements, agriculture, and transport", "Landforms and drainage"],
        "a": 2,
        "e": "Human geography focuses on people and their activities — including population distribution, settlements, agriculture, industry, transport, and cultural patterns.",
        "h": "Human geography = people and their activities.",
        "yr": "SS1"
      },
      {
        "q": "Geography is considered both a natural science and a social science because:",
        "o": ["It studies rocks and people equally", "It studies physical features of the Earth and also human activities and environments", "It is taught in both arts and science schools", "It uses both maps and graphs"],
        "a": 1,
        "e": "Geography bridges natural science (studying physical features: landforms, climate, rivers) and social science (studying human activities: population, economy, culture). This dual nature makes it unique.",
        "h": "Physical = natural science; human = social science.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is a value of studying geography?",
        "o": ["It helps us understand only the history of ancient civilisations", "It aids in planning and development, navigation, and environmental management", "It is only useful for map-drawing professions", "It has no practical real-world applications"],
        "a": 1,
        "e": "Geography is valuable for planning and development, environmental management, navigation, understanding climate change, and careers in surveying, town planning, and meteorology.",
        "h": "Geography values: planning, navigation, environment, careers.",
        "yr": "SS1"
      },
      {
        "q": "Cartography as a branch of geography is concerned with:",
        "o": ["Study of plants and animals", "Map-making and map interpretation", "Study of the atmosphere", "Study of human settlements"],
        "a": 1,
        "e": "Cartography is the science and art of map-making and map reading. It is a fundamental tool in geography for representing Earth's features on paper or digitally.",
        "h": "Cartography = map-making and map reading.",
        "yr": "SS1"
      },
      {
        "q": "Regional Geography involves:",
        "o": ["Studying only physical features worldwide", "Detailed study of specific areas or regions of the world", "Mapping weather patterns globally", "Studying only human activities in cities"],
        "a": 1,
        "e": "Regional geography focuses on the detailed study of specific geographical areas or regions, examining both physical and human characteristics of that particular place.",
        "h": "Regional geography = detailed study of specific areas/regions.",
        "yr": "SS1"
      },
      {
        "q": "Biogeography is the branch of geography that studies:",
        "o": ["Human population patterns", "The distribution of plants and animals", "Geological rock formations", "Economic activities of nations"],
        "a": 1,
        "e": "Biogeography studies the geographical distribution of plants (phytogeography) and animals (zoogeography) across the Earth's surface and the factors that influence these distributions.",
        "h": "Biogeography = distribution of plants and animals.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following careers is directly related to geography?",
        "o": ["Pharmacist", "Town planner", "Surgeon", "Historian"],
        "a": 1,
        "e": "Town planning is directly related to geography — it involves using geographical knowledge to design and organise settlements, land use, and infrastructure. Other geography careers include surveying, meteorology, and cartography.",
        "h": "Geography careers: town planner, surveyor, meteorologist, cartographer.",
        "yr": "SS1"
      },
      {
        "q": "Environmental Geography studies:",
        "o": ["The history of ancient environments", "The interactions between humans and their natural environment", "Only the physical environment without human influence", "Only pollution and waste management"],
        "a": 1,
        "e": "Environmental geography examines the interactions between human societies and their natural environment, including issues like pollution, deforestation, climate change, and resource use.",
        "h": "Environmental geography = human-environment interactions.",
        "yr": "SS1"
      },
      {
        "q": "The study of geography helps in understanding natural disasters because:",
        "o": ["Geography predicts the exact date of earthquakes", "It provides knowledge of Earth's processes, landforms, and climate patterns that cause disasters", "Geographers can stop natural disasters", "It tells us where to build houses only"],
        "a": 1,
        "e": "Geography provides understanding of Earth's physical processes — tectonic activity, weather systems, river behaviour — that cause natural disasters like earthquakes, floods, and hurricanes, enabling better preparation.",
        "h": "Geography explains Earth's processes = better understanding of disasters.",
        "yr": "SS1"
      },
      {
        "q": "Which aspect of geography is most useful in agriculture and food production?",
        "o": ["Cartography", "Physical geography (soil, climate, relief)", "Historical geography", "Urban geography"],
        "a": 1,
        "e": "Physical geography — particularly soil science, climatology, and relief — is essential for agriculture as it determines which crops can grow where, water availability, and land suitability.",
        "h": "Soil + climate + relief knowledge → agriculture decisions.",
        "yr": "SS1"
      },
      {
        "q": "Who among the following ancient scholars is credited with early geographical work?",
        "o": ["Socrates", "Eratosthenes", "Archimedes", "Pythagoras"],
        "a": 1,
        "e": "Eratosthenes (276–194 BC) was an ancient Greek scholar who calculated the circumference of the Earth and is often called the 'Father of Geography'. He also made one of the first maps of the world.",
        "h": "Eratosthenes = Father of Geography, calculated Earth's circumference.",
        "yr": "SS1"
      },
      {
        "q": "Geography helps in navigation because:",
        "o": ["It teaches how to build ships", "It provides knowledge of locations, distances, directions, and map reading", "It studies ocean animals", "It predicts ocean currents only"],
        "a": 1,
        "e": "Geography equips people with knowledge of spatial relationships, locations, directions, distances, and map reading — all essential for navigation by sea, land, or air.",
        "h": "Geography: locations, directions, distances, maps = navigation.",
        "yr": "SS1"
      },
      {
        "q": "The study of long-term atmospheric conditions such as rainfall patterns and temperature averages is called:",
        "o": ["Meteorology", "Geology", "Climatology", "Hydrology"],
        "a": 2,
        "e": "Climatology is the branch of geography (and atmospheric science) that studies long-term atmospheric conditions — climate patterns including average temperature, rainfall, humidity, and seasonal changes.",
        "h": "Climatology = long-term atmospheric conditions (climate).",
        "yr": "SS1"
      },
      {
        "q": "Which of the following best describes the scope of geography?",
        "o": ["Geography only studies the physical world", "Geography covers physical features, human activities, and their interactions on Earth", "Geography only studies human societies", "Geography is limited to Africa and Europe"],
        "a": 1,
        "e": "The scope of geography covers physical features (landforms, climate, rivers), human activities (settlement, agriculture, industry), and the interactions between people and their environment globally.",
        "h": "Scope of geography = physical + human + interactions.",
        "yr": "SS1"
      },
      {
        "q": "Geography contributes to national development by:",
        "o": ["Providing entertainment through map games", "Informing decisions on resource use, infrastructure planning, and land management", "Only teaching students about foreign countries", "Measuring the age of rocks and fossils"],
        "a": 1,
        "e": "Geography informs government and development agencies on resource distribution, infrastructure planning, land use, environmental management — all critical for national development.",
        "h": "Geography informs planning, resource use, and development decisions.",
        "yr": "SS1"
      },
      {
        "q": "Physical geography is to landforms as human geography is to:",
        "o": ["Rock types", "Soil profiles", "Population and settlements", "Ocean currents"],
        "a": 2,
        "e": "Physical geography studies natural features like landforms; analogously, human geography studies people-related features such as population, settlements, culture, and economic activities.",
        "h": "Physical → landforms; Human → population and settlements.",
        "yr": "SS1"
      },
      {
        "q": "The practical importance of geography in everyday life includes all EXCEPT:",
        "o": ["Helping in route-finding and navigation", "Informing agricultural decisions based on climate and soil", "Treating diseases in hospitals", "Planning cities and settlements"],
        "a": 2,
        "e": "Treating diseases is the domain of medicine/health sciences, not geography. Geography is practically useful for navigation, agriculture, urban planning, disaster management, and resource management.",
        "h": "Geography is NOT used for treating diseases — that is medicine.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "The Earth and Other Planets",
    "topicCode": "SS1-GEO-02",
    "module": "Foundations of Geography",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  The <span class=\"learn-keyword\">Earth</span> is the third planet from the Sun and the only known planet that supports life. It orbits the Sun in the Solar System alongside seven other planets. Understanding the Earth's characteristics and how it compares to other planets is foundational to geography.\n</div>\n\n<h3 class=\"learn-subheading\">1. The Solar System</h3>\n<p class=\"learn-p\">The Solar System consists of the Sun and all celestial bodies that orbit it. The eight planets in order from the Sun are: Mercury, Venus, Earth, Mars, Jupiter, Saturn, Uranus, Neptune.</p>\n<p class=\"learn-p\"><strong>Memory aid:</strong> \"My Very Energetic Mother Just Served Us Noodles\"</p>\n\n<h3 class=\"learn-subheading\">2. Characteristics of the Earth</h3>\n<ul class=\"learn-list\">\n  <li><strong>Shape:</strong> The Earth is an <strong>oblate spheroid</strong> — slightly flattened at the poles and bulging at the equator.</li>\n  <li><strong>Size:</strong> Mean radius ≈ 6,371 km; circumference ≈ 40,075 km at the equator.</li>\n  <li><strong>Distance from Sun:</strong> About 150 million km (1 Astronomical Unit — AU).</li>\n  <li><strong>Atmosphere:</strong> Contains 78% nitrogen, 21% oxygen, and 1% other gases — supports life.</li>\n  <li><strong>Water:</strong> About 71% of Earth's surface is covered by water (oceans, lakes, rivers).</li>\n  <li><strong>Magnetic field:</strong> Protects life from harmful solar radiation.</li>\n  <li><strong>One moon (natural satellite).</strong></li>\n  <li><strong>Average surface temperature:</strong> About 15°C — suitable for life.</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. Comparison with Other Planets</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Planet</th><th>Position</th><th>Key Feature</th></tr></thead>\n    <tbody>\n      <tr><td>Mercury</td><td>1st</td><td>Smallest, no atmosphere, extreme temperatures</td></tr>\n      <tr><td>Venus</td><td>2nd</td><td>Hottest planet, thick CO₂ atmosphere, no life</td></tr>\n      <tr><td>Earth</td><td>3rd</td><td>Only planet with life, liquid water, oxygen atmosphere</td></tr>\n      <tr><td>Mars</td><td>4th</td><td>Red planet, thin CO₂ atmosphere, possible past water</td></tr>\n      <tr><td>Jupiter</td><td>5th</td><td>Largest planet, gas giant, 95 moons</td></tr>\n      <tr><td>Saturn</td><td>6th</td><td>Gas giant, famous rings, least dense planet</td></tr>\n      <tr><td>Uranus</td><td>7th</td><td>Ice giant, rotates on its side</td></tr>\n      <tr><td>Neptune</td><td>8th</td><td>Farthest, strongest winds, ice giant</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">4. Why Earth Supports Life</h3>\n<ul class=\"learn-list\">\n  <li>Located in the <strong>habitable zone</strong> (not too hot, not too cold).</li>\n  <li>Has liquid water on its surface.</li>\n  <li>Has an oxygen-nitrogen atmosphere.</li>\n  <li>Has a protective magnetic field (magnetosphere).</li>\n  <li>Has moderate gravity to retain its atmosphere.</li>\n</ul>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Earth's shape = oblate spheroid (not a perfect sphere). Order of planets: Mercury, Venus, Earth, Mars, Jupiter, Saturn, Uranus, Neptune. Earth is the only planet with liquid water and life. Jupiter is the largest; Mercury is the smallest.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> The Solar System has 8 planets. Earth (3rd from Sun) is unique — oblate spheroid, 71% water surface, oxygen atmosphere, moderate temperature, one moon, and the only known planet supporting life. Jupiter is largest; Mercury is smallest.\n</div>\n  ",
    "questions": [
      {
        "q": "The shape of the Earth is best described as:",
        "o": ["A perfect sphere", "An oblate spheroid", "A flat disc", "A perfect cube"],
        "a": 1,
        "e": "The Earth is an oblate spheroid — slightly flattened at the poles (North and South) and bulging slightly at the equator. It is not a perfect sphere.",
        "h": "Earth = oblate spheroid (flattened at poles, bulging at equator).",
        "yr": "SS1"
      },
      {
        "q": "What percentage of Earth's surface is covered by water?",
        "o": ["29%", "50%", "71%", "90%"],
        "a": 2,
        "e": "About 71% of Earth's surface is covered by water — mainly oceans, with the remaining 29% being land. This is why Earth is sometimes called the 'Blue Planet'.",
        "h": "71% of Earth's surface = water (oceans, lakes, rivers).",
        "yr": "SS1"
      },
      {
        "q": "The correct order of planets from the Sun is:",
        "o": ["Mercury, Earth, Venus, Mars, Jupiter, Saturn, Uranus, Neptune", "Mercury, Venus, Earth, Mars, Jupiter, Saturn, Uranus, Neptune", "Venus, Mercury, Earth, Mars, Saturn, Jupiter, Uranus, Neptune", "Mercury, Venus, Mars, Earth, Jupiter, Saturn, Uranus, Neptune"],
        "a": 1,
        "e": "The correct order is Mercury, Venus, Earth, Mars, Jupiter, Saturn, Uranus, Neptune. Memory aid: 'My Very Energetic Mother Just Served Us Noodles'.",
        "h": "My Very Energetic Mother Just Served Us Noodles = M,V,E,Ma,J,S,U,N.",
        "yr": "SS1"
      },
      {
        "q": "Earth is the only planet known to support life mainly because:",
        "o": ["It is the largest planet in the Solar System", "It has liquid water, an oxygen atmosphere, and is in the habitable zone", "It has the most moons", "It is closest to the Sun"],
        "a": 1,
        "e": "Earth supports life because it is in the habitable zone (not too hot, not too cold), has liquid water, an oxygen-nitrogen atmosphere, and a protective magnetic field.",
        "h": "Life conditions: habitable zone + liquid water + oxygen atmosphere.",
        "yr": "SS1"
      },
      {
        "q": "The largest planet in the Solar System is:",
        "o": ["Saturn", "Uranus", "Neptune", "Jupiter"],
        "a": 3,
        "e": "Jupiter is the largest planet in the Solar System. It is a gas giant with a mass more than twice that of all other planets combined.",
        "h": "Jupiter is the largest planet.",
        "yr": "SS1"
      },
      {
        "q": "Which planet is known as the 'Red Planet'?",
        "o": ["Mercury", "Venus", "Mars", "Jupiter"],
        "a": 2,
        "e": "Mars is called the Red Planet because its surface is covered with iron oxide (rust), giving it a reddish appearance. It is the 4th planet from the Sun.",
        "h": "Mars = the Red Planet (iron oxide surface).",
        "yr": "SS1"
      },
      {
        "q": "The approximate distance between the Earth and the Sun is:",
        "o": ["15 million km", "150 million km", "1,500 million km", "15 km"],
        "a": 1,
        "e": "Earth is about 150 million km (approximately 93 million miles) from the Sun. This distance is called 1 Astronomical Unit (1 AU).",
        "h": "Earth-Sun distance = ~150 million km (1 AU).",
        "yr": "SS1"
      },
      {
        "q": "What is the approximate mean radius of the Earth?",
        "o": ["637 km", "6,371 km", "63,710 km", "637,100 km"],
        "a": 1,
        "e": "The Earth's mean radius is approximately 6,371 km. Its equatorial radius is slightly larger (6,378 km) because it bulges at the equator.",
        "h": "Earth's mean radius ≈ 6,371 km.",
        "yr": "SS1"
      },
      {
        "q": "Venus is the hottest planet in the Solar System because:",
        "o": ["It is closest to the Sun", "It has a thick CO₂ atmosphere that traps heat (greenhouse effect)", "It has no atmosphere", "It has many active volcanoes"],
        "a": 1,
        "e": "Venus has a thick carbon dioxide atmosphere that creates an extreme greenhouse effect, trapping heat and making surface temperatures reach about 465°C — hotter than Mercury despite being farther from the Sun.",
        "h": "Venus: thick CO₂ atmosphere → extreme greenhouse effect → hottest planet.",
        "yr": "SS1"
      },
      {
        "q": "The atmospheric composition of Earth is approximately:",
        "o": ["50% oxygen, 50% nitrogen", "78% nitrogen, 21% oxygen, 1% other gases", "90% nitrogen, 10% oxygen", "100% oxygen"],
        "a": 1,
        "e": "Earth's atmosphere consists of approximately 78% nitrogen, 21% oxygen, and about 1% other gases including argon, CO₂, and water vapour. This composition supports life.",
        "h": "Earth's atmosphere: 78% N₂, 21% O₂, 1% others.",
        "yr": "SS1"
      },
      {
        "q": "Which planet is famous for its prominent ring system?",
        "o": ["Jupiter", "Uranus", "Saturn", "Neptune"],
        "a": 2,
        "e": "Saturn is famous for its spectacular ring system made of ice and rock particles. Although other gas giants have rings, Saturn's are by far the most visible and prominent.",
        "h": "Saturn = famous for its prominent rings.",
        "yr": "SS1"
      },
      {
        "q": "Mercury is the planet with extreme temperatures because:",
        "o": ["It has a very thick atmosphere", "It has no significant atmosphere to retain heat or block cold", "It is the farthest from the Sun", "It has no core"],
        "a": 1,
        "e": "Mercury has virtually no atmosphere to moderate temperature extremes. Dayside temperatures reach about 430°C; night-side temperatures drop to −180°C.",
        "h": "Mercury: no atmosphere → extreme temperature swings.",
        "yr": "SS1"
      },
      {
        "q": "The habitable zone around a star is:",
        "o": ["The area with the most planets", "The region where liquid water can exist on a planet's surface", "The region closest to the star", "The area with the strongest magnetic field"],
        "a": 1,
        "e": "The habitable zone (also called the 'Goldilocks zone') is the range of distances from a star where liquid water can exist on a planet's surface — not too hot, not too cold.",
        "h": "Habitable zone = distance where liquid water can exist on surface.",
        "yr": "SS1"
      },
      {
        "q": "Earth's magnetic field is important because it:",
        "o": ["Creates earthquakes", "Protects life from harmful solar radiation and cosmic rays", "Causes the tides", "Controls the seasons"],
        "a": 1,
        "e": "Earth's magnetic field (magnetosphere) deflects harmful solar wind particles and cosmic radiation, protecting life on Earth. Without it, the atmosphere would slowly be stripped away.",
        "h": "Earth's magnetic field = protects from harmful solar radiation.",
        "yr": "SS1"
      },
      {
        "q": "How many natural satellites (moons) does Earth have?",
        "o": ["0", "1", "2", "4"],
        "a": 1,
        "e": "Earth has one natural satellite — the Moon. Jupiter has the most confirmed moons (95), while Mercury and Venus have none.",
        "h": "Earth has one moon (natural satellite).",
        "yr": "SS1"
      },
      {
        "q": "The smallest planet in the Solar System is:",
        "o": ["Mars", "Venus", "Pluto", "Mercury"],
        "a": 3,
        "e": "Mercury is the smallest planet in the Solar System. (Note: Pluto was reclassified as a dwarf planet in 2006, so it is no longer counted among the 8 planets.)",
        "h": "Mercury is the smallest of the 8 planets.",
        "yr": "SS1"
      },
      {
        "q": "Uranus is described as an 'ice giant' because:",
        "o": ["It is completely covered in snow", "It is composed mainly of water, ammonia, and methane ices with a rocky core", "It orbits a region of space filled with ice", "Its atmosphere is entirely frozen"],
        "a": 1,
        "e": "Uranus is classified as an ice giant because its interior is composed mainly of heavier materials — water, methane, and ammonia ices — unlike the gas giants Jupiter and Saturn which are mostly hydrogen and helium.",
        "h": "Uranus = ice giant (water, methane, ammonia ices interior).",
        "yr": "SS1"
      },
      {
        "q": "The Earth's equatorial circumference is approximately:",
        "o": ["4,007 km", "40,075 km", "400,750 km", "4,000,750 km"],
        "a": 1,
        "e": "The Earth's equatorial circumference is approximately 40,075 km (about 24,901 miles). This is slightly larger than the polar circumference because Earth bulges at the equator.",
        "h": "Earth's equatorial circumference ≈ 40,075 km.",
        "yr": "SS1"
      },
      {
        "q": "Which planet rotates on its side (has an axial tilt of about 98°)?",
        "o": ["Saturn", "Neptune", "Uranus", "Mars"],
        "a": 2,
        "e": "Uranus has an extreme axial tilt of about 97.8°, meaning it essentially rotates on its side. This is thought to have been caused by a massive collision with an Earth-sized object in the distant past.",
        "h": "Uranus rotates on its side (98° axial tilt).",
        "yr": "SS1"
      },
      {
        "q": "Why is Earth sometimes called the 'Blue Planet'?",
        "o": ["Its sky is always blue", "About 71% of its surface is covered by water, giving it a blue appearance from space", "It emits blue light", "Its rocks are mostly blue in colour"],
        "a": 1,
        "e": "Earth is called the Blue Planet because when viewed from space, it appears predominantly blue due to the vast oceans that cover about 71% of its surface.",
        "h": "71% water surface = blue from space = 'Blue Planet'.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Earth's Rotation and Revolution",
    "topicCode": "SS1-GEO-03",
    "module": "Foundations of Geography",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  The Earth has two major movements: <span class=\"learn-keyword\">rotation</span> (spinning on its axis) and <span class=\"learn-keyword\">revolution</span> (orbiting the Sun). These two movements are responsible for day and night, the seasons, and other important geographical phenomena.\n</div>\n\n<h3 class=\"learn-subheading\">1. Rotation</h3>\n<p class=\"learn-p\">Rotation is the spinning of the Earth on its own axis from <strong>west to east</strong> (anticlockwise when viewed from the North Pole). One complete rotation takes approximately <strong>24 hours</strong> (one day).</p>\n<h4>Effects of Rotation:</h4>\n<ul class=\"learn-list\">\n  <li><strong>Day and night:</strong> The part of Earth facing the Sun has day; the part facing away has night.</li>\n  <li><strong>Apparent movement of Sun and stars:</strong> The Sun appears to rise in the east and set in the west.</li>\n  <li><strong>Time zones:</strong> Different longitudes experience different times. The Earth is divided into 24 time zones (15° longitude each).</li>\n  <li><strong>Deflection of winds and ocean currents (Coriolis effect):</strong> To the right in the Northern Hemisphere; to the left in the Southern Hemisphere.</li>\n</ul>\n\n<h3 class=\"learn-subheading\">2. Revolution</h3>\n<p class=\"learn-p\">Revolution is the movement of the Earth around the Sun in an <strong>elliptical orbit</strong>. One complete revolution takes approximately <strong>365¼ days</strong> (one year). The extra quarter day is accounted for by adding a leap year every 4 years (366 days).</p>\n<p class=\"learn-p\">Earth's axis is tilted at <strong>23.5°</strong> from the vertical to the plane of its orbit. This tilt is the main cause of seasons.</p>\n\n<h3 class=\"learn-subheading\">3. Seasons</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Season (Northern Hemisphere)</th><th>Approx. Dates</th><th>Sun position</th></tr></thead>\n    <tbody>\n      <tr><td>Summer</td><td>June–August</td><td>Sun over Tropic of Cancer (23.5°N)</td></tr>\n      <tr><td>Autumn</td><td>Sept–Nov</td><td>Sun over Equator</td></tr>\n      <tr><td>Winter</td><td>Dec–Feb</td><td>Sun over Tropic of Capricorn (23.5°S)</td></tr>\n      <tr><td>Spring</td><td>March–May</td><td>Sun over Equator</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">4. Solstices and Equinoxes</h3>\n<ul class=\"learn-list\">\n  <li><strong>Summer Solstice (June 21):</strong> Sun directly overhead the Tropic of Cancer; longest day in Northern Hemisphere.</li>\n  <li><strong>Winter Solstice (December 22):</strong> Sun directly overhead the Tropic of Capricorn; shortest day in Northern Hemisphere.</li>\n  <li><strong>Vernal Equinox (March 21):</strong> Sun over Equator; day and night equal everywhere.</li>\n  <li><strong>Autumnal Equinox (September 23):</strong> Sun over Equator; day and night equal everywhere.</li>\n</ul>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Rotation (24 hrs) = day/night + time zones + Coriolis effect. Revolution (365¼ days) = seasons + solstices + equinoxes. Earth's axis tilt = 23.5°. June 21 = summer solstice (N. Hemisphere). December 22 = winter solstice. March 21 and September 23 = equinoxes.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Rotation (24 hrs, W→E): day/night, time zones, Coriolis effect. Revolution (365¼ days, elliptical orbit): seasons. Earth's 23.5° tilt causes seasons. Solstices: June 21 (summer N. Hemi.) and December 22 (winter N. Hemi.). Equinoxes: March 21 and September 23.\n</div>\n  ",
    "questions": [
      {
        "q": "The Earth's rotation is the movement of the Earth:",
        "o": ["Around the Sun in an elliptical orbit", "On its own axis from west to east", "Away from the Moon", "Toward the Sun during summer"],
        "a": 1,
        "e": "Rotation is the spinning of the Earth on its own axis from west to east (anticlockwise when viewed from the North Pole). One complete rotation takes approximately 24 hours.",
        "h": "Rotation = spinning on own axis W→E, 24 hours.",
        "yr": "SS1"
      },
      {
        "q": "The primary cause of day and night is:",
        "o": ["The revolution of the Earth around the Sun", "The rotation of the Earth on its axis", "The tilt of the Earth's axis", "The movement of the Moon around the Earth"],
        "a": 1,
        "e": "Day and night are caused by the rotation of the Earth on its axis. The side facing the Sun experiences day; the side facing away experiences night.",
        "h": "Day and night = caused by Earth's rotation (not revolution).",
        "yr": "SS1"
      },
      {
        "q": "How long does one complete rotation of the Earth take?",
        "o": ["12 hours", "24 hours", "365 days", "28 days"],
        "a": 1,
        "e": "One complete rotation of the Earth on its axis takes approximately 24 hours (one day). This is the basis for our 24-hour day.",
        "h": "One rotation = 24 hours (one day).",
        "yr": "SS1"
      },
      {
        "q": "The Earth's revolution is its movement:",
        "o": ["On its own axis from west to east", "Around the Sun in an elliptical orbit", "Around the Moon once a month", "Through different time zones"],
        "a": 1,
        "e": "Revolution is the movement of the Earth around the Sun in an elliptical (oval) orbit. One complete revolution takes approximately 365¼ days (one year).",
        "h": "Revolution = Earth orbiting the Sun, ~365¼ days.",
        "yr": "SS1"
      },
      {
        "q": "The main cause of the seasons is:",
        "o": ["The varying distance of the Earth from the Sun", "The tilt of the Earth's axis at 23.5° as it revolves around the Sun", "The rotation of the Earth", "The shape of the Earth's orbit"],
        "a": 1,
        "e": "Seasons are caused by the 23.5° tilt of Earth's axis. As Earth revolves around the Sun, different parts of the Earth receive more direct sunlight at different times of year.",
        "h": "Seasons = caused by Earth's 23.5° axial tilt during revolution.",
        "yr": "SS1"
      },
      {
        "q": "The June 21 Summer Solstice means that in the Northern Hemisphere:",
        "o": ["It is the shortest day of the year", "The Sun is directly overhead the Tropic of Capricorn", "It is the longest day of the year", "Day and night are equal"],
        "a": 2,
        "e": "On June 21 (Summer Solstice), the Sun is directly overhead the Tropic of Cancer (23.5°N). The Northern Hemisphere has its longest day and receives the most direct sunlight.",
        "h": "June 21: Sun over Tropic of Cancer; longest day in N. Hemisphere.",
        "yr": "SS1"
      },
      {
        "q": "On which dates do equinoxes occur, and what happens?",
        "o": ["June 21 and December 22; poles experience 24 hours of daylight", "March 21 and September 23; day and night are equal everywhere", "January 1 and July 1; seasons change", "April 15 and October 15; Earth is closest to the Sun"],
        "a": 1,
        "e": "Equinoxes occur on approximately March 21 (Vernal) and September 23 (Autumnal). On these dates, the Sun is directly over the Equator, and day and night are equal (12 hours each) everywhere on Earth.",
        "h": "Equinoxes: March 21 and Sept 23; day = night everywhere.",
        "yr": "SS1"
      },
      {
        "q": "The Coriolis effect, caused by Earth's rotation, deflects winds:",
        "o": ["To the left in the Northern Hemisphere", "To the right in the Northern Hemisphere, to the left in the Southern Hemisphere", "Toward the equator always", "Downward toward the Earth's surface"],
        "a": 1,
        "e": "The Coriolis effect deflects moving air (and ocean currents) to the right in the Northern Hemisphere and to the left in the Southern Hemisphere, due to Earth's rotation.",
        "h": "Coriolis: right in N. Hemisphere; left in S. Hemisphere.",
        "yr": "SS1"
      },
      {
        "q": "A leap year occurs every 4 years to account for:",
        "o": ["The extra 6 hours per day not accounted for", "The extra quarter day (approximately 6 hours) per year from Earth's revolution", "The slowing down of Earth's rotation", "Changes in Earth's distance from the Sun"],
        "a": 1,
        "e": "Earth takes 365¼ days to complete one revolution. The extra ¼ day (about 6 hours) accumulates to one full day every 4 years — added as February 29 in a leap year (366 days).",
        "h": "365¼ days → extra ¼ day × 4 years = 1 extra day = leap year.",
        "yr": "SS1"
      },
      {
        "q": "How many time zones does the Earth have?",
        "o": ["12", "24", "36", "360"],
        "a": 1,
        "e": "The Earth is divided into 24 time zones, each covering 15° of longitude. This corresponds to the 24 hours it takes for Earth to rotate 360° (360° ÷ 24 hours = 15°/hour).",
        "h": "24 time zones (15° longitude each; 360° ÷ 24 = 15°).",
        "yr": "SS1"
      },
      {
        "q": "The Winter Solstice in the Northern Hemisphere occurs around:",
        "o": ["June 21", "September 23", "December 22", "March 21"],
        "a": 2,
        "e": "The Winter Solstice in the Northern Hemisphere occurs around December 22. The Sun is directly overhead the Tropic of Capricorn (23.5°S), giving the Northern Hemisphere its shortest day.",
        "h": "December 22 = Winter Solstice (N. Hemisphere shortest day).",
        "yr": "SS1"
      },
      {
        "q": "The Earth's orbit around the Sun is:",
        "o": ["Perfectly circular", "Elliptical (oval-shaped)", "Spiral", "Square"],
        "a": 1,
        "e": "Earth's orbit around the Sun is elliptical (oval-shaped), not perfectly circular. This means Earth is slightly closer to the Sun in January (perihelion) and farther in July (aphelion).",
        "h": "Earth's orbit = elliptical (oval), not circular.",
        "yr": "SS1"
      },
      {
        "q": "The apparent movement of the Sun from east to west across the sky is caused by:",
        "o": ["The Sun moving around the Earth", "The revolution of the Earth around the Sun", "The rotation of the Earth from west to east", "The Moon's orbit around Earth"],
        "a": 2,
        "e": "The Sun appears to move from east to west because the Earth rotates from west to east. It is the Earth moving, not the Sun — an apparent motion created by our perspective from the rotating Earth.",
        "h": "Earth rotates W→E; Sun appears to move E→W (apparent motion).",
        "yr": "SS1"
      },
      {
        "q": "The angle at which the Earth's axis is tilted is:",
        "o": ["0°", "23.5°", "45°", "66.5°"],
        "a": 1,
        "e": "Earth's axis is tilted at 23.5° from the vertical (perpendicular to the plane of its orbit around the Sun). This tilt is the primary cause of the seasons.",
        "h": "Earth's axial tilt = 23.5°.",
        "yr": "SS1"
      },
      {
        "q": "During the December Solstice, which hemisphere experiences summer?",
        "o": ["Northern Hemisphere", "Southern Hemisphere", "Both hemispheres equally", "Neither hemisphere"],
        "a": 1,
        "e": "During the December Solstice (December 22), the Sun is directly over the Tropic of Capricorn (23.5°S). The Southern Hemisphere experiences summer while the Northern Hemisphere has winter.",
        "h": "December solstice: Southern Hemisphere = summer.",
        "yr": "SS1"
      },
      {
        "q": "The Tropic of Cancer is located at:",
        "o": ["23.5°S latitude", "0° latitude (Equator)", "23.5°N latitude", "66.5°N latitude"],
        "a": 2,
        "e": "The Tropic of Cancer is at 23.5°N latitude. It marks the northernmost point where the Sun can be directly overhead (at solar noon), which occurs on the June Solstice.",
        "h": "Tropic of Cancer = 23.5°N (Sun overhead on June 21).",
        "yr": "SS1"
      },
      {
        "q": "Time zones are based on which geographical feature?",
        "o": ["Latitude", "Longitude", "Altitude", "Distance from the equator"],
        "a": 1,
        "e": "Time zones are based on longitude. The Earth rotates 360° in 24 hours, so each time zone corresponds to 15° of longitude (360° ÷ 24 = 15°). Moving eastward adds an hour; westward subtracts an hour.",
        "h": "Time zones based on longitude (15° per zone).",
        "yr": "SS1"
      },
      {
        "q": "On the March 21 Vernal Equinox, the Sun is directly overhead:",
        "o": ["The Tropic of Cancer", "The Tropic of Capricorn", "The Equator", "The Arctic Circle"],
        "a": 2,
        "e": "On the Vernal Equinox (March 21), the Sun is directly overhead the Equator (0° latitude). Day and night are each 12 hours everywhere on Earth.",
        "h": "March 21 equinox: Sun overhead the Equator; equal day and night.",
        "yr": "SS1"
      },
      {
        "q": "The polar regions experience long periods of daylight in summer because of:",
        "o": ["Their proximity to the Sun in summer", "The 23.5° tilt of Earth's axis keeping the pole tilted toward the Sun", "The rotation of the Earth being slower near the poles", "Reflection of sunlight from ice"],
        "a": 1,
        "e": "The 23.5° axial tilt means that during summer, the pole is tilted toward the Sun. At and near the poles, the Sun remains above the horizon for extended periods — up to 24 hours (midnight sun).",
        "h": "Axial tilt = pole tilted toward Sun in summer = extended daylight.",
        "yr": "SS1"
      },
      {
        "q": "The number of degrees the Earth rotates per hour is:",
        "o": ["10°", "15°", "24°", "30°"],
        "a": 1,
        "e": "The Earth rotates 360° in 24 hours. 360° ÷ 24 hours = 15° per hour. This is why each time zone covers 15° of longitude.",
        "h": "360° ÷ 24 hours = 15° per hour.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Latitudes and Longitudes",
    "topicCode": "SS1-GEO-04",
    "module": "Foundations of Geography",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Latitudes</span> and <span class=\"learn-keyword\">longitudes</span> are imaginary lines drawn on the Earth's surface to create a grid system that allows us to locate any point on Earth precisely. Together they form the <span class=\"learn-keyword\">geographical coordinate system</span>.\n</div>\n\n<h3 class=\"learn-subheading\">1. Latitudes (Parallels)</h3>\n<p class=\"learn-p\">Latitudes are imaginary horizontal lines drawn parallel to the Equator. They measure the angular distance north or south of the Equator.</p>\n<ul class=\"learn-list\">\n  <li><strong>Equator:</strong> 0° latitude — the longest parallel; divides Earth into Northern and Southern Hemispheres.</li>\n  <li><strong>Tropic of Cancer:</strong> 23.5°N — northernmost point Sun is directly overhead.</li>\n  <li><strong>Tropic of Capricorn:</strong> 23.5°S — southernmost point Sun is directly overhead.</li>\n  <li><strong>Arctic Circle:</strong> 66.5°N — boundary of the Arctic region.</li>\n  <li><strong>Antarctic Circle:</strong> 66.5°S — boundary of the Antarctic region.</li>\n  <li><strong>North Pole:</strong> 90°N; <strong>South Pole:</strong> 90°S.</li>\n</ul>\n<p class=\"learn-p\">All lines of latitude are parallel to each other. They are circles (not semi-circles). They become smaller moving toward the poles.</p>\n\n<h3 class=\"learn-subheading\">2. Longitudes (Meridians)</h3>\n<p class=\"learn-p\">Longitudes are imaginary vertical lines running from the North Pole to the South Pole. They measure the angular distance east or west of the Prime Meridian.</p>\n<ul class=\"learn-list\">\n  <li><strong>Prime Meridian (Greenwich Meridian):</strong> 0° longitude — passes through Greenwich, England.</li>\n  <li>Longitudes range from 0° to 180° East and 0° to 180° West.</li>\n  <li><strong>International Date Line:</strong> 180° longitude — crossing it changes the date by ±1 day.</li>\n</ul>\n<p class=\"learn-p\">All lines of longitude are equal in length (semi-circles). They converge at the poles.</p>\n\n<h3 class=\"learn-subheading\">3. Uses of Latitudes and Longitudes</h3>\n<ul class=\"learn-list\">\n  <li>Precise location of any point on Earth.</li>\n  <li>Navigation (ships, aircraft, GPS).</li>\n  <li>Time calculation (1° longitude = 4 minutes time difference).</li>\n  <li>Determining climate zones based on latitude.</li>\n</ul>\n\n<h3 class=\"learn-subheading\">4. Time Calculation Using Longitude</h3>\n<p class=\"learn-p\">Earth rotates 360° in 24 hours = 15° per hour = 1° per 4 minutes. Moving east means adding time; moving west means subtracting time.</p>\n<p class=\"learn-p\"><strong>Example:</strong> If Lagos is at 3°E and Greenwich (0°) is 12:00 noon, Lagos time = 12:00 + (3 × 4 minutes) = 12:12 pm.</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Latitudes = horizontal parallels (N/S of equator). Longitudes = vertical meridians (E/W of Prime Meridian). Equator = 0° lat. Prime Meridian = 0° long. 1° longitude = 4 minutes time difference. East = add time; West = subtract time.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Latitudes measure N/S from Equator (0°) to poles (90°). Key parallels: Equator (0°), Tropics (23.5°), Arctic/Antarctic Circles (66.5°). Longitudes measure E/W from Prime Meridian. 1° longitude = 4 minutes. GPS uses lat/long coordinates.\n</div>\n  ",
    "questions": [
      {
        "q": "Lines of latitude are described as:",
        "o": ["Vertical lines running from pole to pole", "Horizontal lines parallel to the Equator", "Diagonal lines across the Earth", "Lines that only exist in the Northern Hemisphere"],
        "a": 1,
        "e": "Lines of latitude (parallels) are imaginary horizontal lines drawn parallel to the Equator. They measure angular distance north or south of the Equator.",
        "h": "Latitudes = horizontal lines parallel to Equator.",
        "yr": "SS1"
      },
      {
        "q": "The Equator is at:",
        "o": ["23.5°N", "66.5°N", "0° latitude", "90°N"],
        "a": 2,
        "e": "The Equator is at 0° latitude. It is the longest line of latitude, divides the Earth into Northern and Southern Hemispheres, and is equidistant from both poles.",
        "h": "Equator = 0° latitude.",
        "yr": "SS1"
      },
      {
        "q": "The Prime Meridian (Greenwich Meridian) is at:",
        "o": ["23.5°E", "180° longitude", "0° longitude", "90°W"],
        "a": 2,
        "e": "The Prime Meridian is at 0° longitude. It passes through Greenwich, England and serves as the reference line from which all other longitudes are measured east or west.",
        "h": "Prime Meridian = 0° longitude (Greenwich, England).",
        "yr": "SS1"
      },
      {
        "q": "Lines of longitude (meridians) are:",
        "o": ["Horizontal circles parallel to the Equator", "Vertical semi-circles running from the North to South Pole", "Diagonal lines across the equator", "Lines that are parallel to each other"],
        "a": 1,
        "e": "Lines of longitude (meridians) are imaginary vertical semi-circles running from the North Pole to the South Pole. They measure angular distance east or west of the Prime Meridian.",
        "h": "Longitudes = vertical semi-circles from pole to pole.",
        "yr": "SS1"
      },
      {
        "q": "The Tropic of Capricorn is located at:",
        "o": ["23.5°N", "66.5°S", "23.5°S", "0°"],
        "a": 2,
        "e": "The Tropic of Capricorn is at 23.5°S latitude. It marks the southernmost point where the Sun can be directly overhead, which occurs on December 22 (Winter Solstice in N. Hemisphere).",
        "h": "Tropic of Capricorn = 23.5°S.",
        "yr": "SS1"
      },
      {
        "q": "The International Date Line is located at approximately:",
        "o": ["0° longitude", "90°E longitude", "180° longitude", "66.5°N latitude"],
        "a": 2,
        "e": "The International Date Line is at approximately 180° longitude, roughly opposite the Prime Meridian. Crossing it from west to east means going back one calendar day; east to west adds one day.",
        "h": "International Date Line ≈ 180° longitude.",
        "yr": "SS1"
      },
      {
        "q": "The time difference for every 1° of longitude is:",
        "o": ["1 minute", "4 minutes", "15 minutes", "1 hour"],
        "a": 1,
        "e": "The Earth rotates 360° in 24 hours = 15° per hour = 1° per 4 minutes. Therefore, for every degree of longitude, there is a 4-minute time difference.",
        "h": "1° longitude = 4 minutes time difference.",
        "yr": "SS1"
      },
      {
        "q": "If Greenwich (0°) shows 12:00 noon, what time is it at 30°E?",
        "o": ["10:00 am", "11:00 am", "12:00 noon", "2:00 pm"],
        "a": 3,
        "e": "30°E is east of Greenwich, so add time. 30° × 4 minutes = 120 minutes = 2 hours. 12:00 noon + 2 hours = 2:00 pm.",
        "h": "30°E: 30 × 4 = 120 min = +2 hrs → 12:00 + 2 = 2:00 pm.",
        "yr": "SS1"
      },
      {
        "q": "The Arctic Circle is located at:",
        "o": ["23.5°N", "66.5°N", "90°N", "0°"],
        "a": 1,
        "e": "The Arctic Circle is at 66.5°N latitude. It marks the boundary of the Arctic region, beyond which the Sun does not set on the summer solstice and does not rise on the winter solstice.",
        "h": "Arctic Circle = 66.5°N.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is a use of latitudes and longitudes?",
        "o": ["Measuring the depth of the ocean", "Precisely locating any point on Earth for navigation", "Determining the height of mountains", "Measuring the speed of winds"],
        "a": 1,
        "e": "Latitudes and longitudes create a global grid system that allows any point on Earth to be precisely located. This is essential for navigation, mapping, and GPS technology.",
        "h": "Lat/Long = precise location system for navigation and mapping.",
        "yr": "SS1"
      },
      {
        "q": "All lines of latitude (parallels) are:",
        "o": ["Equal in length", "Semi-circles from pole to pole", "Parallel to each other and become smaller toward the poles", "Only found north of the Equator"],
        "a": 2,
        "e": "Lines of latitude are parallel to each other (hence 'parallels'). The Equator is the longest; they progressively become shorter circles as they approach the poles.",
        "h": "Latitudes: parallel to each other, smaller toward poles.",
        "yr": "SS1"
      },
      {
        "q": "If it is 3:00 pm at 45°E, what is the time at 15°E?",
        "o": ["1:00 pm", "2:00 pm", "4:00 pm", "5:00 pm"],
        "a": 1,
        "e": "15°E is 30° west of 45°E. Subtract: 30° × 4 minutes = 120 minutes = 2 hours. 3:00 pm − 2 hours = 1:00 pm.",
        "h": "45°E to 15°E = 30° westward → −2 hours → 1:00 pm.",
        "yr": "SS1"
      },
      {
        "q": "The Antarctic Circle is located at:",
        "o": ["23.5°S", "66.5°S", "90°S", "0°"],
        "a": 1,
        "e": "The Antarctic Circle is at 66.5°S latitude. It marks the boundary of the Antarctic region.",
        "h": "Antarctic Circle = 66.5°S.",
        "yr": "SS1"
      },
      {
        "q": "Which direction do you travel moving from 30°N to 10°N latitude?",
        "o": ["Eastward", "Westward", "Northward", "Southward"],
        "a": 3,
        "e": "Moving from 30°N to 10°N is moving toward the Equator — that is southward. Latitudes decrease toward the Equator (0°) and increase toward the poles (90°).",
        "h": "30°N → 10°N = moving toward Equator = southward.",
        "yr": "SS1"
      },
      {
        "q": "Nigeria is located at approximately 10°N latitude. This means Nigeria is in:",
        "o": ["The Southern Hemisphere, south of the Equator", "The Northern Hemisphere, north of the Equator", "On the Equator", "The Eastern Hemisphere"],
        "a": 1,
        "e": "10°N means 10 degrees north of the Equator. Therefore Nigeria is in the Northern Hemisphere. Its latitudes range from about 4°N to 14°N.",
        "h": "10°N = 10° north of Equator = Northern Hemisphere.",
        "yr": "SS1"
      },
      {
        "q": "The longest line of latitude is:",
        "o": ["The Tropic of Cancer", "The Tropic of Capricorn", "The Equator", "The Arctic Circle"],
        "a": 2,
        "e": "The Equator (0°) is the longest line of latitude. It is a great circle with a circumference of about 40,075 km. All other latitudes are progressively shorter toward the poles.",
        "h": "Equator (0°) = longest line of latitude.",
        "yr": "SS1"
      },
      {
        "q": "All lines of longitude (meridians) are equal in length because:",
        "o": ["They all pass through the same points on the equator", "They are all semi-circles from North to South Pole, covering the same distance", "The Earth is a perfect sphere", "Longitude lines do not vary in length"],
        "a": 1,
        "e": "All meridians are semi-circles running from the North Pole to the South Pole. Since all poles are the same distance apart through the centre of the Earth, all meridians are equal in length (about 20,004 km).",
        "h": "All meridians = semi-circles pole to pole = equal length.",
        "yr": "SS1"
      },
      {
        "q": "Which city is located at 0° longitude?",
        "o": ["Paris, France", "Greenwich, England", "New York, USA", "Accra, Ghana"],
        "a": 1,
        "e": "Greenwich, England (now part of London) is located at 0° longitude — the Prime Meridian. The Royal Observatory at Greenwich was chosen as the reference point in 1884.",
        "h": "0° longitude = Greenwich, England (Prime Meridian).",
        "yr": "SS1"
      },
      {
        "q": "Latitude lines are used to determine:",
        "o": ["Time zones", "Climate zones and seasons", "East-west direction only", "The depth of ocean trenches"],
        "a": 1,
        "e": "Latitude is used to determine climate zones — tropical (near equator), temperate (middle latitudes), polar (high latitudes). It also indicates which hemisphere a place is in.",
        "h": "Latitude determines climate zones.",
        "yr": "SS1"
      },
      {
        "q": "A ship is at the position 20°N, 40°E. The '20°N' refers to its position:",
        "o": ["40° east of the Prime Meridian", "20° north of the Equator", "20° south of the Equator", "In the Southern Hemisphere"],
        "a": 1,
        "e": "In geographic coordinates, the first value is latitude: 20°N means 20 degrees north of the Equator (Northern Hemisphere). The second value (40°E) is the longitude east of the Prime Meridian.",
        "h": "20°N = 20 degrees north of the Equator (latitude).",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "The Earth's Structure",
    "topicCode": "SS1-GEO-05",
    "module": "Physical Geography",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  The Earth is not a solid uniform mass — it has distinct <span class=\"learn-keyword\">layers</span> with different compositions, temperatures, and densities. Understanding the Earth's internal structure is important for explaining earthquakes, volcanic activity, and the formation of rocks and landforms.\n</div>\n\n<h3 class=\"learn-subheading\">1. The Three Main Layers</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Layer</th><th>Depth</th><th>Composition</th><th>State</th></tr></thead>\n    <tbody>\n      <tr><td>Crust</td><td>0–70 km</td><td>Silica, aluminium, iron, magnesium</td><td>Solid</td></tr>\n      <tr><td>Mantle</td><td>70–2,900 km</td><td>Iron, magnesium silicates</td><td>Semi-solid (plastic)</td></tr>\n      <tr><td>Core</td><td>2,900–6,371 km</td><td>Iron and nickel</td><td>Outer core: liquid; Inner core: solid</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">2. The Crust</h3>\n<p class=\"learn-p\">The crust is the thin outermost layer of the Earth. There are two types:</p>\n<ul class=\"learn-list\">\n  <li><strong>Continental crust (SIAL):</strong> Thicker (up to 70 km); made of silica and aluminium; less dense. Forms continents.</li>\n  <li><strong>Oceanic crust (SIMA):</strong> Thinner (about 5–10 km); made of silica and magnesium; denser. Forms ocean floors.</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. The Mantle</h3>\n<p class=\"learn-p\">The mantle is the thickest layer (about 2,830 km thick). It is composed of iron and magnesium silicate rocks. The upper part of the mantle is called the <strong>asthenosphere</strong> — a semi-plastic zone on which tectonic plates float and move.</p>\n\n<h3 class=\"learn-subheading\">4. The Core</h3>\n<ul class=\"learn-list\">\n  <li><strong>Outer core:</strong> Liquid iron and nickel; responsible for Earth's magnetic field.</li>\n  <li><strong>Inner core:</strong> Solid iron and nickel; temperatures reach about 5,000–6,000°C.</li>\n</ul>\n\n<h3 class=\"learn-subheading\">5. Importance of Earth's Structure</h3>\n<ul class=\"learn-list\">\n  <li>The core produces Earth's magnetic field, protecting life.</li>\n  <li>The mantle's convection currents drive plate tectonics (causing earthquakes and volcanoes).</li>\n  <li>The crust provides the foundation for all life, soil, and human activities.</li>\n</ul>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Three layers: Crust (thinnest, solid), Mantle (thickest, semi-solid), Core (hottest; outer = liquid, inner = solid). Continental crust = SIAL (silica + aluminium). Oceanic crust = SIMA (silica + magnesium). The liquid outer core creates Earth's magnetic field.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Earth has three layers: crust (solid, 0–70 km), mantle (semi-solid, 70–2,900 km), and core (2,900–6,371 km; outer liquid, inner solid). Continental crust = SIAL; oceanic crust = SIMA. Mantle convection drives plate movement.\n</div>\n  ",
    "questions": [
      {
        "q": "The Earth's structure from the surface inward consists of:",
        "o": ["Core, Mantle, Crust", "Crust, Mantle, Core", "Mantle, Crust, Core", "Core, Crust, Mantle"],
        "a": 1,
        "e": "From the surface inward, the Earth's layers are: Crust (outermost), Mantle (middle), and Core (innermost/deepest). The crust is the thinnest; the mantle is the thickest.",
        "h": "Surface to centre: Crust → Mantle → Core.",
        "yr": "SS1"
      },
      {
        "q": "The thinnest layer of the Earth is the:",
        "o": ["Inner core", "Outer core", "Mantle", "Crust"],
        "a": 3,
        "e": "The crust is the thinnest layer of the Earth — ranging from about 5–10 km under oceans to up to 70 km under mountains. The mantle is the thickest layer.",
        "h": "Crust = thinnest Earth layer (5–70 km).",
        "yr": "SS1"
      },
      {
        "q": "The continental crust is also called SIAL because it is composed of:",
        "o": ["Sulfur and Iron", "Silicon and Aluminium", "Silica and Magnesium", "Sodium and Lithium"],
        "a": 1,
        "e": "SIAL stands for Silicon (Si) and Aluminium (Al) — the dominant elements in continental crust. Continental crust is thicker and less dense than oceanic crust.",
        "h": "SIAL = Silicon + Aluminium (continental crust).",
        "yr": "SS1"
      },
      {
        "q": "The oceanic crust is called SIMA because it consists mainly of:",
        "o": ["Sodium and Magnesium", "Silicon and Magnesium", "Sulfur and Iron", "Silica and Aluminium"],
        "a": 1,
        "e": "SIMA stands for Silicon (Si) and Magnesium (Ma) — the main components of oceanic crust. Oceanic crust is thinner (5–10 km) and denser than continental crust.",
        "h": "SIMA = Silicon + Magnesium (oceanic crust).",
        "yr": "SS1"
      },
      {
        "q": "The Earth's magnetic field is generated by:",
        "o": ["The solid inner core", "The liquid outer core of iron and nickel", "The mantle's convection currents", "The Earth's rotation combined with the crust"],
        "a": 1,
        "e": "Earth's magnetic field is generated by the movement of liquid iron and nickel in the outer core. This moving molten metal creates electrical currents that produce the magnetic field.",
        "h": "Magnetic field = generated by liquid iron/nickel in the outer core.",
        "yr": "SS1"
      },
      {
        "q": "The mantle is described as semi-solid because:",
        "o": ["It is half rock and half water", "It behaves like a very thick, slow-moving fluid (plastic) under long-term pressure", "It contains liquid water reserves", "It is solid at the top and liquid at the bottom"],
        "a": 1,
        "e": "The mantle is solid rock but under the extreme heat and pressure of millions of years, it behaves plastically — flowing very slowly. This is why tectonic plates can move on top of it.",
        "h": "Mantle is solid rock but behaves plastically (like very slow fluid).",
        "yr": "SS1"
      },
      {
        "q": "The asthenosphere is part of:",
        "o": ["The crust", "The upper mantle", "The outer core", "The inner core"],
        "a": 1,
        "e": "The asthenosphere is the semi-plastic zone in the upper mantle, located below the rigid lithosphere (crust + upper mantle). Tectonic plates float and move on the asthenosphere.",
        "h": "Asthenosphere = semi-plastic zone in the upper mantle.",
        "yr": "SS1"
      },
      {
        "q": "The thickest layer of the Earth is the:",
        "o": ["Crust", "Outer core", "Inner core", "Mantle"],
        "a": 3,
        "e": "The mantle is the thickest layer, extending from about 70 km to 2,900 km depth — approximately 2,830 km thick. This is much thicker than the crust or core sections.",
        "h": "Mantle = thickest Earth layer (~2,830 km thick).",
        "yr": "SS1"
      },
      {
        "q": "The inner core of the Earth is:",
        "o": ["Liquid iron and nickel", "Solid iron and nickel", "Semi-solid silicate rock", "Gaseous hydrogen"],
        "a": 1,
        "e": "Despite being the hottest part of the Earth (about 5,000–6,000°C), the inner core is solid because the enormous pressure (about 3.5 million atmospheres) keeps the iron and nickel in a solid state.",
        "h": "Inner core = solid iron and nickel (extreme pressure keeps it solid).",
        "yr": "SS1"
      },
      {
        "q": "Convection currents in the mantle are important because they:",
        "o": ["Cool the Earth's surface", "Drive the movement of tectonic plates causing earthquakes and volcanoes", "Create ocean waves", "Produce the Earth's atmosphere"],
        "a": 1,
        "e": "Heat from the core creates convection currents in the mantle — hot material rises, cools, and sinks in circular patterns. These currents drag tectonic plates, causing their movement, leading to earthquakes and volcanoes.",
        "h": "Mantle convection → drives tectonic plate movement.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is the correct depth range for the Earth's mantle?",
        "o": ["0–70 km", "70–2,900 km", "2,900–5,100 km", "5,100–6,371 km"],
        "a": 1,
        "e": "The mantle extends from about 70 km (below the crust) to 2,900 km depth. Below 2,900 km is the core (outer core: 2,900–5,100 km; inner core: 5,100–6,371 km).",
        "h": "Mantle: 70–2,900 km depth.",
        "yr": "SS1"
      },
      {
        "q": "Continental crust is thicker and less dense than oceanic crust because:",
        "o": ["Continents have more water", "It is composed of lighter silica-aluminium rocks (SIAL)", "Oceanic crust has been eroded more", "Continental crust is younger"],
        "a": 1,
        "e": "Continental crust consists mainly of SIAL rocks (silica and aluminium), which are less dense. Oceanic crust consists of denser SIMA rocks (silica and magnesium), making it thinner and heavier.",
        "h": "SIAL lighter → continental crust thicker but less dense.",
        "yr": "SS1"
      },
      {
        "q": "The extreme temperatures in the Earth's inner core (about 5,000–6,000°C) come from:",
        "o": ["Solar radiation absorbed through the crust", "Residual heat from Earth's formation and radioactive decay", "Chemical reactions between iron and nickel", "Friction from Earth's rotation"],
        "a": 1,
        "e": "The Earth's internal heat comes from two main sources: residual heat from the planet's formation (accretion) billions of years ago, and heat from the decay of radioactive elements.",
        "h": "Earth's internal heat = formation heat + radioactive decay.",
        "yr": "SS1"
      },
      {
        "q": "What is the lithosphere?",
        "o": ["The liquid outer core", "The rigid outer layer of Earth, consisting of the crust and upper mantle", "The semi-plastic asthenosphere", "The entire mantle"],
        "a": 1,
        "e": "The lithosphere is the rigid outer shell of the Earth, consisting of the crust and the uppermost solid part of the mantle. It is broken into tectonic plates that float on the asthenosphere.",
        "h": "Lithosphere = rigid crust + uppermost mantle (broken into tectonic plates).",
        "yr": "SS1"
      },
      {
        "q": "Why is the study of the Earth's internal structure important in geography?",
        "o": ["It helps explain the origin of ocean waves", "It explains earthquakes, volcanoes, mountain building, and rock formation", "It tells us about the age of the Moon", "It explains weather patterns"],
        "a": 1,
        "e": "Understanding Earth's internal structure explains tectonic processes — plate movement causes earthquakes, volcanic eruptions, mountain building, and the formation of different rock types.",
        "h": "Earth's structure explains earthquakes, volcanoes, and mountain building.",
        "yr": "SS1"
      },
      {
        "q": "The outer core of the Earth is:",
        "o": ["Solid iron and silica", "Liquid iron and nickel", "Semi-plastic silicate", "Gaseous hydrogen and helium"],
        "a": 1,
        "e": "The outer core (about 2,900–5,100 km depth) is composed of liquid iron and nickel. Its liquid state (due to slightly lower pressure than the inner core) allows convection that generates Earth's magnetic field.",
        "h": "Outer core = liquid iron and nickel.",
        "yr": "SS1"
      },
      {
        "q": "Tectonic plates are sections of the Earth's:",
        "o": ["Inner core", "Outer core", "Mantle only", "Lithosphere (crust + upper mantle)"],
        "a": 3,
        "e": "Tectonic plates are large sections of the lithosphere (crust + uppermost solid mantle) that float on the semi-plastic asthenosphere and move relative to each other.",
        "h": "Tectonic plates = sections of the lithosphere.",
        "yr": "SS1"
      },
      {
        "q": "The element that makes up the largest percentage of the Earth's core is:",
        "o": ["Silicon", "Aluminium", "Iron", "Oxygen"],
        "a": 2,
        "e": "Iron is the dominant element in Earth's core, making up about 85% of the inner core and about 80% of the outer core. Nickel is the second most abundant element in the core.",
        "h": "Earth's core is mainly iron (with some nickel).",
        "yr": "SS1"
      },
      {
        "q": "Which layer of the Earth is directly responsible for providing fertile soil for agriculture?",
        "o": ["The inner core", "The outer core", "The mantle", "The crust"],
        "a": 3,
        "e": "The crust is the outermost layer on which all life and human activity occurs. Soil forms from the weathering of crustal rocks. Agriculture depends entirely on the top few metres of the crust.",
        "h": "Crust = Earth's surface layer where soil forms for agriculture.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following correctly compares continental and oceanic crust?",
        "o": ["Continental is thinner and denser; oceanic is thicker and less dense", "Continental is thicker and less dense (SIAL); oceanic is thinner and denser (SIMA)", "Both types are the same thickness and density", "Oceanic crust is thicker and made of SIAL"],
        "a": 1,
        "e": "Continental crust is thicker (up to 70 km) and less dense (SIAL — silicon and aluminium). Oceanic crust is thinner (5–10 km) and denser (SIMA — silicon and magnesium).",
        "h": "Continental: thicker, less dense (SIAL). Oceanic: thinner, denser (SIMA).",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Rocks",
    "topicCode": "SS1-GEO-06",
    "module": "Physical Geography",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Rocks</span> are naturally occurring solid mixtures of minerals that make up the Earth's crust. There are three main types of rocks based on how they are formed: <span class=\"learn-keyword\">igneous</span>, <span class=\"learn-keyword\">sedimentary</span>, and <span class=\"learn-keyword\">metamorphic</span>. These types are continually being transformed into each other through the <span class=\"learn-keyword\">rock cycle</span>.\n</div>\n\n<h3 class=\"learn-subheading\">1. Igneous Rocks</h3>\n<p class=\"learn-p\">Formed by the cooling and solidification of molten rock (magma or lava).</p>\n<ul class=\"learn-list\">\n  <li><strong>Intrusive (plutonic):</strong> Magma cools slowly underground → large crystals. Example: <strong>Granite</strong>.</li>\n  <li><strong>Extrusive (volcanic):</strong> Lava cools quickly on surface → small or no crystals. Example: <strong>Basalt</strong>, obsidian.</li>\n</ul>\n<p class=\"learn-p\"><strong>Characteristics:</strong> Hard, resistant to erosion; no fossils; crystalline.</p>\n\n<h3 class=\"learn-subheading\">2. Sedimentary Rocks</h3>\n<p class=\"learn-p\">Formed by the compaction and cementation of sediments (fragments of other rocks, minerals, or organic matter) deposited by water, wind, or ice.</p>\n<ul class=\"learn-list\">\n  <li><strong>Clastic:</strong> Formed from rock fragments. Examples: Sandstone, shale, conglomerate.</li>\n  <li><strong>Chemical:</strong> Formed from minerals precipitated from solution. Example: Limestone (some types), rock salt.</li>\n  <li><strong>Organic:</strong> Formed from remains of organisms. Example: Coal, coral limestone.</li>\n</ul>\n<p class=\"learn-p\"><strong>Characteristics:</strong> Layered (stratified); may contain fossils; found near rivers, seas, deserts.</p>\n\n<h3 class=\"learn-subheading\">3. Metamorphic Rocks</h3>\n<p class=\"learn-p\">Formed when existing rocks (igneous or sedimentary) are transformed by heat and pressure without melting.</p>\n<ul class=\"learn-list\">\n  <li>Limestone → <strong>Marble</strong></li>\n  <li>Shale → <strong>Slate</strong></li>\n  <li>Sandstone → <strong>Quartzite</strong></li>\n  <li>Granite → <strong>Gneiss</strong></li>\n</ul>\n<p class=\"learn-p\"><strong>Characteristics:</strong> Very hard; banded or foliated; fossils destroyed by heat.</p>\n\n<h3 class=\"learn-subheading\">4. Importance of Rocks</h3>\n<ul class=\"learn-list\">\n  <li>Building materials: granite, marble, sandstone.</li>\n  <li>Soil formation: weathered rocks form soil.</li>\n  <li>Mineral resources: gold, iron ore, coal found in rocks.</li>\n  <li>Tourism: scenic landforms made of rocks.</li>\n</ul>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Three rock types: Igneous (cooling magma/lava), Sedimentary (compacted sediments, has fossils, layered), Metamorphic (heat + pressure transforms existing rocks). Key examples: Granite (igneous), Sandstone (sedimentary), Marble (metamorphic). Marble comes from limestone.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Igneous (magma/lava solidifies), Sedimentary (sediments compacted; layered; has fossils), Metamorphic (heat + pressure; very hard; no fossils). Rock cycle: rocks continuously transform between types. Examples: Granite, Sandstone, Marble.\n</div>\n  ",
    "questions": [
      {
        "q": "Rocks formed by the cooling and solidification of molten rock are called:",
        "o": ["Sedimentary rocks", "Metamorphic rocks", "Igneous rocks", "Organic rocks"],
        "a": 2,
        "e": "Igneous rocks form when magma (underground) or lava (on the surface) cools and solidifies. The word 'igneous' comes from the Latin 'ignis' meaning fire.",
        "h": "Igneous = formed from cooling magma or lava.",
        "yr": "SS1"
      },
      {
        "q": "Granite is an example of which type of rock?",
        "o": ["Sedimentary", "Metamorphic", "Extrusive igneous", "Intrusive igneous"],
        "a": 3,
        "e": "Granite is an intrusive (plutonic) igneous rock — it forms when magma cools slowly deep underground, producing large visible crystals. It is a hard, coarse-grained rock.",
        "h": "Granite = intrusive igneous rock (slow cooling underground).",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is a characteristic feature of sedimentary rocks?",
        "o": ["They are formed from magma", "They have large crystals due to slow cooling", "They are layered (stratified) and may contain fossils", "They are always black in colour"],
        "a": 2,
        "e": "Sedimentary rocks are formed in layers (strata) as sediments are deposited and compacted over time. They often contain fossils because organisms are preserved in the sediment layers.",
        "h": "Sedimentary rocks = layered (stratified) + may contain fossils.",
        "yr": "SS1"
      },
      {
        "q": "Metamorphic rocks are formed by:",
        "o": ["Cooling of lava on the Earth's surface", "Deposition and compaction of sediments", "Transformation of existing rocks by heat and pressure", "Biological activity of living organisms"],
        "a": 2,
        "e": "Metamorphic rocks form when existing rocks (igneous or sedimentary) are changed by intense heat and pressure (without melting) deep within the Earth's crust.",
        "h": "Metamorphic = existing rocks transformed by heat and pressure.",
        "yr": "SS1"
      },
      {
        "q": "Marble is a metamorphic rock formed from:",
        "o": ["Sandstone", "Granite", "Limestone", "Shale"],
        "a": 2,
        "e": "Marble is formed when limestone (a sedimentary rock) is subjected to heat and pressure, transforming its calcite crystals into interlocking crystals of marble.",
        "h": "Limestone → (heat + pressure) → Marble.",
        "yr": "SS1"
      },
      {
        "q": "Basalt is an example of:",
        "o": ["Intrusive igneous rock", "Extrusive igneous rock", "Sedimentary rock", "Metamorphic rock"],
        "a": 1,
        "e": "Basalt is an extrusive (volcanic) igneous rock — it forms when lava cools quickly on the Earth's surface, producing fine-grained or glassy texture with tiny or invisible crystals.",
        "h": "Basalt = extrusive igneous rock (fast cooling on surface).",
        "yr": "SS1"
      },
      {
        "q": "Which of the following rocks is most likely to contain fossils?",
        "o": ["Granite", "Marble", "Basalt", "Sandstone"],
        "a": 3,
        "e": "Sandstone is a sedimentary rock. Sedimentary rocks are the only type that commonly contain fossils, because organisms can be buried and preserved in sediment layers. Igneous and metamorphic rocks rarely contain fossils.",
        "h": "Only sedimentary rocks commonly contain fossils.",
        "yr": "SS1"
      },
      {
        "q": "The rock cycle refers to:",
        "o": ["The daily rotation of rocks with the Earth", "The continuous transformation of rocks between igneous, sedimentary, and metamorphic types", "The erosion of rocks by rivers", "The formation of rock layers over time"],
        "a": 1,
        "e": "The rock cycle is the continuous process by which rocks are formed, altered, destroyed, and reformed. Rocks can transform between all three types through geological processes.",
        "h": "Rock cycle = continuous transformation between igneous, sedimentary, and metamorphic.",
        "yr": "SS1"
      },
      {
        "q": "Shale transformed by heat and pressure becomes:",
        "o": ["Marble", "Quartzite", "Slate", "Gneiss"],
        "a": 2,
        "e": "Shale is a sedimentary rock. When subjected to heat and pressure during metamorphism, it transforms into slate — a fine-grained metamorphic rock used in roofing.",
        "h": "Shale → (heat + pressure) → Slate.",
        "yr": "SS1"
      },
      {
        "q": "Intrusive igneous rocks have larger crystals than extrusive rocks because:",
        "o": ["They are found at the surface where there is more space", "They cool slowly underground, giving crystals more time to grow", "They contain more iron than extrusive rocks", "They are formed from sediment, not magma"],
        "a": 1,
        "e": "Intrusive rocks cool slowly deep underground where temperatures remain high for long periods, giving mineral crystals time to grow large. Extrusive rocks cool quickly on the surface, producing small or no crystals.",
        "h": "Slow cooling underground → large crystals (intrusive).",
        "yr": "SS1"
      },
      {
        "q": "Coal is classified as which type of sedimentary rock?",
        "o": ["Clastic sedimentary", "Chemical sedimentary", "Organic sedimentary", "Metamorphic"],
        "a": 2,
        "e": "Coal is an organic sedimentary rock — formed from the compressed and heated remains of plants (mainly ferns and trees) that lived millions of years ago in swampy environments.",
        "h": "Coal = organic sedimentary rock (compressed plant remains).",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is NOT a use of rocks?",
        "o": ["Building and construction materials", "Formation of soil through weathering", "Source of mineral resources like iron ore and gold", "Producing rainfall"],
        "a": 3,
        "e": "Rocks do not produce rainfall — that is a meteorological/hydrological process. Rocks are used for building (granite, marble), form soil when weathered, and contain mineral resources.",
        "h": "Rocks don't produce rainfall. Uses: building, soil, minerals.",
        "yr": "SS1"
      },
      {
        "q": "Sandstone transformed by heat and pressure becomes:",
        "o": ["Marble", "Slate", "Gneiss", "Quartzite"],
        "a": 3,
        "e": "Sandstone (sedimentary) is transformed into quartzite by metamorphism. The quartz grains in sandstone are fused together under heat and pressure to form the very hard quartzite.",
        "h": "Sandstone → (heat + pressure) → Quartzite.",
        "yr": "SS1"
      },
      {
        "q": "Sedimentary rocks are formed near rivers, seas, and deserts because:",
        "o": ["These areas have the highest temperatures", "Water, wind, and ice transport and deposit sediments in these environments", "Magma is most common near water bodies", "Heat and pressure are highest near oceans"],
        "a": 1,
        "e": "Sedimentary rocks form where agents of transportation (water, wind, ice) deposit their load of sediments. Rivers deposit in deltas and floodplains; wind deposits in deserts; seas receive marine sediments.",
        "h": "Water, wind, and ice deposit sediments in rivers, seas, deserts.",
        "yr": "SS1"
      },
      {
        "q": "Why are fossils rarely found in metamorphic rocks?",
        "o": ["Metamorphic rocks are too soft to preserve fossils", "The intense heat and pressure during metamorphism destroys organic remains", "Metamorphic rocks form too quickly for fossils to be preserved", "Fossils only form in volcanic areas"],
        "a": 1,
        "e": "The intense heat and pressure involved in metamorphism destroy any organic remains (fossils) that were present in the original rock. Fossils are preserved only in sedimentary rocks.",
        "h": "Metamorphism's heat and pressure destroys fossils.",
        "yr": "SS1"
      },
      {
        "q": "Granite is commonly used as a building material because it is:",
        "o": ["Soft and easy to cut", "Lightweight and inexpensive", "Hard, durable, and resistant to weathering", "The only rock found at Earth's surface"],
        "a": 2,
        "e": "Granite's interlocking crystals make it extremely hard and durable, resistant to weathering and erosion. It is widely used in construction for foundations, floors, and countertops.",
        "h": "Granite used in construction: hard, durable, resistant to weathering.",
        "yr": "SS1"
      },
      {
        "q": "The process by which sediments are pressed together and cemented to form sedimentary rocks is called:",
        "o": ["Crystallisation", "Metamorphism", "Lithification (compaction and cementation)", "Magmatic intrusion"],
        "a": 2,
        "e": "Lithification is the process by which loose sediments are converted into solid sedimentary rock through compaction (pressure from overlying sediments) and cementation (minerals binding particles together).",
        "h": "Lithification = compaction + cementation → sedimentary rock.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following pairs correctly matches a rock with its type?",
        "o": ["Marble — Sedimentary; Granite — Metamorphic", "Basalt — Sedimentary; Sandstone — Igneous", "Granite — Igneous; Marble — Metamorphic", "Slate — Igneous; Limestone — Metamorphic"],
        "a": 2,
        "e": "Granite is an intrusive igneous rock; Marble is a metamorphic rock (from limestone). This is a key pairing to remember.",
        "h": "Granite = igneous; Marble = metamorphic.",
        "yr": "SS1"
      },
      {
        "q": "Which type of rock is most important for the formation of soil?",
        "o": ["Metamorphic rocks only", "All three rock types contribute through weathering", "Only igneous rocks", "Only sedimentary rocks"],
        "a": 1,
        "e": "All three rock types contribute to soil formation through weathering — the physical and chemical breakdown of rocks into smaller particles. The type of soil depends on the parent rock being weathered.",
        "h": "All rock types contribute to soil through weathering.",
        "yr": "SS1"
      },
      {
        "q": "Conglomerate is a sedimentary rock made of:",
        "o": ["Compressed plant remains", "Rounded pebbles and gravel cemented together", "Fine clay particles", "Precipitated minerals from seawater"],
        "a": 1,
        "e": "Conglomerate is a clastic sedimentary rock made up of rounded pebbles, gravel, and sand cemented together by fine mineral material. The rounded particles indicate transportation by water before deposition.",
        "h": "Conglomerate = rounded pebbles cemented together (clastic sedimentary).",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Town/Village",
    "topicCode": "SS1-GEO-07",
    "module": "Human Geography",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  Human settlements range from small <span class=\"learn-keyword\">villages</span> to large <span class=\"learn-keyword\">towns</span> and cities. Understanding the characteristics, similarities, and differences between towns and villages helps us study how human populations organise themselves in space.\n</div>\n\n<h3 class=\"learn-subheading\">1. Village</h3>\n<p class=\"learn-p\">A village is a small settlement with a relatively small population, usually engaged in primary activities (farming, fishing, forestry). Characteristics include:</p>\n<ul class=\"learn-list\">\n  <li>Small population (typically fewer than 5,000 people).</li>\n  <li>Simple infrastructure — few roads, limited utilities.</li>\n  <li>Economy based on agriculture and fishing.</li>\n  <li>Close-knit community with strong social ties.</li>\n  <li>Few or no specialised services (hospitals, banks).</li>\n  <li>Low level of industrialisation.</li>\n</ul>\n\n<h3 class=\"learn-subheading\">2. Town</h3>\n<p class=\"learn-p\">A town is a larger settlement with a bigger population, more diverse economic activities, and better infrastructure. Characteristics include:</p>\n<ul class=\"learn-list\">\n  <li>Larger population (typically 5,000–100,000 people).</li>\n  <li>More developed infrastructure (roads, hospitals, schools, markets).</li>\n  <li>Diverse economy — trade, services, light industry, administration.</li>\n  <li>Less personal community relations than a village.</li>\n  <li>Better access to utilities: electricity, pipe-borne water, internet.</li>\n  <li>Administrative functions (local government offices).</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. Similarities Between Towns and Villages</h3>\n<ul class=\"learn-list\">\n  <li>Both are human settlements where people live.</li>\n  <li>Both have some form of administrative organisation.</li>\n  <li>Both have markets and commercial activities.</li>\n  <li>Both can grow in size over time.</li>\n</ul>\n\n<h3 class=\"learn-subheading\">4. Differences</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Feature</th><th>Village</th><th>Town</th></tr></thead>\n    <tbody>\n      <tr><td>Population</td><td>Small</td><td>Larger</td></tr>\n      <tr><td>Economy</td><td>Farming/fishing</td><td>Trade, industry, services</td></tr>\n      <tr><td>Infrastructure</td><td>Basic/limited</td><td>More developed</td></tr>\n      <tr><td>Services</td><td>Few</td><td>Many (hospitals, banks)</td></tr>\n      <tr><td>Community</td><td>Close-knit</td><td>Less personal</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Village = small, farming-based, close-knit community, limited services. Town = larger, diverse economy, better infrastructure, more services. Both are settlements with markets and can grow over time.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Villages: small population, farming economy, close-knit, limited infrastructure. Towns: larger population, diverse economy (trade + services), better infrastructure. Both are human settlements that can grow over time.\n</div>\n  ",
    "questions": [
      {
        "q": "A village is characterised by:",
        "o": ["Large population and diverse industries", "Small population and mainly agricultural economy", "Many hospitals and universities", "Heavy industrial activity"],
        "a": 1,
        "e": "A village is a small settlement where the population is small (typically under 5,000) and economic activities are mainly primary — farming, fishing, or forestry.",
        "h": "Village = small population, agricultural economy.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following best describes a town compared to a village?",
        "o": ["A town has a smaller population and less infrastructure", "A town has a larger population, more diverse economy, and better infrastructure", "A town is always located near a river", "A town only has administrative functions"],
        "a": 1,
        "e": "A town has a larger population (typically 5,000–100,000), a more diverse economy (trade, services, industry), and better infrastructure (hospitals, schools, roads) than a village.",
        "h": "Town > village in population, economy diversity, and infrastructure.",
        "yr": "SS1"
      },
      {
        "q": "The economy of a typical Nigerian village is MAINLY based on:",
        "o": ["Manufacturing and heavy industry", "International trade and banking", "Agriculture, fishing, and forestry (primary activities)", "Tourism and hospitality"],
        "a": 2,
        "e": "Nigerian villages are typically subsistence-based, with economies centred on farming, fishing, and forestry. These are primary economic activities that directly use natural resources.",
        "h": "Village economy = mainly farming, fishing, forestry.",
        "yr": "SS1"
      },
      {
        "q": "One key similarity between a town and a village is that:",
        "o": ["Both have the same population size", "Both are human settlements where people live", "Both have equal levels of infrastructure", "Both are exclusively agricultural"],
        "a": 1,
        "e": "Both towns and villages are human settlements — places where people live, work, and organise their social and economic lives. The difference lies in their size, population, and services.",
        "h": "Both towns and villages are human settlements.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following services is MORE likely to be found in a town than a village?",
        "o": ["Subsistence farms", "Traditional rulers' palaces", "Hospitals, banks, and secondary schools", "Bush paths and footpaths"],
        "a": 2,
        "e": "Towns have more specialised services like hospitals, banks, secondary schools, police stations, and local government offices. Villages typically lack these because of smaller population and economic activity.",
        "h": "Hospitals, banks, schools = more common in towns than villages.",
        "yr": "SS1"
      },
      {
        "q": "A close-knit community with strong social ties is a feature of:",
        "o": ["Large cities", "Towns", "Villages", "Industrial centres"],
        "a": 2,
        "e": "Villages have small populations where people generally know each other. This leads to close-knit communities with strong social bonds, family ties, and communal cooperation.",
        "h": "Close-knit community = village characteristic.",
        "yr": "SS1"
      },
      {
        "q": "A town differs from a village mainly in terms of:",
        "o": ["The language spoken by inhabitants", "Population size, economic diversity, and infrastructure development", "The religious practices of residents", "The type of crops grown"],
        "a": 1,
        "e": "The main differences between towns and villages are in population size (towns are larger), economic activities (more diverse in towns), and infrastructure (better developed in towns).",
        "h": "Towns differ from villages in population, economy, and infrastructure.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is a factor that causes villages to grow into towns over time?",
        "o": ["Decrease in agricultural production", "Discovery of resources, improved transport links, and population growth", "Reduction in government services", "Migration of people out of the area"],
        "a": 1,
        "e": "Villages grow into towns when factors like discovery of resources (minerals, markets), improved roads and transport, population growth, or establishment of industries attract more people and economic activity.",
        "h": "Villages → towns: resources, transport, population growth, industries.",
        "yr": "SS1"
      },
      {
        "q": "The term 'urbanisation' refers to:",
        "o": ["The building of new villages", "The process of population and economic activity moving from rural to urban areas", "The decline of towns into villages", "The spread of forests into settlements"],
        "a": 1,
        "e": "Urbanisation is the process by which the proportion of people living in urban areas (towns and cities) increases relative to rural areas, usually through rural-urban migration and natural population growth in cities.",
        "h": "Urbanisation = increasing population/activity in urban areas (rural to urban).",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is a problem commonly associated with villages?",
        "o": ["Traffic congestion", "High level of pollution", "Lack of basic infrastructure and social amenities", "High crime rates"],
        "a": 2,
        "e": "Villages often lack adequate infrastructure — poor roads, limited healthcare, inadequate schools, no electricity or pipe-borne water in some areas. These push people to migrate to towns and cities.",
        "h": "Village problem: lack of infrastructure and social amenities.",
        "yr": "SS1"
      },
      {
        "q": "The primary difference in community relations between towns and villages is that:",
        "o": ["Villages have no community relations", "Towns have stronger family ties than villages", "Villages have closer personal relations; towns have more impersonal social relations", "Towns have no crime"],
        "a": 2,
        "e": "In villages, the small population means people know each other personally — strong family and community bonds. In towns, the larger and more diverse population leads to more impersonal, formal relationships.",
        "h": "Village = personal close relations; Town = more impersonal relations.",
        "yr": "SS1"
      },
      {
        "q": "A Local Government Area (LGA) administrative headquarters is typically located in a:",
        "o": ["Village", "Town", "Forest reserve", "Farming hamlet"],
        "a": 1,
        "e": "LGA administrative headquarters (or secretariats) are located in towns, which have the infrastructure (buildings, roads, utilities) needed for administrative functions.",
        "h": "LGA headquarters located in towns (better infrastructure).",
        "yr": "SS1"
      },
      {
        "q": "Which of the following economic activities is most associated with a town?",
        "o": ["Subsistence farming", "Fishing in rivers", "Retail trade, banking, and manufacturing", "Hunting and gathering"],
        "a": 2,
        "e": "Towns have more diverse economies including retail trade (markets, shops), banking, light manufacturing, education, healthcare, and administration — unlike villages which are mainly agricultural.",
        "h": "Town economy: trade, banking, manufacturing (secondary and tertiary activities).",
        "yr": "SS1"
      },
      {
        "q": "Rural-urban migration in Nigeria is mainly caused by:",
        "o": ["People preferring cooler climates in cities", "The search for better jobs, education, and social amenities in towns and cities", "Government policies that force people to move", "Natural disasters destroying villages"],
        "a": 1,
        "e": "Rural-urban migration in Nigeria is primarily driven by the push factors of villages (lack of jobs, poor schools, limited healthcare) and pull factors of cities (employment, better schools, hospitals, and opportunities).",
        "h": "Rural-urban migration: push (village problems) + pull (city opportunities).",
        "yr": "SS1"
      },
      {
        "q": "Both towns and villages have markets because:",
        "o": ["Markets are only found in large cities", "Exchange of goods and services is a basic human need common to all settlements", "The government forces all settlements to have markets", "Markets only exist in Nigeria"],
        "a": 1,
        "e": "Markets exist in all human settlements because buying and selling goods is a fundamental economic activity. Even the smallest village has some form of local market, though its scale differs from urban markets.",
        "h": "Markets exist everywhere because trade is a basic human activity.",
        "yr": "SS1"
      },
      {
        "q": "The settlement pattern in most Nigerian villages is:",
        "o": ["Scattered/dispersed due to agricultural land needs or linear along roads/rivers", "Always nucleated in a perfect grid pattern", "Random with no pattern", "Always circular around a central market"],
        "a": 0,
        "e": "Nigerian villages typically show dispersed (scattered) patterns where farmsteads are spread out for agricultural access, or linear patterns along roads and rivers for trade and transport access.",
        "h": "Nigerian village patterns: dispersed (farming) or linear (along roads/rivers).",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is a push factor that causes people to leave villages?",
        "o": ["Availability of good hospitals in the village", "Good roads and transportation links", "Lack of employment opportunities and poor infrastructure in villages", "Presence of large universities in villages"],
        "a": 2,
        "e": "Push factors are negative conditions in villages that drive people away — including lack of jobs, poor roads, inadequate schools and hospitals, and limited economic opportunities.",
        "h": "Push factors (village): no jobs, poor infrastructure, limited services.",
        "yr": "SS1"
      },
      {
        "q": "Comparing infrastructure, which settlement is better developed?",
        "o": ["Village", "Town", "Both are equally developed", "Infrastructure is unrelated to settlement type"],
        "a": 1,
        "e": "Towns have more developed infrastructure — better roads, electricity, pipe-borne water, hospitals, schools, banks, and communication networks — because their larger population and economic activity justify the investment.",
        "h": "Towns have more developed infrastructure than villages.",
        "yr": "SS1"
      },
      {
        "q": "What is the main economic difference between a village and a town?",
        "o": ["Villages have more factories than towns", "Towns rely entirely on farming; villages have industries", "Villages mainly depend on primary activities; towns have primary, secondary, and tertiary activities", "There is no economic difference"],
        "a": 2,
        "e": "Villages are dominated by primary economic activities (farming, fishing). Towns have all three sectors: primary, secondary (manufacturing), and tertiary (services — banking, education, healthcare, trade).",
        "h": "Village = mainly primary; Town = primary + secondary + tertiary.",
        "yr": "SS1"
      },
      {
        "q": "A nucleated settlement pattern is one where:",
        "o": ["Houses are spread out over a wide area", "Houses are clustered together around a central point", "Houses are arranged in a straight line", "Houses are built on separate islands"],
        "a": 1,
        "e": "In a nucleated settlement, buildings are clustered closely together, often around a central feature such as a market, church, well, or crossroads. This is common in towns and some villages.",
        "h": "Nucleated = clustered buildings around a central point.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "The Local Government Area (LGA)",
    "topicCode": "SS1-GEO-08",
    "module": "Human Geography",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  The <span class=\"learn-keyword\">Local Government Area (LGA)</span> is the third tier of government in Nigeria and the most basic unit of governance closest to the people. There are <span class=\"learn-keyword\">774 LGAs</span> in Nigeria. Understanding LGAs is important for studying the geographical organisation and administration of Nigeria.\n</div>\n\n<h3 class=\"learn-subheading\">1. Definition of LGA</h3>\n<p class=\"learn-p\">A Local Government Area (LGA) is a defined geographical and administrative unit of government responsible for local administration and service delivery to communities at the grassroots level.</p>\n\n<h3 class=\"learn-subheading\">2. Functions of the LGA</h3>\n<ul class=\"learn-list\">\n  <li>Provision of primary health care and basic education.</li>\n  <li>Maintenance of roads and street lights at local level.</li>\n  <li>Provision of markets, parks, and recreational centres.</li>\n  <li>Registration of births, deaths, and marriages.</li>\n  <li>Collection of local taxes and rates.</li>\n  <li>Sanitation and waste management.</li>\n  <li>Agricultural extension services.</li>\n  <li>Construction and maintenance of drains.</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. Importance of LGA in Geography</h3>\n<ul class=\"learn-list\">\n  <li>LGAs serve as the basic unit for geographical data collection (census, surveys).</li>\n  <li>They provide the framework for planning and development at the local level.</li>\n  <li>They help manage natural resources and the environment locally.</li>\n  <li>They enable students to study regional differences within states and Nigeria.</li>\n</ul>\n\n<h3 class=\"learn-subheading\">4. Structure of Government in Nigeria</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Tier</th><th>Level</th><th>Number</th></tr></thead>\n    <tbody>\n      <tr><td>1st Tier</td><td>Federal Government</td><td>1</td></tr>\n      <tr><td>2nd Tier</td><td>State Government</td><td>36 states + FCT</td></tr>\n      <tr><td>3rd Tier</td><td>Local Government Area (LGA)</td><td>774</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Nigeria has 774 LGAs. LGA is the 3rd tier (local/grassroots) government. Key functions: primary healthcare, basic education, roads, markets, sanitation, births/deaths registration. Kano State has the most LGAs (44); some states have fewer.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> 774 LGAs in Nigeria = 3rd tier government. Functions: healthcare, education, roads, markets, sanitation, tax collection. Importance in geography: data collection unit, local planning, resource management.\n</div>\n  ",
    "questions": [
      {
        "q": "How many Local Government Areas (LGAs) are in Nigeria?",
        "o": ["36", "360", "774", "1,000"],
        "a": 2,
        "e": "Nigeria has 774 Local Government Areas. These are the basic administrative units at the grassroots level across the country's 36 states and the Federal Capital Territory (FCT).",
        "h": "Nigeria has 774 LGAs.",
        "yr": "SS1"
      },
      {
        "q": "The LGA is described as which tier of government in Nigeria?",
        "o": ["First tier (federal)", "Second tier (state)", "Third tier (local)", "Fourth tier (community)"],
        "a": 2,
        "e": "The LGA is the third tier of government in Nigeria's federal structure: Federal (1st tier) → State (2nd tier) → LGA (3rd tier). It is the tier closest to the grassroots.",
        "h": "LGA = 3rd tier of government in Nigeria.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is a function of the Local Government Area?",
        "o": ["Making national laws", "Collecting import duties", "Providing primary healthcare and maintaining local roads", "Controlling military forces"],
        "a": 2,
        "e": "LGAs are responsible for grassroots services including primary healthcare, basic education, maintenance of local roads and drains, markets, sanitation, and registration of births and deaths.",
        "h": "LGA functions: primary healthcare, local roads, markets, sanitation.",
        "yr": "SS1"
      },
      {
        "q": "The LGA is important in geography because it:",
        "o": ["Controls international trade", "Serves as the basic unit for geographical data collection and local planning", "Manages oil pipelines", "Controls immigration at borders"],
        "a": 1,
        "e": "LGAs are the basic geographical and administrative units used for census data collection, demographic surveys, local development planning, and natural resource management at the grassroots level.",
        "h": "LGA in geography: basic unit for data collection and local planning.",
        "yr": "SS1"
      },
      {
        "q": "Which Nigerian state has the highest number of Local Government Areas?",
        "o": ["Lagos", "Rivers", "Kano", "Oyo"],
        "a": 2,
        "e": "Kano State has the highest number of LGAs in Nigeria — 44 LGAs. Lagos State has 20 LGAs despite being the most economically active state.",
        "h": "Kano State has the most LGAs in Nigeria (44).",
        "yr": "SS1"
      },
      {
        "q": "The registration of births, deaths, and marriages is a function of:",
        "o": ["The Federal Government only", "The State Government only", "The Local Government Area", "International organisations"],
        "a": 2,
        "e": "The registration of births, deaths, and marriages is a constitutional function of LGAs in Nigeria. This vital registration is performed at the local level to track population changes.",
        "h": "Birth/death/marriage registration = LGA function.",
        "yr": "SS1"
      },
      {
        "q": "Collection of local taxes and rates is carried out by:",
        "o": ["The Federal Inland Revenue Service", "Customs and Excise", "The Local Government Area", "State Tax Boards only"],
        "a": 2,
        "e": "LGAs collect local taxes, tenement rates, market fees, and other local levies to generate revenue for funding local services and development projects.",
        "h": "LGA collects local taxes, tenement rates, market fees.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following accurately describes the relationship between LGAs and states in Nigeria?",
        "o": ["LGAs are above states in government structure", "LGAs are the third tier of government below state governments", "LGAs and states are on the same level", "LGAs are independent of state governments"],
        "a": 1,
        "e": "In Nigeria's three-tier federal system, LGAs are below state governments (2nd tier) and the federal government (1st tier). LGAs depend partly on federal and state allocations.",
        "h": "Federal (1st) → State (2nd) → LGA (3rd).",
        "yr": "SS1"
      },
      {
        "q": "Sanitation and waste management at the community level is the responsibility of:",
        "o": ["The President", "The state Governor", "The Local Government Area", "Private waste companies only"],
        "a": 2,
        "e": "Sanitation and waste management at the grassroots level is a function of LGAs. They organise refuse collection, drainage maintenance, and environmental health enforcement in their communities.",
        "h": "LGA responsible for sanitation and waste management at community level.",
        "yr": "SS1"
      },
      {
        "q": "LGA chairmen are elected by:",
        "o": ["The President", "The State Governor", "Registered voters within the LGA", "Traditional rulers"],
        "a": 2,
        "e": "LGA chairmen (and councillors) are elected by registered voters within the LGA through local government elections conducted by the State Independent Electoral Commission (SIEC).",
        "h": "LGA chairmen elected by voters in the LGA.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is NOT a function of the LGA?",
        "o": ["Provision of markets and recreational centres", "Agricultural extension services", "Setting national immigration policy", "Maintenance of roads within the local area"],
        "a": 2,
        "e": "Setting national immigration policy is a federal government function. LGA functions are local: markets, healthcare, education, roads, sanitation, agricultural extension, and local tax collection.",
        "h": "Immigration policy = federal function, NOT LGA.",
        "yr": "SS1"
      },
      {
        "q": "The administrative headquarters of an LGA is usually called:",
        "o": ["State capital", "Aso Rock", "Council secretariat or LGA headquarters", "Government House"],
        "a": 2,
        "e": "The administrative headquarters of an LGA is the council secretariat or LGA secretariat, where the LGA chairman's office, council chambers, and administrative departments are located.",
        "h": "LGA headquarters = council secretariat.",
        "yr": "SS1"
      },
      {
        "q": "Primary education (primary schools) in Nigeria is funded and managed mainly by:",
        "o": ["The Federal Government alone", "Private schools only", "Local Government Areas in partnership with state governments", "International NGOs"],
        "a": 2,
        "e": "Primary education in Nigeria is jointly managed by LGAs and state governments. LGAs are constitutionally responsible for primary education, often in partnership with state Universal Basic Education Boards.",
        "h": "Primary education managed by LGAs (with state government).",
        "yr": "SS1"
      },
      {
        "q": "How does an LGA contribute to geographical studies?",
        "o": ["LGAs build geography classrooms", "LGAs provide the spatial units for studying population distribution, resources, and planning", "LGAs have no role in geography", "LGAs only study weather patterns"],
        "a": 1,
        "e": "LGAs are fundamental geographical units. Population censuses, surveys, resource distribution maps, and development plans all use LGA as the basic spatial unit — making them essential in regional and human geography.",
        "h": "LGAs = basic spatial units for population, resource, and planning studies.",
        "yr": "SS1"
      },
      {
        "q": "Agricultural extension services provided by LGAs aim to:",
        "o": ["Build airports for exporting farm produce", "Help farmers improve their farming techniques and productivity", "Create laws about farmland ownership", "Import food from other countries"],
        "a": 1,
        "e": "Agricultural extension services help farmers learn modern farming techniques, use improved seeds and fertilisers, access credit, and improve productivity. LGAs deliver these at the grassroots farming community level.",
        "h": "Agricultural extension = helps farmers improve techniques and productivity.",
        "yr": "SS1"
      },
      {
        "q": "The Federal Capital Territory (FCT) Abuja has how many area councils (equivalent of LGAs)?",
        "o": ["3", "6", "36", "774"],
        "a": 1,
        "e": "The FCT Abuja has 6 Area Councils which function as the equivalent of LGAs: Abuja Municipal, Bwari, Gwagwalada, Kuje, Kwali, and Abaji.",
        "h": "FCT Abuja has 6 Area Councils (equivalent of LGAs).",
        "yr": "SS1"
      },
      {
        "q": "The provision of drains and drainage maintenance in towns is handled by:",
        "o": ["The Federal Ministry of Works", "Only private contractors", "Local Government Areas", "State Environmental Ministries only"],
        "a": 2,
        "e": "Construction and maintenance of drains at the local level is a function of LGAs. Proper drainage prevents flooding and improves sanitation — essential local services.",
        "h": "Drainage maintenance = LGA function.",
        "yr": "SS1"
      },
      {
        "q": "From a geographical perspective, why is it important to study LGAs?",
        "o": ["LGAs control international boundaries", "They allow study of local variations in population, resources, and development within Nigeria", "LGAs set exchange rates", "LGAs build international airports"],
        "a": 1,
        "e": "Studying LGAs in geography allows us to examine local variations in population density, economic activities, natural resources, infrastructure, and challenges across Nigeria's diverse regions.",
        "h": "LGAs allow study of local geographical variations within Nigeria.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following describes the relationship between LGA and the community?",
        "o": ["LGAs have no contact with communities", "LGAs are the tier of government closest to the grassroots (community level)", "LGAs only deal with international matters", "LGAs govern only urban areas"],
        "a": 1,
        "e": "LGAs are the tier of government closest to the people at the grassroots level. They directly serve communities with local infrastructure, healthcare, education, and administrative services.",
        "h": "LGA = closest tier of government to the grassroots community.",
        "yr": "SS1"
      },
      {
        "q": "If Kano State has 44 LGAs and Borno State has 27 LGAs, the difference between them is:",
        "o": ["11 LGAs", "17 LGAs", "21 LGAs", "71 LGAs"],
        "a": 1,
        "e": "Difference = 44 − 27 = 17 LGAs. Kano State has the most LGAs (44) in Nigeria; states vary widely in their number of LGAs based on population and area.",
        "h": "44 − 27 = 17 LGAs difference.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Mountains",
    "topicCode": "SS1-GEO-09",
    "module": "Physical Geography",
    "term": "Second Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Mountains</span> are large landforms that rise steeply above the surrounding landscape. They are formed by different geological processes and have significant influences on climate, settlement, and economic activities.\n</div>\n\n<h3 class=\"learn-subheading\">1. Types of Mountains</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Type</th><th>Formation</th><th>Examples</th></tr></thead>\n    <tbody>\n      <tr><td>Fold Mountains</td><td>Compressional forces fold rock layers into ridges and valleys</td><td>Himalayas (Asia), Alps (Europe), Rockies (N. America)</td></tr>\n      <tr><td>Block Mountains (Horsts)</td><td>Tensional forces cause blocks of land to be uplifted between faults</td><td>Black Forest (Germany), Vosges (France), Ruwenzori (Africa)</td></tr>\n      <tr><td>Volcanic Mountains</td><td>Accumulation of lava, ash, and other materials from volcanic eruptions</td><td>Mt. Kilimanjaro (Tanzania), Mt. Etna (Italy), Mt. Cameroon (West Africa)</td></tr>\n      <tr><td>Residual (Relict) Mountains</td><td>Resistant rocks remain after surrounding softer rocks are eroded</td><td>Adamawa Highlands (Nigeria/Cameroon), Aso Rock (Nigeria)</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">2. Importance of Mountains</h3>\n<ul class=\"learn-list\">\n  <li><strong>Climate influence:</strong> Mountains affect rainfall (orographic rainfall on windward slopes; rain shadow on leeward side).</li>\n  <li><strong>Water source:</strong> Mountains are sources of major rivers (Himalayas → Ganges, Indus).</li>\n  <li><strong>Tourism:</strong> Scenic mountain landscapes attract tourists (skiing, hiking).</li>\n  <li><strong>Mineral resources:</strong> Mountains contain rich mineral deposits.</li>\n  <li><strong>Defence:</strong> Mountains serve as natural barriers and defensive positions.</li>\n  <li><strong>Vegetation zones:</strong> Mountains have distinct vegetation zones from base to summit.</li>\n</ul>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Four mountain types: Fold (compression → folded rock), Block/Horst (faulting → uplifted block), Volcanic (lava accumulation), Residual (erosion leaves hard rock). Key examples: Himalayas (fold), Kilimanjaro (volcanic), Ruwenzori (block), Aso Rock (residual). Mt. Cameroon is West Africa's highest volcanic mountain.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Mountains: fold (compression), block (faulting), volcanic (lava), residual (erosion). Importance: climate/rainfall, river sources, tourism, minerals, defence, vegetation zones. Mt. Cameroon = West Africa's highest; Kilimanjaro = Africa's highest.\n</div>\n  ",
    "questions": [
      {
        "q": "Fold mountains are formed by:",
        "o": ["Tensional forces pulling rocks apart", "Compressional forces pushing rock layers to fold into ridges", "Volcanic eruptions building up lava", "Erosion of surrounding soft rocks leaving hard rock behind"],
        "a": 1,
        "e": "Fold mountains form when compressional (convergent) tectonic forces push rock layers together, causing them to buckle and fold upward into ridges and downward into valleys (synclines).",
        "h": "Fold mountains = compression pushes rocks → fold upward.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is an example of a fold mountain?",
        "o": ["Mt. Kilimanjaro", "Ruwenzori", "Aso Rock", "The Himalayas"],
        "a": 3,
        "e": "The Himalayas in Asia are the world's highest fold mountains, formed by the collision of the Indian and Eurasian tectonic plates. Other fold mountains include the Alps, Rockies, and Andes.",
        "h": "Himalayas = fold mountains (formed by plate collision).",
        "yr": "SS1"
      },
      {
        "q": "Block mountains (horsts) are formed by:",
        "o": ["Compressional folding of rock layers", "Accumulation of volcanic lava", "Uplifting of land blocks between fault lines due to tensional forces", "Erosion of surrounding rocks"],
        "a": 2,
        "e": "Block mountains (horsts) form when tensional forces pull the Earth's crust apart, causing blocks of land to be pushed upward (or remain elevated) between fault lines while adjacent blocks drop to form rift valleys (grabens).",
        "h": "Block mountains = land uplifted between faults (tensional forces).",
        "yr": "SS1"
      },
      {
        "q": "Mt. Kilimanjaro in Tanzania is an example of a:",
        "o": ["Fold mountain", "Block mountain", "Volcanic mountain", "Residual mountain"],
        "a": 2,
        "e": "Mt. Kilimanjaro is Africa's highest mountain and is a volcanic mountain, formed by the accumulation of volcanic materials (lava, ash, cinders) through eruptions over millions of years.",
        "h": "Kilimanjaro = volcanic mountain (Africa's highest).",
        "yr": "SS1"
      },
      {
        "q": "Residual (relict) mountains are formed by:",
        "o": ["Volcanic eruptions", "Compressional folding", "Remaining after surrounding softer rocks are eroded away", "Uplifting between fault lines"],
        "a": 2,
        "e": "Residual mountains form when differential erosion removes surrounding softer rocks over millions of years, leaving hard, resistant rock masses standing as mountains (inselbergs or monadnocks).",
        "h": "Residual mountains = hard rock remains after soft rock is eroded.",
        "yr": "SS1"
      },
      {
        "q": "Aso Rock in Abuja, Nigeria is an example of:",
        "o": ["A fold mountain", "A volcanic mountain", "A residual (relict) mountain", "A block mountain"],
        "a": 2,
        "e": "Aso Rock is a massive granite inselberg (residual mountain) — it remains because its hard granite resisted erosion while surrounding softer rocks were worn away over millions of years.",
        "h": "Aso Rock = residual (relict) mountain (hard granite inselberg).",
        "yr": "SS1"
      },
      {
        "q": "The Ruwenzori Mountains in East Africa are an example of:",
        "o": ["Fold mountains", "Block mountains", "Volcanic mountains", "Residual mountains"],
        "a": 1,
        "e": "The Ruwenzori Mountains (on the Uganda-DRC border) are block mountains — they were uplifted between fault lines of the East African Rift System. They are also called the 'Mountains of the Moon'.",
        "h": "Ruwenzori = block mountains (East African rift system).",
        "yr": "SS1"
      },
      {
        "q": "Orographic rainfall refers to rainfall that occurs when:",
        "o": ["Hot air rises over the equator", "Moist air is forced to rise over a mountain, cools, and condenses", "Cool air sinks over oceans and condenses", "Air converges at a weather front"],
        "a": 1,
        "e": "Orographic rainfall occurs when moist air is forced to rise over a mountain range. As it rises, it cools, moisture condenses, and rainfall occurs on the windward side. The leeward side receives little rain (rain shadow).",
        "h": "Orographic rain: moist air rises over mountain → cools → rains on windward side.",
        "yr": "SS1"
      },
      {
        "q": "The leeward side of a mountain receives less rainfall because:",
        "o": ["It is closer to the equator", "The air has already lost its moisture on the windward side (rain shadow effect)", "It is always higher in altitude", "The wind does not reach the leeward side"],
        "a": 1,
        "e": "The leeward side (sheltered from prevailing winds) is in the rain shadow. Air descends on this side, warming and becoming drier — it has already lost its moisture on the windward side.",
        "h": "Leeward = rain shadow (dry side; air already lost moisture).",
        "yr": "SS1"
      },
      {
        "q": "Mt. Cameroon is significant in West Africa because it is:",
        "o": ["The highest fold mountain in Africa", "West Africa's highest mountain and an active volcano", "A block mountain in Nigeria", "A residual mountain on the coast"],
        "a": 1,
        "e": "Mt. Cameroon (4,095 m) is the highest mountain in West Africa and one of Africa's most active volcanoes. It last erupted in 2012 and is located near the coast of Cameroon.",
        "h": "Mt. Cameroon = West Africa's highest mountain + active volcano.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is an importance of mountains?",
        "o": ["They prevent all rainfall in surrounding areas", "They are sources of major rivers and freshwater", "They block all human movement permanently", "They have no economic value"],
        "a": 1,
        "e": "Mountains are major sources of rivers because snow and ice melt from their peaks and glaciers feed river systems. The Himalayas feed the Ganges, Indus, and Yangtze rivers.",
        "h": "Mountains = sources of major rivers (snowmelt and glaciers).",
        "yr": "SS1"
      },
      {
        "q": "The Adamawa Highlands in Nigeria/Cameroon are an example of:",
        "o": ["Volcanic mountains", "Fold mountains", "Block mountains", "Residual highlands"],
        "a": 3,
        "e": "The Adamawa Highlands are residual/relict highlands — ancient resistant rocks that have survived erosion while surrounding areas were worn down. They form the high plateau along the Nigeria-Cameroon border.",
        "h": "Adamawa Highlands = residual highlands.",
        "yr": "SS1"
      },
      {
        "q": "Mountain tourism is important because:",
        "o": ["Mountains have no scenic value", "Scenic landscapes attract visitors who generate income for local communities", "Mountains are too dangerous for any activity", "Only geographers visit mountains"],
        "a": 1,
        "e": "Mountain tourism provides income for local communities through activities like hiking, skiing, mountain climbing, and scenic tours. It supports hotels, restaurants, guides, and local crafts industries.",
        "h": "Mountain tourism = scenic attraction → income for communities.",
        "yr": "SS1"
      },
      {
        "q": "Why do mountains have distinct vegetation zones from base to summit?",
        "o": ["Rocks change as you go up the mountain", "Temperature decreases and rainfall patterns change with altitude", "Human settlement prevents vegetation at higher altitudes", "Sunlight is blocked at higher altitudes"],
        "a": 1,
        "e": "As altitude increases, temperature decreases (by about 6.5°C per 1,000 m). This creates distinct vegetation zones: tropical forest at the base → temperate forest → alpine grassland → snow at the summit.",
        "h": "Altitude increases → temperature decreases → distinct vegetation zones.",
        "yr": "SS1"
      },
      {
        "q": "The Alps mountain range is located in:",
        "o": ["North America", "Asia", "Africa", "Europe"],
        "a": 3,
        "e": "The Alps are located in central Europe, spanning countries including Switzerland, France, Italy, Austria, and Germany. They are fold mountains formed by the collision of the African and Eurasian plates.",
        "h": "Alps = fold mountains in Europe (Switzerland, France, Italy, etc.).",
        "yr": "SS1"
      },
      {
        "q": "Mountains can serve as natural barriers to:",
        "o": ["Ocean waves", "Military invasion, cultural diffusion, and transportation", "River flow", "Cloud formation"],
        "a": 1,
        "e": "Mountains serve as natural barriers — they can slow or prevent military invasions, restrict cultural diffusion, and make transportation difficult. Historically, mountain ranges have served as political borders.",
        "h": "Mountains = natural barriers to movement, invasion, and cultural diffusion.",
        "yr": "SS1"
      },
      {
        "q": "Which African country contains Africa's highest peak, Mt. Kilimanjaro?",
        "o": ["Kenya", "Uganda", "Tanzania", "Ethiopia"],
        "a": 2,
        "e": "Mt. Kilimanjaro (5,895 m), Africa's highest peak, is located in Tanzania, near the border with Kenya. It is a volcanic mountain with three volcanic cones: Kibo, Mawenzi, and Shira.",
        "h": "Mt. Kilimanjaro (Africa's highest) is in Tanzania.",
        "yr": "SS1"
      },
      {
        "q": "The Andes Mountains in South America are classified as:",
        "o": ["Block mountains", "Volcanic mountains", "Residual mountains", "Fold mountains"],
        "a": 3,
        "e": "The Andes are fold mountains — formed by the subduction of the Nazca plate under the South American plate. They are the longest continental mountain range in the world.",
        "h": "Andes = fold mountains (plate subduction, South America).",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is a mineral found in mountainous areas?",
        "o": ["Seawater salt only", "Gold, tin, coal, and other metal ores", "Palm oil", "Sand dunes"],
        "a": 1,
        "e": "Mountains are rich in mineral resources — gold, tin, iron ore, coal, copper, and precious stones are commonly found in mountainous and highland areas due to geological processes that concentrate minerals.",
        "h": "Mountains are rich in minerals: gold, tin, iron ore, coal.",
        "yr": "SS1"
      },
      {
        "q": "Block mountains are also called:",
        "o": ["Grabens", "Horsts", "Synclines", "Anticlines"],
        "a": 1,
        "e": "Block mountains are called horsts — uplifted blocks of land between faults. The sunken blocks between faults are called grabens (rift valleys).",
        "h": "Block mountain = horst (uplifted block between faults).",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Lowlands",
    "topicCode": "SS1-GEO-10",
    "module": "Physical Geography",
    "term": "Second Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Lowlands</span> are areas of relatively flat or gently undulating terrain close to sea level. They are among the most economically important landforms because they support dense populations, agriculture, and major settlements.\n</div>\n\n<h3 class=\"learn-subheading\">1. Types of Lowlands</h3>\n<ul class=\"learn-list\">\n  <li><strong>Plains:</strong> Flat, extensive areas of low-lying land. Examples: the Great Plains of North America, the Northern Plains of Nigeria.</li>\n  <li><strong>Plateaus:</strong> Elevated flat-topped landforms, often surrounded by escarpments. Examples: Jos Plateau (Nigeria), East African Plateau.</li>\n  <li><strong>River floodplains and deltas:</strong> Flat lands formed by river deposition. Example: Niger Delta (Nigeria).</li>\n  <li><strong>Coastal lowlands:</strong> Flat land along coastlines. Example: the coastal areas of Lagos and Cross River states.</li>\n</ul>\n\n<h3 class=\"learn-subheading\">2. Examples in Nigeria and West Africa</h3>\n<ul class=\"learn-list\">\n  <li><strong>Niger Delta:</strong> A vast lowland formed by river deposition; rich in oil and gas.</li>\n  <li><strong>Sokoto Plains:</strong> Low-lying areas in north-western Nigeria used for farming.</li>\n  <li><strong>Chad Basin:</strong> A lowland depression in north-eastern Nigeria.</li>\n  <li><strong>Coastal plains:</strong> Along the Atlantic coast from Nigeria to Senegal.</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. Importance of Lowlands</h3>\n<ul class=\"learn-list\">\n  <li><strong>Agriculture:</strong> Flat land is ideal for mechanised farming; fertile alluvial soils in floodplains.</li>\n  <li><strong>Settlements:</strong> Most of the world's major cities are built on lowlands due to ease of construction and accessibility.</li>\n  <li><strong>Transport:</strong> Roads and railways are easier and cheaper to build on flat land.</li>\n  <li><strong>Industry:</strong> Factory construction is easier and cheaper on flat ground.</li>\n  <li><strong>Trade:</strong> Coastal lowlands often have ports and harbours.</li>\n</ul>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Lowland types: plains, plateaus, river floodplains/deltas, coastal lowlands. Nigerian examples: Niger Delta, Sokoto Plains, Chad Basin. Lowlands are important for agriculture, settlements, transport, and industry. Most major cities are on lowlands.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Lowlands = flat or gently undulating land near sea level. Types: plains, plateaus, floodplains, coastal. Nigerian examples: Niger Delta, Sokoto Plains, Chad Basin. Importance: farming, settlements, transport, industry, trade.\n</div>\n  ",
    "questions": [
      {
        "q": "Lowlands are areas of land that are:",
        "o": ["Very mountainous and rugged", "Flat or gently undulating and close to sea level", "Always below sea level", "Only found near rivers"],
        "a": 1,
        "e": "Lowlands are areas of relatively flat or gently undulating terrain at low elevations (close to or at sea level). They are contrasted with highlands and mountains.",
        "h": "Lowlands = flat or gently undulating, close to sea level.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is an example of a lowland in Nigeria?",
        "o": ["Jos Plateau", "Adamawa Highlands", "Niger Delta", "Mambilla Plateau"],
        "a": 2,
        "e": "The Niger Delta is a vast lowland formed by sediment deposition at the mouth of the Niger River. It is a low-lying, flat, swampy area rich in oil and gas resources.",
        "h": "Niger Delta = lowland (river delta, flat, swampy).",
        "yr": "SS1"
      },
      {
        "q": "A plateau is a type of landform characterised by:",
        "o": ["Very steep slopes and jagged peaks", "A flat or gently rolling elevated surface surrounded by escarpments", "A low-lying depression filled with water", "Sand dunes and desert",],
        "a": 1,
        "e": "A plateau is an elevated flat-topped landform, often bounded by escarpments (steep cliff-like edges). The Jos Plateau in Nigeria is an example, rising abruptly from surrounding lowlands.",
        "h": "Plateau = elevated flat-topped land bounded by escarpments.",
        "yr": "SS1"
      },
      {
        "q": "Floodplains and deltas are formed by:",
        "o": ["Volcanic eruptions", "Tectonic plate collisions", "River deposition of sediment", "Wind erosion"],
        "a": 2,
        "e": "Floodplains and deltas are lowlands formed by rivers depositing sediments. Floodplains are flat areas beside rivers; deltas form at the river's mouth where it meets the sea.",
        "h": "Floodplains and deltas = formed by river sediment deposition.",
        "yr": "SS1"
      },
      {
        "q": "Why are most major world cities built on lowlands?",
        "o": ["Lowlands have a cooler climate", "Flat land is easier to build on, more accessible, and has better soil", "Lowlands are always near mountains", "Cities can only be built near rivers"],
        "a": 1,
        "e": "Lowlands are favoured for settlement because flat land is easier and cheaper to construct on, transport networks are easily built, soil is often fertile for agriculture, and coastal lowlands have port access.",
        "h": "Cities on lowlands: flat land easier to build, accessible, good soil.",
        "yr": "SS1"
      },
      {
        "q": "The Sokoto Plains in north-western Nigeria are used primarily for:",
        "o": ["Mining of petroleum", "Tourism and mountaineering", "Farming and cattle grazing", "Deep-sea fishing"],
        "a": 2,
        "e": "The Sokoto Plains are low-lying areas used extensively for agriculture — mainly millet, sorghum, groundnuts, and cattle grazing. The flat, open terrain is ideal for farming.",
        "h": "Sokoto Plains = low-lying; used for farming and cattle grazing.",
        "yr": "SS1"
      },
      {
        "q": "The alluvial soils of floodplains are very fertile because:",
        "o": ["They are formed from volcanic ash", "Rivers deposit rich, fine-grained sediments during flooding", "They are made of sand only", "Salt from the sea makes them fertile"],
        "a": 1,
        "e": "During floods, rivers overflow their banks and deposit fine-grained nutrient-rich sediment (alluvium) on the floodplain. This makes alluvial soils some of the most fertile in the world.",
        "h": "Alluvial soil = river-deposited fine sediment → very fertile.",
        "yr": "SS1"
      },
      {
        "q": "The Chad Basin in north-eastern Nigeria is described as:",
        "o": ["A high plateau", "A coastal lowland", "A volcanic cone", "A lowland depression"],
        "a": 3,
        "e": "The Chad Basin is a large inland drainage basin — a low-lying depression shared by Nigeria, Niger, Chad, and Cameroon. Lake Chad occupies part of this lowland basin.",
        "h": "Chad Basin = lowland depression (inland drainage basin).",
        "yr": "SS1"
      },
      {
        "q": "Transport infrastructure (roads and railways) is cheaper to build on lowlands because:",
        "o": ["Lowlands receive more rainfall for construction", "Flat terrain requires less engineering, fewer bridges, and less cutting through rock", "Lowlands always have more workers", "Lowland roads last longer due to dry weather"],
        "a": 1,
        "e": "Building roads and railways on flat lowland terrain is much cheaper than in mountainous areas because it requires less rock cutting, fewer tunnels, fewer bridges, and less grading of slopes.",
        "h": "Flat lowland terrain = cheaper and easier to build roads/railways.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is a problem associated with lowland areas?",
        "o": ["Difficulty in farming due to steep slopes", "Risk of flooding during heavy rainfall and high tides", "Lack of water sources", "Very cold temperatures preventing settlement"],
        "a": 1,
        "e": "Lowland areas, especially floodplains and coastal lowlands, are prone to flooding during heavy rainfall or high tides (storm surges). The Niger Delta frequently experiences flooding, impacting communities.",
        "h": "Lowland problem: flooding (rivers overflow, storm surges).",
        "yr": "SS1"
      },
      {
        "q": "The Jos Plateau in Nigeria is an example of:",
        "o": ["A coastal lowland", "A river floodplain", "A plateau (elevated flat-topped landform)", "A rift valley"],
        "a": 2,
        "e": "The Jos Plateau is a plateau — an elevated flat-topped landform in central Nigeria. It rises to about 1,200–1,300 m above sea level, giving it a cooler climate than surrounding lowlands.",
        "h": "Jos Plateau = plateau (elevated flat-topped land in central Nigeria).",
        "yr": "SS1"
      },
      {
        "q": "The Niger Delta is economically important to Nigeria mainly because:",
        "o": ["It has the best farmland in Nigeria", "It contains vast petroleum (oil and gas) reserves", "It is the highest land in Nigeria", "It receives the most rainfall"],
        "a": 1,
        "e": "The Niger Delta lowland is Nigeria's oil and gas producing region. It contains the vast majority of Nigeria's petroleum reserves, making it the economic hub of the nation's oil industry.",
        "h": "Niger Delta = Nigeria's petroleum heartland (oil and gas).",
        "yr": "SS1"
      },
      {
        "q": "Mechanised farming is more practical in lowlands because:",
        "o": ["Lowlands have better rainfall", "Flat terrain allows large machines to operate efficiently without obstacles", "Lowlands have no weeds", "Machines work better in warm weather"],
        "a": 1,
        "e": "Flat lowland terrain allows tractors, harvesters, and other large agricultural machinery to operate efficiently across large fields. Steep or rugged terrain makes machine use difficult or impossible.",
        "h": "Flat terrain = machines operate efficiently = mechanised farming practical.",
        "yr": "SS1"
      },
      {
        "q": "Coastal lowlands are important for trade because:",
        "o": ["They receive the most sunlight", "They provide sites for ports, harbours, and harbours which facilitate maritime trade", "They have the richest soil for agriculture", "They have cooler temperatures for workers"],
        "a": 1,
        "e": "Coastal lowlands provide suitable sites for ports and harbours (flat land near water), facilitating import and export trade via shipping. Nigeria's ports at Lagos, Apapa, and Port Harcourt are all on coastal lowlands.",
        "h": "Coastal lowlands → ports and harbours → maritime trade.",
        "yr": "SS1"
      },
      {
        "q": "The Great Plains of North America are an example of:",
        "o": ["Block mountains", "Fold mountains", "Extensive plains (lowlands)", "Volcanic plateaus"],
        "a": 2,
        "e": "The Great Plains are vast flat lowland plains stretching across the central United States and Canada. They are one of the world's most important agricultural regions, producing large amounts of wheat and corn.",
        "h": "Great Plains of North America = extensive flat plains (lowlands).",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is NOT an advantage of lowlands?",
        "o": ["Easy construction of roads and buildings", "Fertile alluvial soils for farming", "Natural protection from invasion and attack", "Suitable for large-scale industries"],
        "a": 2,
        "e": "Natural protection from invasion is an advantage of highlands and mountains — not lowlands. Lowlands are actually more vulnerable to attacks because they lack natural defensive barriers.",
        "h": "Natural defence = highland/mountain advantage, NOT lowland.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following best explains why lowland areas support large populations?",
        "o": ["Lowlands are always cool and pleasant", "They offer flat land for farming, building, transport, and commerce", "People prefer to live near the sea only", "Lowlands have fewer diseases"],
        "a": 1,
        "e": "Lowlands support large populations because they offer flat land ideal for farming, construction, industry, and transport. These economic advantages attract settlement and population growth.",
        "h": "Lowlands support populations: farming, building, transport, commerce.",
        "yr": "SS1"
      },
      {
        "q": "Swampy coastal lowlands such as the Niger Delta present challenges for settlement because:",
        "o": ["They have very hot climates", "They are difficult to build on, prone to flooding, and infested with mosquitoes", "They have no economic resources", "The soil is too fertile for settlements"],
        "a": 1,
        "e": "Swampy coastal lowlands like the Niger Delta present challenges: unstable, waterlogged soil makes construction difficult; frequent flooding threatens settlements; and stagnant water breeds malaria-carrying mosquitoes.",
        "h": "Swampy lowlands: difficult to build, flooding, mosquitoes/malaria.",
        "yr": "SS1"
      },
      {
        "q": "River deltas like the Niger Delta form when:",
        "o": ["Rivers flow very fast at their mouths", "Rivers deposit their sediment load where they meet the sea, building up a fan-shaped landform", "Volcanoes erupt near rivers", "Tectonic plates collide near coastlines"],
        "a": 1,
        "e": "River deltas form when a river slows down as it meets the sea (or a lake), losing energy and depositing its sediment load. This builds up a fan or triangle-shaped landform of low-lying land.",
        "h": "Delta = river slows → deposits sediment at mouth → builds up land.",
        "yr": "SS1"
      },
      {
        "q": "The East African Plateau is an example of a:",
        "o": ["River floodplain", "Coastal plain", "Plateau (elevated flat land)", "Rift valley"],
        "a": 2,
        "e": "The East African Plateau is a large elevated plateau covering much of East Africa (Kenya, Tanzania, Uganda). It rises to 1,000–2,000 m above sea level and supports diverse ecosystems and large human populations.",
        "h": "East African Plateau = elevated plateau (highland with flat top).",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "The Environment",
    "topicCode": "SS1-GEO-11",
    "module": "Environmental Geography",
    "term": "Second Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  The <span class=\"learn-keyword\">environment</span> is everything that surrounds us — both natural and human-made. It includes the air we breathe, the water we drink, the land we live on, and the societies we belong to. Understanding the environment and human interactions with it is central to geography.\n</div>\n\n<h3 class=\"learn-subheading\">1. Meaning of Environment</h3>\n<p class=\"learn-p\">The environment refers to all living and non-living things surrounding an organism, including the physical, biological, social, and cultural conditions that influence its existence and development.</p>\n\n<h3 class=\"learn-subheading\">2. Components of the Environment</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Component</th><th>Elements</th></tr></thead>\n    <tbody>\n      <tr><td><strong>Physical (Natural) Environment</strong></td><td>Landforms, rocks, soils, water bodies, climate, vegetation, atmosphere</td></tr>\n      <tr><td><strong>Biological (Biotic) Environment</strong></td><td>Plants (flora), animals (fauna), micro-organisms</td></tr>\n      <tr><td><strong>Human (Social/Cultural) Environment</strong></td><td>Population, settlements, culture, religion, economy, government</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">3. Interactions Between Humans and the Environment</h3>\n<p class=\"learn-p\">Humans and the environment constantly interact in two directions:</p>\n<ul class=\"learn-list\">\n  <li><strong>Environment influencing humans:</strong> Climate affects farming; terrain affects settlement; rivers provide water and transport.</li>\n  <li><strong>Humans influencing the environment:</strong> Deforestation, mining, agriculture, urbanisation, pollution — all modify the natural environment.</li>\n</ul>\n\n<h3 class=\"learn-subheading\">4. Environmental Problems</h3>\n<ul class=\"learn-list\">\n  <li>Deforestation — clearing forests for farming and logging.</li>\n  <li>Soil erosion — washing away of topsoil.</li>\n  <li>Air and water pollution — from industries, vehicles, and chemicals.</li>\n  <li>Desertification — encroachment of desert into productive land.</li>\n  <li>Flooding — from heavy rain, poor drainage, and deforestation.</li>\n  <li>Global warming/climate change — from increased greenhouse gases.</li>\n</ul>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Three environment components: Physical (landforms, climate), Biological (plants, animals), Human/Social (culture, economy, settlements). Human-environment interaction is two-way: environment affects humans AND humans modify the environment. Key problems: deforestation, erosion, pollution, desertification.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Environment = all surroundings (living + non-living). Components: physical, biological, human. Interactions are two-way. Major problems: deforestation, erosion, pollution, desertification, flooding, climate change.\n</div>\n  ",
    "questions": [
      {
        "q": "The environment is best defined as:",
        "o": ["Only the natural landscape around us", "All living and non-living things surrounding an organism", "Only the weather and climate of a place", "The buildings and infrastructure in a city"],
        "a": 1,
        "e": "The environment refers to all living and non-living things that surround an organism — including physical, biological, social, and cultural conditions that influence its existence.",
        "h": "Environment = all living and non-living surroundings.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following belongs to the PHYSICAL component of the environment?",
        "o": ["Plants and animals", "Culture and religion", "Landforms, climate, and water bodies", "Population and settlements"],
        "a": 2,
        "e": "The physical environment includes natural, non-living elements: landforms (mountains, plains), climate, soils, water bodies (rivers, lakes, oceans), rocks, and the atmosphere.",
        "h": "Physical environment: landforms, climate, soils, water bodies.",
        "yr": "SS1"
      },
      {
        "q": "The biological (biotic) component of the environment includes:",
        "o": ["Rivers and lakes", "Rocks and minerals", "Plants, animals, and micro-organisms", "Culture and settlements"],
        "a": 2,
        "e": "The biological (biotic) environment consists of all living organisms — plants (flora), animals (fauna), and micro-organisms such as bacteria and fungi.",
        "h": "Biological environment = plants, animals, micro-organisms.",
        "yr": "SS1"
      },
      {
        "q": "The human (social/cultural) environment includes:",
        "o": ["Rocks and soils", "Rivers and rainfall", "Population, culture, economy, and settlements", "Vegetation and wildlife"],
        "a": 2,
        "e": "The human or social/cultural environment includes all human-created or influenced elements — population distribution, cultural practices, religious beliefs, economic activities, governance, and settlements.",
        "h": "Human environment: population, culture, economy, settlements.",
        "yr": "SS1"
      },
      {
        "q": "The statement 'Climate affects what crops farmers can grow' is an example of:",
        "o": ["Humans modifying the environment", "The environment influencing human activities", "Biological factors controlling climate", "Cultural practices creating new landforms"],
        "a": 1,
        "e": "When climate determines which crops can be grown, it is an example of the environment influencing human activities. This is one direction of the two-way human-environment interaction.",
        "h": "Climate → crops = environment influencing humans.",
        "yr": "SS1"
      },
      {
        "q": "Deforestation as an environmental problem is caused mainly by:",
        "o": ["Natural rainfall washing away forests", "Human activities like farming, logging, and urban expansion", "Volcanic eruptions destroying forests", "Ocean tides removing coastal forests"],
        "a": 1,
        "e": "Deforestation is primarily caused by human activities: clearing forests for agriculture (especially cash crop farming), logging for timber, and clearing land for urban expansion and road construction.",
        "h": "Deforestation = human activities (farming, logging, urban expansion).",
        "yr": "SS1"
      },
      {
        "q": "Desertification is the process by which:",
        "o": ["Deserts become farmland", "Productive land becomes increasingly arid and desert-like", "Rainfall increases in dry areas", "Plants invade sand dunes"],
        "a": 1,
        "e": "Desertification is the degradation of productive land in arid and semi-arid areas into desert-like conditions, caused by overgrazing, deforestation, poor farming practices, and climate change.",
        "h": "Desertification = productive land becoming desert-like.",
        "yr": "SS1"
      },
      {
        "q": "Human-environment interaction is described as 'two-way' because:",
        "o": ["It only happens in two countries", "The environment affects humans AND humans modify the environment", "Only plants and humans interact", "The interaction only happens twice a year"],
        "a": 1,
        "e": "Human-environment interaction is two-way: the environment shapes human activities (climate → farming; terrain → settlement patterns) while humans also modify the environment (deforestation, pollution, urbanisation).",
        "h": "Two-way: environment affects humans + humans affect environment.",
        "yr": "SS1"
      },
      {
        "q": "Air pollution from factories and vehicles is an environmental problem caused by:",
        "o": ["Natural volcanic eruptions only", "Human industrial and transportation activities releasing harmful emissions", "Plant respiration releasing toxic gases", "Ocean evaporation creating smog"],
        "a": 1,
        "e": "Air pollution is mainly caused by human industrial activities (factories releasing smoke and chemicals) and transportation (vehicles burning fossil fuels releasing CO₂, CO, and particulates).",
        "h": "Air pollution = human industry and vehicle emissions.",
        "yr": "SS1"
      },
      {
        "q": "Soil erosion occurs when:",
        "o": ["Soil becomes too fertile and expands", "The topsoil is removed by water or wind, often worsened by deforestation", "Rocks dissolve and disappear", "The soil absorbs too much water"],
        "a": 1,
        "e": "Soil erosion is the removal of topsoil by water or wind. It is worsened by deforestation (no roots to hold soil), overgrazing, and poor farming practices.",
        "h": "Soil erosion = topsoil removed by water or wind (worsened by deforestation).",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is an example of humans modifying the physical environment?",
        "o": ["Rain falling on farmland", "Rivers flooding naturally", "Building a dam across a river to create a reservoir", "Seasons changing with the Earth's revolution"],
        "a": 2,
        "e": "Building a dam modifies the physical environment — it blocks and redirects a river, creating an artificial reservoir. This is a clear example of humans actively changing the natural landscape.",
        "h": "Humans modifying environment: building dams, deforestation, mining.",
        "yr": "SS1"
      },
      {
        "q": "Climate change and global warming are caused mainly by:",
        "o": ["Increased rainfall globally", "Rising greenhouse gas emissions (CO₂, methane) from human activities", "The Sun getting closer to Earth", "Natural volcanic eruptions only"],
        "a": 1,
        "e": "Global warming is primarily caused by increasing concentrations of greenhouse gases — especially CO₂ from burning fossil fuels and methane from agriculture and landfills — trapping heat in the atmosphere.",
        "h": "Global warming = greenhouse gas emissions (CO₂, methane) from human activities.",
        "yr": "SS1"
      },
      {
        "q": "The encroachment of the Sahara Desert southward into Nigeria's northern states is an example of:",
        "o": ["Soil erosion", "Flooding", "Desertification", "Air pollution"],
        "a": 2,
        "e": "The southward advance of the Sahara Desert into Nigeria's Sahel zone (Sokoto, Kebbi, Borno, Yobe states) is desertification — caused by overgrazing, deforestation, and changing rainfall patterns.",
        "h": "Sahara advancing southward into Nigeria = desertification.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following best describes environmental conservation?",
        "o": ["Destroying the environment for development", "Protecting and sustainably managing natural resources for present and future generations", "Using up all natural resources as quickly as possible", "Preventing all human use of natural resources"],
        "a": 1,
        "e": "Environmental conservation means protecting and sustainably managing natural resources — forests, water, soil, and wildlife — so they remain available for both present and future generations.",
        "h": "Conservation = sustainable management of resources for present + future.",
        "yr": "SS1"
      },
      {
        "q": "The physical environment influences human settlement patterns because:",
        "o": ["Humans never change their settlement patterns", "Terrain, water availability, and climate determine where people prefer to live", "Settlement is always uniform regardless of environment", "Physical features have no impact on where people live"],
        "a": 1,
        "e": "The physical environment strongly influences settlement: people prefer flat, fertile land near water; avoid steep mountains, deserts, and flood-prone areas; and settle where the climate supports food production.",
        "h": "Physical environment (terrain, water, climate) shapes where people settle.",
        "yr": "SS1"
      },
      {
        "q": "Water pollution in Nigerian rivers is mainly caused by:",
        "o": ["Natural flooding", "Industrial effluents, oil spills, and dumping of sewage and agricultural chemicals", "Evaporation reducing water quality", "Natural weathering of rocks"],
        "a": 1,
        "e": "Nigeria's rivers face pollution from industrial effluents, oil spills (especially in the Niger Delta), agricultural chemical runoff, and sewage discharge — all human activities.",
        "h": "River pollution: industrial effluents, oil spills, sewage, chemicals.",
        "yr": "SS1"
      },
      {
        "q": "Flooding in Nigerian cities is worsened by:",
        "o": ["Too many trees absorbing water", "Deforestation, poor drainage, and building on floodplains", "Very dry weather", "Mountains blocking rainfall"],
        "a": 1,
        "e": "Urban flooding in Nigeria is worsened by deforestation (no roots to absorb water), poor/blocked drainage systems, building on floodplains, and increased impermeable surfaces (roads, rooftops) that prevent infiltration.",
        "h": "Urban flooding worsened by: deforestation, poor drainage, building on floodplains.",
        "yr": "SS1"
      },
      {
        "q": "What is the difference between the natural and human environment?",
        "o": ["The natural environment was created by humans; the human environment was created by nature", "The natural environment consists of physical and biological elements; the human environment consists of cultural, social, and economic elements", "They are the same thing", "The human environment includes mountains and rivers"],
        "a": 1,
        "e": "The natural environment consists of physical (landforms, climate, water) and biological (plants, animals) elements that exist without human creation. The human environment consists of culture, economy, settlements, and social systems.",
        "h": "Natural = physical + biological; Human = culture + economy + settlements.",
        "yr": "SS1"
      },
      {
        "q": "The concept of sustainable development means:",
        "o": ["Using all resources immediately for maximum profit", "Developing the economy while protecting the environment for future generations", "Stopping all economic development to protect nature", "Only developing cities, not rural areas"],
        "a": 1,
        "e": "Sustainable development means meeting the needs of the present generation without compromising the ability of future generations to meet their own needs — balancing economic growth with environmental protection.",
        "h": "Sustainable development = development now without compromising future generations.",
        "yr": "SS1"
      },
      {
        "q": "Why is the study of the environment important in geography?",
        "o": ["It only teaches students about forests", "It helps understand the relationships between humans and their surroundings for better planning and conservation", "Geography only studies maps, not the environment", "Environmental study is only for scientists"],
        "a": 1,
        "e": "Studying the environment in geography is essential for understanding human-environment relationships, identifying environmental problems, planning sustainable use of resources, and preparing students to be responsible environmental citizens.",
        "h": "Environment study: understand human-environment relationships and guide conservation.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Weather",
    "topicCode": "SS1-GEO-12",
    "module": "Physical Geography",
    "term": "Second Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Weather</span> refers to the atmospheric conditions of a place at a <span class=\"learn-keyword\">specific time</span>. It is what we experience day to day — sunshine, rain, wind, or clouds. Understanding weather is essential for agriculture, transport, aviation, and disaster preparedness.\n</div>\n\n<h3 class=\"learn-subheading\">1. Meaning of Weather</h3>\n<p class=\"learn-p\">Weather is the state of the atmosphere at a particular place and time, including temperature, humidity, rainfall, wind, cloud cover, and sunshine. It can change from hour to hour or day to day.</p>\n\n<h3 class=\"learn-subheading\">2. Elements of Weather</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Element</th><th>Definition</th><th>Instrument</th></tr></thead>\n    <tbody>\n      <tr><td>Temperature</td><td>Degree of hotness or coldness of the air</td><td>Thermometer</td></tr>\n      <tr><td>Rainfall/Precipitation</td><td>Water falling from clouds as rain, snow, or hail</td><td>Rain gauge</td></tr>\n      <tr><td>Humidity</td><td>Amount of water vapour in the air</td><td>Hygrometer/Psychrometer</td></tr>\n      <tr><td>Wind</td><td>Movement of air horizontally</td><td>Anemometer (speed); Wind vane (direction)</td></tr>\n      <tr><td>Cloud cover</td><td>Amount of sky covered by clouds</td><td>Estimated in oktas (0–8)</td></tr>\n      <tr><td>Sunshine</td><td>Amount of direct sunlight received</td><td>Sunshine recorder (Campbell-Stokes)</td></tr>\n      <tr><td>Atmospheric pressure</td><td>Weight of air pressing down on the Earth's surface</td><td>Barometer</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">3. Weather vs. Climate</h3>\n<p class=\"learn-p\"><strong>Weather</strong> = short-term atmospheric conditions (today's rain, this morning's temperature).</p>\n<p class=\"learn-p\"><strong>Climate</strong> = long-term average of weather over at least 30 years for a place.</p>\n\n<h3 class=\"learn-subheading\">4. Importance of Weather</h3>\n<ul class=\"learn-list\">\n  <li>Guides farmers on when to plant and harvest.</li>\n  <li>Affects transportation and aviation safety.</li>\n  <li>Influences human clothing and shelter.</li>\n  <li>Early warning of storms and floods saves lives.</li>\n</ul>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Weather = short-term (one day); Climate = long-term average (30 years). Key instruments: Thermometer (temperature), Rain gauge (rainfall), Anemometer (wind speed), Wind vane (wind direction), Barometer (pressure), Hygrometer (humidity), Campbell-Stokes recorder (sunshine).\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Weather = atmospheric conditions at a specific time and place. Elements: temperature, rainfall, humidity, wind, cloud cover, sunshine, pressure. Measured with specific instruments. Weather differs from climate (climate = 30-year average).\n</div>\n  ",
    "questions": [
      {
        "q": "Weather is defined as:",
        "o": ["The average atmospheric conditions over 30 years", "The state of the atmosphere at a particular place and time", "Only the rainfall experienced in one place", "The temperature of the ocean surface"],
        "a": 1,
        "e": "Weather is the state of the atmosphere at a specific place and time. It includes conditions like temperature, rainfall, humidity, wind, and cloud cover, and can change hourly or daily.",
        "h": "Weather = atmospheric conditions at a specific place and TIME.",
        "yr": "SS1"
      },
      {
        "q": "The instrument used to measure temperature is the:",
        "o": ["Barometer", "Rain gauge", "Thermometer", "Anemometer"],
        "a": 2,
        "e": "A thermometer measures the temperature of the air (or any substance). It works by the expansion and contraction of a liquid (mercury or alcohol) with temperature changes.",
        "h": "Thermometer = measures temperature.",
        "yr": "SS1"
      },
      {
        "q": "Which instrument measures the amount of rainfall?",
        "o": ["Anemometer", "Hygrometer", "Barometer", "Rain gauge"],
        "a": 3,
        "e": "A rain gauge is a cylindrical container that collects and measures the depth of rainfall over a given period. Rainfall is measured in millimetres (mm).",
        "h": "Rain gauge = measures rainfall (in mm).",
        "yr": "SS1"
      },
      {
        "q": "An anemometer is used to measure:",
        "o": ["Rainfall amount", "Wind direction", "Wind speed", "Atmospheric pressure"],
        "a": 2,
        "e": "An anemometer measures wind speed, usually in metres per second (m/s) or kilometres per hour (km/h). It consists of rotating cups that spin faster as wind speed increases.",
        "h": "Anemometer = measures wind SPEED.",
        "yr": "SS1"
      },
      {
        "q": "The instrument that measures the direction of wind is the:",
        "o": ["Anemometer", "Wind vane (weather vane)", "Barometer", "Hygrometer"],
        "a": 1,
        "e": "A wind vane (weather vane) shows the direction from which the wind is blowing. By convention, wind direction is named after where the wind comes from (e.g., a northerly wind blows from the north).",
        "h": "Wind vane = measures wind DIRECTION.",
        "yr": "SS1"
      },
      {
        "q": "Atmospheric pressure is measured using a:",
        "o": ["Thermometer", "Anemometer", "Rain gauge", "Barometer"],
        "a": 3,
        "e": "A barometer measures atmospheric (air) pressure — the weight of the air pressing down on the Earth's surface. Falling pressure often indicates approaching rain; rising pressure indicates fair weather.",
        "h": "Barometer = measures atmospheric pressure.",
        "yr": "SS1"
      },
      {
        "q": "Humidity refers to:",
        "o": ["The amount of sunshine in a day", "The amount of water vapour in the atmosphere", "The speed of wind at a place", "The depth of rainfall collected"],
        "a": 1,
        "e": "Humidity is the amount of water vapour (moisture) in the air. Relative humidity is expressed as a percentage of the maximum moisture the air can hold at a given temperature.",
        "h": "Humidity = amount of water vapour in the air.",
        "yr": "SS1"
      },
      {
        "q": "The instrument used to measure humidity (moisture in the air) is the:",
        "o": ["Barometer", "Anemometer", "Hygrometer or Psychrometer", "Campbell-Stokes recorder"],
        "a": 2,
        "e": "A hygrometer (or psychrometer — which uses wet and dry bulb thermometers) measures the relative humidity of the air. High humidity (near 100%) means the air is almost saturated with moisture.",
        "h": "Hygrometer/Psychrometer = measures humidity.",
        "yr": "SS1"
      },
      {
        "q": "The key difference between weather and climate is:",
        "o": ["Weather occurs only in tropical countries; climate is global", "Weather is short-term conditions at a specific time; climate is long-term average over 30+ years", "They are the same thing", "Weather is measured in cities; climate is measured in villages"],
        "a": 1,
        "e": "Weather describes short-term atmospheric conditions at a specific time (today it's raining). Climate is the average weather conditions over a long period — typically 30 years — for a place.",
        "h": "Weather = short-term (day); Climate = long-term average (30+ years).",
        "yr": "SS1"
      },
      {
        "q": "The Campbell-Stokes sunshine recorder is used to measure:",
        "o": ["Temperature", "Rainfall", "Duration of bright sunshine", "Cloud cover"],
        "a": 2,
        "e": "The Campbell-Stokes sunshine recorder uses a glass sphere to focus sunlight onto a recording card, burning a trace. The length of the burn marks shows the duration of bright sunshine in hours.",
        "h": "Campbell-Stokes recorder = measures duration of sunshine.",
        "yr": "SS1"
      },
      {
        "q": "Cloud cover is measured in units called:",
        "o": ["Millimetres", "Oktas (eighths of sky covered)", "Kilometres per hour", "Hectopascals"],
        "a": 1,
        "e": "Cloud cover is measured in oktas — eighths of the sky covered by cloud. 0 oktas = completely clear sky; 8 oktas = completely overcast. Observers estimate by dividing the sky into 8 equal portions.",
        "h": "Cloud cover measured in oktas (0 = clear, 8 = overcast).",
        "yr": "SS1"
      },
      {
        "q": "Why is knowledge of weather important for farmers?",
        "o": ["Weather knowledge helps farmers sell their produce at higher prices", "It guides planting and harvesting times, irrigation decisions, and protection of crops", "Farmers only need weather forecasts for market days", "Weather has no impact on agriculture"],
        "a": 1,
        "e": "Weather knowledge is vital for farmers: knowing when rains will begin determines planting time; knowing when dry spells will occur guides irrigation; weather forecasts help protect crops from storms or frosts.",
        "h": "Weather guides farmers: planting time, irrigation, crop protection.",
        "yr": "SS1"
      },
      {
        "q": "A sudden change in weather from sunshine to heavy rain within the same day is an example of:",
        "o": ["Climate change", "Climate variation", "Day-to-day weather variation", "Seasonal change"],
        "a": 2,
        "e": "Day-to-day (or even hourly) changes in sunshine, rain, and temperature are typical weather variations. This is the nature of weather — it is short-term and constantly changing.",
        "h": "Short-term changes in conditions = weather variation.",
        "yr": "SS1"
      },
      {
        "q": "Atmospheric pressure decreases with altitude because:",
        "o": ["Temperature increases at higher altitudes", "There are fewer air molecules pressing down from above at higher elevations", "Wind is stronger at higher altitudes", "Clouds block pressure at altitude"],
        "a": 1,
        "e": "At higher altitudes, there is less air above pressing down, so atmospheric pressure decreases. This also means the air is thinner (less oxygen) at high altitudes — why mountain climbers use oxygen masks.",
        "h": "Higher altitude → less air above → lower atmospheric pressure.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following weather conditions is associated with low atmospheric pressure?",
        "o": ["Clear, dry, sunny weather", "Cloudy, wet, and stormy weather", "Cold, dry, frosty conditions", "Hot, calm, sunny conditions"],
        "a": 1,
        "e": "Low atmospheric pressure (cyclone/depression) is associated with rising air, which cools, condenses, and produces clouds and rainfall. Stormy, cloudy, wet weather typically accompanies low pressure.",
        "h": "Low pressure → rising air → cooling → rain and storms.",
        "yr": "SS1"
      },
      {
        "q": "The dew point is the temperature at which:",
        "o": ["Water begins to evaporate", "Air becomes saturated and water vapour begins to condense", "Rain starts to fall from clouds", "Temperature drops below freezing"],
        "a": 1,
        "e": "The dew point is the temperature at which air becomes saturated with water vapour and condensation begins — water droplets form on surfaces (dew) or in the atmosphere (clouds, fog).",
        "h": "Dew point = temperature at which air becomes saturated → condensation.",
        "yr": "SS1"
      },
      {
        "q": "A weather station is placed in a Stevenson Screen (louvred box). This is because:",
        "o": ["The screen keeps instruments dry from rain only", "It protects instruments from rain and direct sunlight while allowing free air circulation for accurate readings", "It hides the instruments from public view", "The screen magnifies temperature readings"],
        "a": 1,
        "e": "A Stevenson Screen is a white louvred box that shields weather instruments from direct sun and rain while allowing free air circulation. This ensures accurate temperature and humidity readings.",
        "h": "Stevenson Screen: protects instruments while allowing air circulation for accuracy.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is NOT an element of weather?",
        "o": ["Temperature", "Rainfall", "Rock type", "Wind speed"],
        "a": 2,
        "e": "Rock type is a geological feature, not an element of weather. Weather elements include: temperature, rainfall, humidity, wind speed and direction, cloud cover, sunshine, and atmospheric pressure.",
        "h": "Rock type is geological, NOT a weather element.",
        "yr": "SS1"
      },
      {
        "q": "Precipitation includes all EXCEPT:",
        "o": ["Rain", "Snow", "Hail", "Dew"],
        "a": 3,
        "e": "Precipitation includes all forms of water falling from the atmosphere to the Earth's surface: rain, snow, sleet, hail, and drizzle. Dew is water that condenses on surfaces (not falling from clouds) and is not classified as precipitation.",
        "h": "Precipitation = rain, snow, hail, sleet. Dew = condensation on surfaces (not precipitation).",
        "yr": "SS1"
      },
      {
        "q": "Which of the following statements correctly describes a weather forecast?",
        "o": ["It tells us the average temperature over the last 30 years", "It predicts atmospheric conditions at a specific place for the near future", "It is the same as a climate record", "It measures past rainfall only"],
        "a": 1,
        "e": "A weather forecast predicts what atmospheric conditions (temperature, rainfall, wind, clouds) will be at a specific place in the near future — hours to days ahead. It is used by farmers, pilots, mariners, and disaster agencies.",
        "h": "Weather forecast = prediction of future atmospheric conditions.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Climate I",
    "topicCode": "SS1-GEO-13",
    "module": "Physical Geography",
    "term": "Second Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Climate</span> is the average weather conditions of a place over a long period, typically <span class=\"learn-keyword\">30 years or more</span>. While weather changes daily, climate describes the typical atmospheric conditions of a region. Climate influences vegetation, agriculture, settlement, and all aspects of human life.\n</div>\n\n<h3 class=\"learn-subheading\">1. Meaning and Difference from Weather</h3>\n<p class=\"learn-p\"><strong>Climate</strong> = long-term average of weather (temperature, rainfall, etc.) over 30+ years.<br><strong>Weather</strong> = short-term conditions at a specific time.</p>\n<p class=\"learn-p\">\"Climate is what you expect; weather is what you get.\"</p>\n\n<h3 class=\"learn-subheading\">2. Elements of Climate</h3>\n<p class=\"learn-p\">The elements of climate are the same as weather — temperature, rainfall, humidity, wind, cloud cover, sunshine, and pressure — but averaged over many years.</p>\n\n<h3 class=\"learn-subheading\">3. Factors Influencing Climate</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Factor</th><th>Effect on Climate</th></tr></thead>\n    <tbody>\n      <tr><td>Latitude</td><td>Areas near the Equator are hotter; temperature decreases toward poles</td></tr>\n      <tr><td>Altitude</td><td>Temperature decreases with altitude (~6.5°C per 1,000 m)</td></tr>\n      <tr><td>Distance from the sea (continentality)</td><td>Coastal areas are moderate; inland areas have extreme temperatures</td></tr>\n      <tr><td>Ocean currents</td><td>Warm currents raise temperature; cold currents lower temperature</td></tr>\n      <tr><td>Prevailing winds</td><td>Onshore winds bring moisture; offshore winds are dry</td></tr>\n      <tr><td>Relief (mountains)</td><td>Windward slopes receive rain; leeward slopes are dry (rain shadow)</td></tr>\n      <tr><td>Natural vegetation</td><td>Forests moderate climate; deforestation causes extreme conditions</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">4. Major Climate Zones</h3>\n<ul class=\"learn-list\">\n  <li><strong>Equatorial:</strong> Hot and wet all year; near 0°N/S. Example: Congo Basin.</li>\n  <li><strong>Tropical:</strong> Hot with distinct wet and dry seasons. Example: most of Nigeria.</li>\n  <li><strong>Sahel/Semi-arid:</strong> Hot and dry with very little rainfall. Example: northern Nigeria.</li>\n  <li><strong>Desert:</strong> Very hot and very dry. Example: Sahara Desert.</li>\n  <li><strong>Temperate:</strong> Moderate temperatures; found in mid-latitudes. Example: Europe.</li>\n</ul>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Climate = 30-year average; Weather = short-term. Seven factors affecting climate: latitude, altitude, distance from sea, ocean currents, prevailing winds, relief, vegetation. Latitude is the most important factor — it determines how directly the Sun's rays hit the Earth.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Climate = long-term (30 years) average weather. Same elements as weather. Factors: latitude (most important), altitude, distance from sea, ocean currents, prevailing winds, relief, vegetation. Climate zones: equatorial, tropical, Sahel, desert, temperate.\n</div>\n  ",
    "questions": [
      {
        "q": "Climate is best defined as:",
        "o": ["The atmospheric conditions on a particular day", "The long-term average of weather conditions over 30 or more years", "The temperature of the ocean surface", "The amount of rainfall in one season"],
        "a": 1,
        "e": "Climate is the average of weather conditions (temperature, rainfall, humidity, wind, etc.) over a long period — conventionally 30 years or more. It describes the typical, expected atmospheric conditions of a place.",
        "h": "Climate = 30-year average of weather conditions.",
        "yr": "SS1"
      },
      {
        "q": "The most important factor influencing climate is:",
        "o": ["Ocean currents", "Altitude", "Latitude", "Distance from the sea"],
        "a": 2,
        "e": "Latitude is the most important factor influencing climate because it determines the angle at which the Sun's rays strike the Earth. Near the Equator, rays are more direct (concentrated) and temperatures are higher.",
        "h": "Latitude = most important climate factor (determines Sun's ray angle).",
        "yr": "SS1"
      },
      {
        "q": "Why do areas near the Equator experience higher temperatures than polar regions?",
        "o": ["Equatorial areas are closer to the Sun's surface", "The Sun's rays strike the Equator more directly (concentrated) and travel less through the atmosphere", "There is more vegetation at the Equator absorbing heat", "Polar areas have more cloud cover blocking heat"],
        "a": 1,
        "e": "Near the Equator, the Sun's rays strike the Earth's surface at a more direct (perpendicular) angle, concentrating energy over a smaller area. At the poles, rays arrive at a low angle, spreading energy over a larger area.",
        "h": "Equator: direct (concentrated) Sun's rays = hotter. Poles: oblique rays = cooler.",
        "yr": "SS1"
      },
      {
        "q": "Temperature decreases with altitude at a rate of approximately:",
        "o": ["1°C per 100 m", "6.5°C per 1,000 m", "10°C per 1,000 m", "0.5°C per 1,000 m"],
        "a": 1,
        "e": "Temperature decreases with altitude at an average rate of about 6.5°C per 1,000 m (the environmental lapse rate). This is why mountains are cooler than surrounding lowlands, and why the Jos Plateau is cooler than coastal Nigeria.",
        "h": "Temperature lapse rate = 6.5°C per 1,000 m altitude increase.",
        "yr": "SS1"
      },
      {
        "q": "Coastal areas have more moderate (less extreme) temperatures than inland areas because:",
        "o": ["Coasts receive more direct sunlight", "The sea has a moderating effect — warming in winter and cooling in summer", "Coastal winds bring cold air from the poles", "Coastal areas have more vegetation"],
        "a": 1,
        "e": "The sea moderates coastal temperatures because water heats and cools more slowly than land. In summer, sea breezes cool coasts; in winter, the sea retains heat, keeping coasts warmer than inland areas.",
        "h": "Sea moderates coastal temperatures (less extreme than inland).",
        "yr": "SS1"
      },
      {
        "q": "Ocean currents affect climate because:",
        "o": ["They change the rotation of the Earth", "Warm currents raise temperatures in adjacent coastal areas; cold currents lower temperatures", "They increase rainfall everywhere they flow past", "They only affect marine life, not climate"],
        "a": 1,
        "e": "Warm ocean currents (e.g., North Atlantic Drift) raise temperatures and increase moisture in adjacent coastal areas. Cold currents (e.g., Benguela Current) lower temperatures and can cause dry conditions near coasts.",
        "h": "Warm current = raises temperature; Cold current = lowers temperature.",
        "yr": "SS1"
      },
      {
        "q": "The equatorial climate is characterised by:",
        "o": ["Cold temperatures and snow throughout the year", "Hot temperatures and heavy rainfall throughout the year", "Hot summers and very cold winters", "Very dry conditions with occasional rainfall"],
        "a": 1,
        "e": "The equatorial climate is hot (average temperature about 26–28°C) and wet throughout the year, with annual rainfall exceeding 2,000 mm. The Congo Basin is a prime example.",
        "h": "Equatorial climate = hot and wet all year, >2,000 mm rainfall.",
        "yr": "SS1"
      },
      {
        "q": "The tropical climate of most of Nigeria is characterised by:",
        "o": ["Cold temperatures and snow in winter", "Hot temperatures with distinct wet and dry seasons", "Rainfall throughout the entire year", "Desert conditions with no rainfall"],
        "a": 1,
        "e": "Most of Nigeria has a tropical climate — hot temperatures throughout the year with distinct wet and dry seasons. The length of the rainy season decreases from south (coastal) to north (Sahel).",
        "h": "Nigeria's tropical climate: hot with distinct wet and dry seasons.",
        "yr": "SS1"
      },
      {
        "q": "Prevailing onshore winds (blowing from sea to land) tend to bring:",
        "o": ["Dry conditions to coastal areas", "Moisture and rainfall to coastal areas", "Cold temperatures to all areas", "Desertification to coastal zones"],
        "a": 1,
        "e": "Onshore winds blow from sea to land, carrying moisture evaporated from the ocean. When this moist air reaches land, it may rise, cool, and produce rainfall — making windward coasts wet.",
        "h": "Onshore wind (sea→land) = carries moisture → rainfall on coast.",
        "yr": "SS1"
      },
      {
        "q": "The Sahel climate in northern Nigeria is characterised by:",
        "o": ["Heavy rainfall and dense forest", "Hot temperatures with very limited, unreliable rainfall", "Cold winters and mild summers", "Permanent snow and ice"],
        "a": 1,
        "e": "The Sahel is a semi-arid zone with hot temperatures and very limited rainfall (250–500 mm per year), concentrated in a short wet season. It borders the Sahara Desert to the north.",
        "h": "Sahel climate = hot + very little, unreliable rainfall (semi-arid).",
        "yr": "SS1"
      },
      {
        "q": "Natural vegetation affects climate because:",
        "o": ["Forests create deserts", "Forests regulate temperature, increase humidity, and promote rainfall through transpiration", "Vegetation has no effect on climate", "Grassland produces more rainfall than forests"],
        "a": 1,
        "e": "Forests play a major role in moderating climate: they reduce temperature extremes, release water vapour through transpiration (promoting rainfall), absorb CO₂, and stabilise local weather patterns.",
        "h": "Forests moderate climate: regulate temperature, increase humidity, promote rain.",
        "yr": "SS1"
      },
      {
        "q": "The Jos Plateau in Nigeria is cooler than surrounding lowlands because of:",
        "o": ["Its latitude (it is far from the Equator)", "Its higher altitude (elevation lowers temperature)", "The presence of cold ocean currents nearby", "It has more dense forest"],
        "a": 1,
        "e": "The Jos Plateau is significantly cooler than surrounding lowlands because of its high altitude (about 1,200–1,300 m). Temperature decreases with altitude at ~6.5°C per 1,000 m.",
        "h": "Jos Plateau cool = higher altitude → lower temperature.",
        "yr": "SS1"
      },
      {
        "q": "Which factor explains why the Sahara Desert in North Africa receives very little rainfall?",
        "o": ["High altitude preventing cloud formation", "Subtropical high pressure zones causing dry descending air and offshore winds", "The Sahara is too close to the South Pole", "The absence of mountains to cause orographic rainfall"],
        "a": 1,
        "e": "The Sahara lies under persistent subtropical high pressure zones where air descends, warms, and dries out. Prevailing winds blow offshore (away from the sea), bringing no moisture.",
        "h": "Sahara dry: subtropical high pressure → descending dry air + offshore winds.",
        "yr": "SS1"
      },
      {
        "q": "The temperate climate is found mainly in:",
        "o": ["Equatorial regions (0–10°N/S)", "Tropical regions (10–25°N/S)", "Mid-latitudes (40–65°N/S)", "Polar regions (above 66.5°N/S)"],
        "a": 2,
        "e": "Temperate climates are found in mid-latitudes (approximately 40–65°N/S), including Western Europe, parts of North America, and southern South America. They have moderate temperatures and seasonal variation.",
        "h": "Temperate climate = mid-latitudes (40–65°N/S); moderate temperatures.",
        "yr": "SS1"
      },
      {
        "q": "How does deforestation affect climate?",
        "o": ["Deforestation increases rainfall by exposing more land to rain", "Deforestation increases temperature and reduces rainfall by removing trees that moderate climate", "Deforestation has no effect on climate", "Deforestation decreases temperatures"],
        "a": 1,
        "e": "Deforestation removes trees that moderate temperature (provide shade, release water vapour). Without trees, surface temperatures increase, rainfall decreases, and the local climate becomes more extreme.",
        "h": "Deforestation → higher temperatures, reduced rainfall, more extreme climate.",
        "yr": "SS1"
      },
      {
        "q": "The phrase 'Climate is what you expect; weather is what you get' means:",
        "o": ["Climate and weather are the same thing", "Climate predicts long-term averages; weather is the actual day-to-day conditions experienced", "Weather is more reliable than climate", "Climate only applies to tropical countries"],
        "a": 1,
        "e": "The phrase summarises the key distinction: climate tells you what atmospheric conditions to generally expect based on long-term averages, while weather is the actual conditions you experience on any particular day.",
        "h": "Climate = expected (long-term); Weather = actual (day-to-day).",
        "yr": "SS1"
      },
      {
        "q": "Which of the following climate zones has the highest annual rainfall?",
        "o": ["Desert climate", "Sahel/semi-arid climate", "Equatorial climate", "Temperate climate"],
        "a": 2,
        "e": "The equatorial climate receives the highest annual rainfall — typically more than 2,000 mm per year, distributed throughout the year with no dry season. The Congo Basin and Amazon Basin are examples.",
        "h": "Equatorial climate = highest rainfall (>2,000 mm, all year).",
        "yr": "SS1"
      },
      {
        "q": "A place located 3,000 metres above sea level would have a temperature approximately how much cooler than a place at sea level at the same latitude?",
        "o": ["3°C cooler", "6.5°C cooler", "19.5°C cooler", "30°C cooler"],
        "a": 2,
        "e": "Temperature decreases at 6.5°C per 1,000 m. At 3,000 m: 3 × 6.5 = 19.5°C cooler than at sea level. This explains why high mountains have snow and cold temperatures even in the tropics.",
        "h": "3,000 m × 6.5°C/1,000 m = 19.5°C cooler.",
        "yr": "SS1"
      },
      {
        "q": "The North Atlantic Drift (warm ocean current) explains why western Europe is:",
        "o": ["Colder than expected for its latitude", "Warmer and wetter than expected for its latitude", "More arid than North Africa", "Equatorial in climate despite its latitude"],
        "a": 1,
        "e": "The North Atlantic Drift is a warm ocean current that flows northeastward across the Atlantic. It warms the western coast of Europe, making it significantly warmer and wetter than locations at similar latitudes in other parts of the world.",
        "h": "North Atlantic Drift: warm current → warmer, wetter western Europe.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is a direct effect of latitude on climate?",
        "o": ["Higher altitude leads to lower temperatures", "Places at higher latitudes receive less direct solar radiation and are cooler", "Coastal areas are wetter than inland areas", "Warm ocean currents raise temperatures in adjacent areas"],
        "a": 1,
        "e": "Latitude directly controls the angle of the Sun's rays — at higher latitudes, rays arrive at a lower angle, spreading energy over a larger area and providing less warmth. This is why the poles are cold.",
        "h": "Higher latitude → lower angle of Sun's rays → less heating → cooler.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Nigeria: Location and Position",
    "topicCode": "SS1-GEO-14",
    "module": "Regional Geography",
    "term": "Second Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  Nigeria is located in <span class=\"learn-keyword\">West Africa</span>, on the Gulf of Guinea. Its geographical position — straddling the tropics and having a long coastline — gives it a diverse climate, rich resources, and significant geopolitical importance.\n</div>\n\n<h3 class=\"learn-subheading\">1. Geographical Location</h3>\n<ul class=\"learn-list\">\n  <li><strong>Continent:</strong> Africa (West Africa).</li>\n  <li><strong>Latitudinal extent:</strong> 4°N to 14°N.</li>\n  <li><strong>Longitudinal extent:</strong> 3°E to 15°E.</li>\n  <li><strong>Area:</strong> Approximately 923,768 km².</li>\n  <li>Nigeria straddles the tropics — between the Equator and the Tropic of Cancer.</li>\n</ul>\n\n<h3 class=\"learn-subheading\">2. Boundaries and Neighbours</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Direction</th><th>Boundary/Neighbour</th></tr></thead>\n    <tbody>\n      <tr><td>North</td><td>Niger Republic and Chad Republic</td></tr>\n      <tr><td>East</td><td>Cameroon</td></tr>\n      <tr><td>West</td><td>Republic of Benin</td></tr>\n      <tr><td>South</td><td>Atlantic Ocean (Gulf of Guinea)</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">3. Significance of Nigeria's Location</h3>\n<ul class=\"learn-list\">\n  <li>Strategic coastal location facilitates international trade through ports.</li>\n  <li>Location in the tropics provides warm climate suitable for diverse agriculture.</li>\n  <li>Bordering four countries promotes regional trade and diplomacy.</li>\n  <li>Coastal location in the Gulf of Guinea provides access to offshore oil resources.</li>\n  <li>Nigeria's size and location makes it a regional power in West Africa.</li>\n</ul>\n\n<h3 class=\"learn-subheading\">4. Nigeria's Position in Africa and the World</h3>\n<ul class=\"learn-list\">\n  <li>Nigeria is Africa's most populous country (over 200 million people).</li>\n  <li>Nigeria is Africa's largest economy (by GDP).</li>\n  <li>Nigeria is a founding member of ECOWAS (Economic Community of West African States).</li>\n  <li>The Prime Meridian (0° longitude) passes through part of Nigeria at about 3–4°E, making Nigeria's time zone GMT+1.</li>\n</ul>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Nigeria: 4°N–14°N latitude; 3°E–15°E longitude. Neighbours: Niger/Chad (N), Cameroon (E), Benin Republic (W), Atlantic Ocean (S). Area = ~923,768 km². Nigeria is in GMT+1 time zone. Most populous country in Africa.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Nigeria in West Africa: 4°N–14°N, 3°E–15°E, ~923,768 km². Bordered by Niger and Chad (N), Cameroon (E), Benin Republic (W), Atlantic Ocean (S). Africa's most populous country and largest economy. GMT+1.\n</div>\n  ",
    "questions": [
      {
        "q": "Nigeria is located in which part of Africa?",
        "o": ["North Africa", "East Africa", "West Africa", "Central Africa"],
        "a": 2,
        "e": "Nigeria is located in West Africa, on the coast of the Gulf of Guinea (Atlantic Ocean). It is the largest country in West Africa by area and the most populous in Africa.",
        "h": "Nigeria is in West Africa.",
        "yr": "SS1"
      },
      {
        "q": "The latitudinal extent of Nigeria is approximately:",
        "o": ["0°N to 4°N", "4°N to 14°N", "14°N to 24°N", "4°S to 14°N"],
        "a": 1,
        "e": "Nigeria extends from approximately 4°N (in the south, near the coastline) to 14°N (in the north, near the boundary with Niger Republic).",
        "h": "Nigeria: 4°N to 14°N latitude.",
        "yr": "SS1"
      },
      {
        "q": "The longitudinal extent of Nigeria is approximately:",
        "o": ["0°E to 10°E", "3°E to 15°E", "10°E to 20°E", "0°W to 10°W"],
        "a": 1,
        "e": "Nigeria extends from approximately 3°E (in the west, near the Benin Republic border) to 15°E (in the east, near the Cameroon border).",
        "h": "Nigeria: 3°E to 15°E longitude.",
        "yr": "SS1"
      },
      {
        "q": "Nigeria is bordered to the south by:",
        "o": ["Niger Republic", "Cameroon", "Republic of Benin", "The Atlantic Ocean (Gulf of Guinea)"],
        "a": 3,
        "e": "Nigeria's southern border is the Atlantic Ocean — specifically the Gulf of Guinea. Nigeria has a coastline of about 853 km along the Gulf of Guinea, providing access to the sea for trade.",
        "h": "Nigeria's southern border = Atlantic Ocean (Gulf of Guinea).",
        "yr": "SS1"
      },
      {
        "q": "Which country borders Nigeria to the west?",
        "o": ["Ghana", "Togo", "Republic of Benin (Benin Republic)", "Cameroon"],
        "a": 2,
        "e": "The Republic of Benin (formerly Dahomey) borders Nigeria to the west. The border between Nigeria and Benin Republic runs roughly north-south.",
        "h": "Nigeria's western neighbour = Republic of Benin.",
        "yr": "SS1"
      },
      {
        "q": "Nigeria is bordered to the east by:",
        "o": ["Chad", "Niger Republic", "Cameroon", "Central African Republic"],
        "a": 2,
        "e": "Cameroon borders Nigeria to the east. The boundary follows partly along the Mandara Mountains and rivers before reaching the coast of the Bight of Biafra.",
        "h": "Nigeria's eastern neighbour = Cameroon.",
        "yr": "SS1"
      },
      {
        "q": "The two countries that border Nigeria to the north are:",
        "o": ["Cameroon and Chad", "Niger Republic and Chad Republic", "Benin Republic and Niger Republic", "Mali and Niger Republic"],
        "a": 1,
        "e": "Nigeria is bordered to the north by Niger Republic (to the north and northwest) and Chad Republic (to the northeast). The Lake Chad Basin area is shared among Nigeria, Niger, Chad, and Cameroon.",
        "h": "Nigeria's northern neighbours = Niger Republic and Chad Republic.",
        "yr": "SS1"
      },
      {
        "q": "The approximate area of Nigeria is:",
        "o": ["92,377 km²", "923,768 km²", "9,237,680 km²", "92,377,000 km²"],
        "a": 1,
        "e": "Nigeria covers approximately 923,768 km², making it the 14th largest country in Africa and about 1.5 times the size of Texas, USA.",
        "h": "Nigeria's area ≈ 923,768 km².",
        "yr": "SS1"
      },
      {
        "q": "Nigeria's time zone is GMT+1 because:",
        "o": ["Nigeria is south of the Equator", "Nigeria's central longitude is about 7°E (east of Greenwich), which is one time zone ahead", "Nigeria has 36 states", "Nigeria adopted GMT+1 for economic reasons only"],
        "a": 1,
        "e": "Nigeria's central longitude is approximately 7–8°E. Since each 15° = 1 hour ahead, and Nigeria spans about 3°E to 15°E, its standard time zone is GMT+1 (West Africa Time — WAT).",
        "h": "Nigeria at ~7°E longitude = GMT+1 (West Africa Time).",
        "yr": "SS1"
      },
      {
        "q": "Nigeria being located in the tropics (between 4°N and 14°N) means it has:",
        "o": ["Cold temperatures year-round", "A warm climate suitable for diverse tropical agriculture", "Snow and frost in winter", "No dry season anywhere in the country"],
        "a": 1,
        "e": "Being between the Equator and the Tropic of Cancer, Nigeria has a tropical climate — generally warm temperatures throughout the year — supporting diverse agriculture including palm oil, cocoa, groundnuts, and cotton.",
        "h": "Tropical location (4°N–14°N) → warm climate → diverse agriculture.",
        "yr": "SS1"
      },
      {
        "q": "Nigeria is described as Africa's most populous country because:",
        "o": ["It has the largest land area in Africa", "Its population exceeds that of any other African country (over 200 million)", "It has the highest birth rate in the world", "Its climate attracts people from other continents"],
        "a": 1,
        "e": "Nigeria has over 200 million people, making it the most populous country in Africa and one of the most populous in the world. One in every six Africans is Nigerian.",
        "h": "Nigeria = Africa's most populous (over 200 million people).",
        "yr": "SS1"
      },
      {
        "q": "ECOWAS (Economic Community of West African States) is significant to Nigeria because:",
        "o": ["Nigeria is not a member of ECOWAS", "Nigeria is a founding member and uses ECOWAS for regional economic and political cooperation", "ECOWAS is a military organisation only", "ECOWAS controls Nigeria's oil production"],
        "a": 1,
        "e": "Nigeria is a founding member of ECOWAS (established 1975), which promotes economic integration, free movement of people and goods, and political stability among 15 West African nations. Nigeria is the dominant economic power in ECOWAS.",
        "h": "Nigeria = founding member of ECOWAS (regional economic cooperation).",
        "yr": "SS1"
      },
      {
        "q": "The Gulf of Guinea is part of which ocean?",
        "o": ["Indian Ocean", "Pacific Ocean", "Atlantic Ocean", "Arctic Ocean"],
        "a": 2,
        "e": "The Gulf of Guinea is a large bay on the Atlantic Ocean along the western and central African coast. Nigeria, Ghana, Côte d'Ivoire, Cameroon, and several other countries border the Gulf of Guinea.",
        "h": "Gulf of Guinea = part of the Atlantic Ocean.",
        "yr": "SS1"
      },
      {
        "q": "Nigeria's strategic coastal location in the Gulf of Guinea is economically important because:",
        "o": ["It allows Nigeria to grow coastal crops only", "It provides access to offshore oil, international shipping routes, and port-based trade", "The coast prevents invasion from other African countries", "Coastal location guarantees the highest rainfall in Nigeria"],
        "a": 1,
        "e": "Nigeria's coastline provides access to offshore petroleum resources in the Gulf of Guinea, international shipping lanes for import/export trade, and sites for major ports (Lagos/Apapa, Port Harcourt, Warri).",
        "h": "Coastal location: offshore oil, shipping lanes, ports for trade.",
        "yr": "SS1"
      },
      {
        "q": "Nigeria's capital city, Abuja, is located approximately at:",
        "o": ["4°N, 3°E", "6°N, 3°E (Lagos)", "9°N, 7°E (Abuja FCT)", "14°N, 15°E"],
        "a": 2,
        "e": "Abuja (the Federal Capital Territory) is located approximately at 9°N latitude and 7°E longitude — in the geographic centre of Nigeria. It was purpose-built as a capital to be more central and politically neutral than Lagos.",
        "h": "Abuja ≈ 9°N, 7°E (central Nigeria).",
        "yr": "SS1"
      },
      {
        "q": "Which of Nigeria's neighbouring countries also borders the Atlantic Ocean?",
        "o": ["Niger Republic", "Chad Republic", "Republic of Benin", "None — Nigeria is the only coastal country"],
        "a": 2,
        "e": "The Republic of Benin (Nigeria's western neighbour) also has a coastline on the Atlantic Ocean (Gulf of Guinea). Niger and Chad are landlocked countries that do not border the Atlantic.",
        "h": "Benin Republic (Nigeria's western neighbour) also has an Atlantic coastline.",
        "yr": "SS1"
      },
      {
        "q": "The city of Lagos is located in southwestern Nigeria at approximately which latitude?",
        "o": ["4°N", "6°N", "10°N", "14°N"],
        "a": 1,
        "e": "Lagos is located at approximately 6°N latitude, along Nigeria's southwest coast on the Atlantic Ocean. It is Nigeria's most populous city and main commercial port.",
        "h": "Lagos ≈ 6°N (southwestern Nigeria, Atlantic coast).",
        "yr": "SS1"
      },
      {
        "q": "The fact that Nigeria borders four different countries has which advantage?",
        "o": ["It reduces Nigeria's land area", "It increases border conflicts with all neighbours", "It promotes regional trade, diplomatic relations, and economic cooperation", "It means Nigeria has no strategic importance"],
        "a": 2,
        "e": "Sharing borders with four countries (Niger, Chad, Cameroon, Benin Republic) creates opportunities for bilateral trade, diplomatic partnerships, and regional cooperation through organisations like ECOWAS.",
        "h": "Multiple borders = regional trade, diplomatic relations, cooperation.",
        "yr": "SS1"
      },
      {
        "q": "Nigeria is Africa's largest economy because:",
        "o": ["It has the largest land area in Africa", "It has the highest GDP in Africa, driven by oil, services, and manufacturing", "It exports the most food in Africa", "It has the most universities in Africa"],
        "a": 1,
        "e": "Nigeria has Africa's largest GDP (Gross Domestic Product), driven primarily by petroleum exports, a large service sector, manufacturing, telecommunications, and agriculture.",
        "h": "Nigeria = Africa's largest economy (largest GDP).",
        "yr": "SS1"
      },
      {
        "q": "Which ocean coastline does Nigeria face to the south?",
        "o": ["Indian Ocean", "Mediterranean Sea", "Red Sea", "Atlantic Ocean (Gulf of Guinea)"],
        "a": 3,
        "e": "Nigeria's southern coastline faces the Atlantic Ocean — specifically the Gulf of Guinea. This coast extends from the border with Benin Republic in the west to the Cameroon border in the east.",
        "h": "Nigeria faces the Atlantic Ocean (Gulf of Guinea) to the south.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Physical Setting of Nigeria",
    "topicCode": "SS1-GEO-15",
    "module": "Regional Geography",
    "term": "Second Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  Nigeria's <span class=\"learn-keyword\">physical setting</span> encompasses its relief (landforms), drainage (rivers and lakes), and vegetation zones. These physical features have shaped human settlement patterns, agricultural activities, and economic development across the country.\n</div>\n\n<h3 class=\"learn-subheading\">1. Relief of Nigeria</h3>\n<p class=\"learn-p\">Nigeria's relief can be divided into the following regions:</p>\n<ul class=\"learn-list\">\n  <li><strong>Coastal Lowlands (South):</strong> Low-lying, swampy, mangrove-covered; includes Niger Delta.</li>\n  <li><strong>Western Lowlands:</strong> Low plateau with scattered hills; forested in places.</li>\n  <li><strong>Eastern Highlands:</strong> Includes Obudu Plateau, Shebshi Mountains; highest point in Nigeria — Chappal Waddi (2,419 m) in Taraba State.</li>\n  <li><strong>Central Plateau (Jos Plateau):</strong> Elevation 1,200–1,300 m; cooler climate; tin and columbite mining.</li>\n  <li><strong>Northern Plains:</strong> Flat, low-lying; Sudan Savanna and Sahel vegetation.</li>\n  <li><strong>Niger-Benue Trough:</strong> Broad river valleys of the Niger and Benue rivers.</li>\n</ul>\n\n<h3 class=\"learn-subheading\">2. Drainage of Nigeria</h3>\n<p class=\"learn-p\">Nigeria's major rivers include:</p>\n<ul class=\"learn-list\">\n  <li><strong>Niger River:</strong> Enters from Guinea/Mali; flows SE through Nigeria before emptying into the Gulf of Guinea via the Niger Delta.</li>\n  <li><strong>Benue River:</strong> Rises in Cameroon; the major tributary of the Niger; joins the Niger at Lokoja.</li>\n  <li><strong>Cross River:</strong> Drains the southeastern states into the Bight of Biafra.</li>\n  <li><strong>Kaduna, Sokoto, Gongola Rivers:</strong> Other significant rivers in northern and central Nigeria.</li>\n  <li><strong>Lake Chad:</strong> A shrinking inland lake in northeast Nigeria, shared with Niger, Chad, and Cameroon.</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. Vegetation Zones of Nigeria</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Zone</th><th>Location</th><th>Description</th></tr></thead>\n    <tbody>\n      <tr><td>Mangrove Forest</td><td>Coastal south</td><td>Salt-tolerant trees; Niger Delta swamps</td></tr>\n      <tr><td>Tropical Rainforest</td><td>Southwest and Southeast</td><td>Dense, multilayered evergreen forest</td></tr>\n      <tr><td>Guinea Savanna</td><td>Central Nigeria</td><td>Tall grass with trees; most extensive zone</td></tr>\n      <tr><td>Sudan Savanna</td><td>North-central</td><td>Shorter grass, more open woodland</td></tr>\n      <tr><td>Sahel Savanna</td><td>Far north</td><td>Sparse grass and drought-resistant shrubs; near desert</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Nigeria's highest point = Chappal Waddi (2,419 m, Taraba State). Niger and Benue join at Lokoja. Vegetation zones from south to north: Mangrove → Rainforest → Guinea Savanna → Sudan Savanna → Sahel Savanna. Jos Plateau = tin and columbite mining, cooler climate.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Relief: coastal lowlands, central Jos Plateau, eastern highlands (Chappal Waddi 2,419 m), northern plains. Major rivers: Niger, Benue (join at Lokoja), Cross River. Vegetation S→N: Mangrove → Rainforest → Guinea Savanna → Sudan Savanna → Sahel.\n</div>\n  ",
    "questions": [
      {
        "q": "The highest point in Nigeria is:",
        "o": ["Jos Plateau (1,300 m)", "Obudu Plateau (1,576 m)", "Chappal Waddi (2,419 m)", "Adamawa Peak (2,000 m)"],
        "a": 2,
        "e": "Chappal Waddi, located in the Gotel Mountains of Taraba State (on the Nigeria-Cameroon border), is Nigeria's highest point at 2,419 m above sea level.",
        "h": "Nigeria's highest point = Chappal Waddi, 2,419 m, Taraba State.",
        "yr": "SS1"
      },
      {
        "q": "The Niger and Benue rivers join (confluence) at:",
        "o": ["Lagos", "Abuja", "Lokoja", "Port Harcourt"],
        "a": 2,
        "e": "The Niger and Benue rivers meet at Lokoja in Kogi State — forming the famous confluence. Below Lokoja, the Niger River flows southward toward the Niger Delta.",
        "h": "Niger + Benue confluence = Lokoja, Kogi State.",
        "yr": "SS1"
      },
      {
        "q": "The vegetation zone in the coastal southern Nigeria, especially the Niger Delta, is:",
        "o": ["Tropical rainforest", "Guinea savanna", "Mangrove forest", "Sudan savanna"],
        "a": 2,
        "e": "Mangrove forests are the dominant vegetation in Nigeria's coastal areas and the Niger Delta. Mangroves are salt-tolerant trees that grow in waterlogged coastal and brackish water conditions.",
        "h": "Niger Delta coast = mangrove forest (salt-tolerant, waterlogged).",
        "yr": "SS1"
      },
      {
        "q": "The Jos Plateau in central Nigeria is known for:",
        "o": ["Oil palm production and fishing", "Tin and columbite mining and a cooler climate", "Petroleum extraction and refining", "Cotton and groundnut farming only"],
        "a": 1,
        "e": "The Jos Plateau is known for its tin and columbite mining (the plateau is rich in these minerals), and its cool climate (elevation ~1,200–1,300 m makes it significantly cooler than surrounding lowlands).",
        "h": "Jos Plateau: tin + columbite mining; cool climate.",
        "yr": "SS1"
      },
      {
        "q": "The Benue River rises (originates) in:",
        "o": ["Guinea", "Mali", "Nigeria (Jos Plateau)", "Cameroon"],
        "a": 3,
        "e": "The Benue River (Nigeria's largest tributary of the Niger) rises in the Adamawa Plateau of Cameroon before entering Nigeria and flowing westward to join the Niger at Lokoja.",
        "h": "Benue River originates in Cameroon.",
        "yr": "SS1"
      },
      {
        "q": "The Guinea Savanna vegetation zone is significant because it is:",
        "o": ["The smallest vegetation zone in Nigeria", "The most extensive vegetation zone, covering much of central Nigeria", "Limited to the far north near the Sahara", "Found only in Lagos and Ogun states"],
        "a": 1,
        "e": "The Guinea Savanna is the most extensive vegetation zone in Nigeria, covering a wide belt across the centre of the country. It features tall grasses interspersed with trees and supports diverse agricultural activities.",
        "h": "Guinea Savanna = Nigeria's most extensive vegetation zone.",
        "yr": "SS1"
      },
      {
        "q": "The tropical rainforest in Nigeria is found mainly in:",
        "o": ["The far north (Sokoto, Kano)", "The central Jos Plateau", "The Southwest (Ogun, Ondo, Edo) and Southeast (Cross River, Akwa Ibom)", "The Niger-Benue trough only"],
        "a": 2,
        "e": "Tropical rainforest occurs in the southwestern and southeastern states of Nigeria — including Ogun, Ondo, Edo, Delta, Cross River, and Akwa Ibom — where rainfall is high enough (>1,500 mm) for dense forest growth.",
        "h": "Tropical rainforest: southwestern + southeastern Nigeria.",
        "yr": "SS1"
      },
      {
        "q": "Lake Chad is geographically important to Nigeria because:",
        "o": ["It is Nigeria's largest freshwater lake entirely within its borders", "It is a shared border lake that provides water and fish for northeastern Nigeria", "It produces the most petroleum in Nigeria", "Lake Chad is not related to Nigeria geographically"],
        "a": 1,
        "e": "Lake Chad is a shared inland lake bordering Nigeria's northeastern region (Borno and Yobe states). It provides water for drinking, irrigation, and fishing for communities in the Lake Chad Basin.",
        "h": "Lake Chad: northeastern Nigeria; provides water, irrigation, fishing.",
        "yr": "SS1"
      },
      {
        "q": "The Sahel Savanna vegetation zone in far northern Nigeria is characterised by:",
        "o": ["Dense tropical trees and heavy rainfall", "Tall grass and thick woodland", "Sparse drought-resistant grasses and shrubs; semi-arid conditions", "Mangrove swamps and waterlogged soils"],
        "a": 2,
        "e": "The Sahel Savanna borders the Sahara Desert. It features sparse, short drought-resistant grasses, shrubs, and a few hardy trees like the acacia. Rainfall is below 500 mm and unreliable.",
        "h": "Sahel Savanna: sparse grass + shrubs; semi-arid; near Sahara.",
        "yr": "SS1"
      },
      {
        "q": "The Niger-Benue Trough is a geographical feature in Nigeria that refers to:",
        "o": ["A mountain range in central Nigeria", "The broad river valleys of the Niger and Benue rivers creating a low-lying zone", "The highland plateau in eastern Nigeria", "The swampy coastal regions of the Niger Delta"],
        "a": 1,
        "e": "The Niger-Benue Trough is a broad zone of low-lying land formed by the river valleys of the Niger (flowing south) and Benue (flowing west). It divides Nigeria's central and northern regions.",
        "h": "Niger-Benue Trough = broad low-lying river valley zone in central Nigeria.",
        "yr": "SS1"
      },
      {
        "q": "Farming activities in the Sahel Savanna of northern Nigeria include:",
        "o": ["Cocoa and rubber farming", "Oil palm and timber production", "Millet, sorghum, and cattle herding (nomadic pastoralism)", "Rice and fishing in mangrove swamps"],
        "a": 2,
        "e": "The Sahel Savanna supports drought-tolerant crops like millet and sorghum. It is also the zone of nomadic pastoralism (Fulani cattle herding) — the sparse rainfall cannot support tree crops.",
        "h": "Sahel farming: millet, sorghum + cattle herding (nomadic pastoralism).",
        "yr": "SS1"
      },
      {
        "q": "The Eastern Highlands of Nigeria include which highlands?",
        "o": ["Udi Plateau and Yoruba Highlands", "Obudu Plateau and Shebshi Mountains", "Mambilla Plateau only", "Jos Plateau and Chad Basin"],
        "a": 1,
        "e": "The Eastern Highlands include the Obudu Plateau (in Cross River State, famous for Obudu Cattle Ranch), the Shebshi Mountains, and the Gotel Mountains (containing Nigeria's highest peak, Chappal Waddi).",
        "h": "Eastern Highlands: Obudu Plateau, Shebshi Mountains, Gotel Mountains.",
        "yr": "SS1"
      },
      {
        "q": "The Cross River is important to southeastern Nigeria because it:",
        "o": ["Flows into the Niger at Lokoja", "Is Nigeria's longest river", "Drains the southeastern states into the Atlantic Ocean", "Is the source of drinking water for Lagos"],
        "a": 2,
        "e": "The Cross River drains southeastern Nigeria (Cross River, Akwa Ibom states) before emptying into the Bight of Biafra (Atlantic Ocean). The Cross River National Park along its banks is a major biodiversity hotspot.",
        "h": "Cross River drains southeastern Nigeria into the Atlantic.",
        "yr": "SS1"
      },
      {
        "q": "The Sudan Savanna in north-central Nigeria supports which major crop?",
        "o": ["Cocoa and rubber", "Mangrove timber", "Cotton and groundnuts", "Cassava and yam"],
        "a": 2,
        "e": "The Sudan Savanna supports cotton, groundnuts, and millet production. Cotton (Kano, Kaduna) and groundnuts (Kano, Katsina, Zamfara) are major cash crops of this zone.",
        "h": "Sudan Savanna crops: cotton, groundnuts, millet.",
        "yr": "SS1"
      },
      {
        "q": "Why is the Niger Delta important to Nigeria's national economy?",
        "o": ["It is the most densely populated area", "It contains Nigeria's largest petroleum deposits and offshore oil fields", "It produces the most food in Nigeria", "It is the centre of Nigeria's manufacturing industry"],
        "a": 1,
        "e": "The Niger Delta (Bayelsa, Delta, Rivers, Cross River, Akwa Ibom states) contains the majority of Nigeria's onshore and offshore petroleum reserves. Oil and gas exports account for over 80% of Nigeria's government revenue.",
        "h": "Niger Delta = Nigeria's petroleum heartland (majority of oil reserves).",
        "yr": "SS1"
      },
      {
        "q": "Which of the following pairs correctly matches a vegetation zone with its description?",
        "o": ["Mangrove: found in northern Nigeria; drought-resistant plants", "Guinea Savanna: most extensive zone; tall grass with trees in central Nigeria", "Sahel: dense rainforest near the Equator", "Rainforest: found only in the north; sparse grass"],
        "a": 1,
        "e": "Guinea Savanna is correctly described as Nigeria's most extensive vegetation zone, covering much of central Nigeria with tall grasses and trees. This is a frequently tested fact.",
        "h": "Guinea Savanna = most extensive; tall grass + trees; central Nigeria.",
        "yr": "SS1"
      },
      {
        "q": "The Northern Plains of Nigeria are used mainly for:",
        "o": ["Offshore oil extraction", "Dense tropical forest logging", "Large-scale crop farming (millet, sorghum) and cattle grazing", "Coastal fishing and mangrove harvesting"],
        "a": 2,
        "e": "The flat Northern Plains support large-scale farming of drought-tolerant crops (millet, sorghum, groundnuts) and extensive cattle grazing (Fulani pastoralism). The flat terrain aids mechanised farming.",
        "h": "Northern Plains: millet, sorghum, groundnuts, cattle grazing.",
        "yr": "SS1"
      },
      {
        "q": "The Obudu Plateau in Cross River State is notable for:",
        "o": ["Its tin and columbite deposits", "Its cool climate, scenic beauty, and the Obudu Cattle Ranch (now Obudu Mountain Resort)", "Being the source of the Niger River", "Being the lowest point in Nigeria"],
        "a": 1,
        "e": "The Obudu Plateau is notable for its cool, refreshing highland climate and the famous Obudu Cattle Ranch (now Obudu Mountain Resort) — a major tourist attraction in Nigeria.",
        "h": "Obudu Plateau: cool climate + Obudu Mountain Resort (tourist attraction).",
        "yr": "SS1"
      },
      {
        "q": "Lake Chad has been shrinking in recent decades mainly because of:",
        "o": ["Increased rainfall causing the lake to overflow its banks", "Reduced rainfall, increased irrigation withdrawals, and climate change", "Volcanic activity in the lake basin", "Government policies to drain the lake"],
        "a": 1,
        "e": "Lake Chad has shrunk to about 10% of its 1960s size due to reduced rainfall (climate change), increased water withdrawal for irrigation by four bordering countries, and desertification in the Sahel region.",
        "h": "Lake Chad shrinking: reduced rainfall + over-irrigation + climate change.",
        "yr": "SS1"
      },
      {
        "q": "The Niger River is important to Nigeria because it:",
        "o": ["Separates Nigeria from Cameroon", "Provides water, fish, irrigation, and hydroelectric power, and created the fertile Niger Delta", "Only serves as a political boundary", "Is entirely in northern Nigeria"],
        "a": 1,
        "e": "The Niger River is Nigeria's most important river: it provides fresh water for drinking and irrigation, supports fishing communities, generates hydroelectric power (Kainji Dam), and created the fertile Niger Delta lowlands.",
        "h": "Niger River: water, fish, irrigation, HEP (Kainji Dam), Niger Delta.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Population of Nigeria",
    "topicCode": "SS1-GEO-16",
    "module": "Human Geography",
    "term": "Third Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  Nigeria is Africa's most populous country with over <span class=\"learn-keyword\">200 million people</span>. Understanding Nigeria's population — its size, distribution, density, and growth — is essential for planning education, healthcare, infrastructure, and economic development.\n</div>\n\n<h3 class=\"learn-subheading\">1. Population Size and Growth</h3>\n<p class=\"learn-p\">Nigeria's population is approximately 216 million (2023 estimate). At independence in 1960 it was about 45 million. The population has grown rapidly due to high birth rates and declining death rates.</p>\n<ul class=\"learn-list\">\n  <li><strong>Natural growth</strong> = Birth rate − Death rate.</li>\n  <li>Nigeria's birth rate ≈ 37 per 1,000; death rate ≈ 11 per 1,000.</li>\n</ul>\n\n<h3 class=\"learn-subheading\">2. Population Distribution and Density</h3>\n<p class=\"learn-p\">Population is <strong>unevenly distributed</strong>. Dense areas: Lagos, Kano, Onitsha. Sparse areas: Niger State, Taraba, Borno.</p>\n<p class=\"learn-p\">Population density = Total population ÷ Total area. Nigeria's average ≈ 218 persons/km².</p>\n\n<h3 class=\"learn-subheading\">3. Factors Influencing Population Distribution</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Factor</th><th>Effect</th></tr></thead>\n    <tbody>\n      <tr><td>Relief and Terrain</td><td>Flat fertile land attracts settlement; swamps and mountains repel</td></tr>\n      <tr><td>Climate and Rainfall</td><td>Adequate rainfall attracts farming populations</td></tr>\n      <tr><td>Economic Activities</td><td>Industrial and commercial areas attract workers</td></tr>\n      <tr><td>Security</td><td>Insecure areas are depopulated; safe areas attract migrants</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Population density = total population ÷ area. Nigeria ≈ 200+ million (Africa's most populous). Dense: Lagos, Kano. Sparse: Niger State, Taraba. Natural increase = Birth rate − Death rate.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Nigeria: 200+ million, Africa's most. Uneven distribution. Density = pop ÷ area. Factors: terrain, climate, economy, security.\n</div>\n  ",
    "questions": [
      {
        "q": "Nigeria is described as Africa's most populous country because:",
        "o": ["It has the largest land area in Africa", "Its population of over 200 million is more than any other African country", "It has the highest birth rate in the world", "It receives the most immigrants in Africa"],
        "a": 1,
        "e": "Nigeria's population exceeds 200 million, making it more populous than any other African country. About 1 in 6 Africans is Nigerian.",
        "h": "Nigeria = Africa's most populous (200+ million).",
        "yr": "SS1"
      },
      {
        "q": "Population density is calculated as:",
        "o": ["Total population × Total area", "Total area ÷ Total population", "Total population ÷ Total area (persons per km²)", "Birth rate − Death rate"],
        "a": 2,
        "e": "Population density = Total population ÷ Total area, expressed in persons per km². It shows how crowded a given area is.",
        "h": "Population density = total population ÷ area (persons/km²).",
        "yr": "SS1"
      },
      {
        "q": "Natural population growth rate is calculated as:",
        "o": ["Birth rate + Death rate", "Birth rate − Death rate", "Immigration rate − Emigration rate", "Total population ÷ Area"],
        "a": 1,
        "e": "Natural increase = Birth rate − Death rate. If more people are being born than dying, the population grows naturally.",
        "h": "Natural increase = Birth rate − Death rate.",
        "yr": "SS1"
      },
      {
        "q": "Which part of Nigeria has the highest population density?",
        "o": ["Taraba State", "Niger State", "Lagos State", "Borno State"],
        "a": 2,
        "e": "Lagos State has the highest population density in Nigeria — over 4,000 persons per km² in some areas. Its small land area combined with its huge urban population makes it extremely dense.",
        "h": "Lagos State = highest population density in Nigeria.",
        "yr": "SS1"
      },
      {
        "q": "Sparsely populated areas in Nigeria include Taraba and Niger States because:",
        "o": ["They have no rivers or resources", "They have large land areas but relatively small populations due to terrain and low economic activity", "The government has banned settlement there", "They have extreme cold weather"],
        "a": 1,
        "e": "Taraba and Niger States are among Nigeria's largest states but have relatively small populations due to rugged terrain, dense forests, limited infrastructure, and lower economic activities.",
        "h": "Niger, Taraba: large area + small population = low density.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is a PHYSICAL factor influencing population distribution in Nigeria?",
        "o": ["Government building new roads", "Industrial development attracting workers", "Fertile soil and adequate rainfall attracting farmers", "Security agencies providing safety"],
        "a": 2,
        "e": "Fertile soil and adequate rainfall are physical (natural) factors that attract agricultural populations. Areas with good soils attract farmers, leading to dense rural settlement.",
        "h": "Physical factor: fertile soil + adequate rainfall → dense farming population.",
        "yr": "SS1"
      },
      {
        "q": "Urban centres like Lagos and Kano are densely populated because:",
        "o": ["They have the most fertile farmland", "They offer employment, services, trade, and economic opportunities that attract migration", "The government forces people to live there", "They receive the most rainfall in Nigeria"],
        "a": 1,
        "e": "Lagos and Kano are commercial and industrial centres offering employment, schools, hospitals, and markets. This attracts rural-urban migrants, increasing their populations.",
        "h": "Urban centres: employment, services, trade → attract migrants → dense.",
        "yr": "SS1"
      },
      {
        "q": "The population of Nigeria at independence in 1960 was approximately:",
        "o": ["10 million", "45 million", "100 million", "200 million"],
        "a": 1,
        "e": "Nigeria's population at independence in 1960 was approximately 45 million. It has grown dramatically to over 200 million today due to high birth rates and declining death rates.",
        "h": "Nigeria at independence 1960 ≈ 45 million people.",
        "yr": "SS1"
      },
      {
        "q": "A high birth rate combined with a declining death rate causes:",
        "o": ["Population decrease", "Stable population with no growth", "Rapid natural population increase", "Population ageing"],
        "a": 2,
        "e": "When the birth rate significantly exceeds the death rate, there is a large natural increase in population. Nigeria's high birth rate (~37/1,000) and declining death rate (~11/1,000) drive rapid growth.",
        "h": "High birth rate − low death rate = rapid natural population increase.",
        "yr": "SS1"
      },
      {
        "q": "Insecurity in some parts of northern Nigeria has caused:",
        "o": ["Increased population in those areas", "Population displacement — people fleeing to safer areas (IDPs)", "Higher birth rates in those states", "More industrial development"],
        "a": 1,
        "e": "Insecurity from Boko Haram insurgency in northeastern Nigeria has caused massive population displacement — millions of Internally Displaced Persons (IDPs) fleeing to safer areas.",
        "h": "Insecurity → population displacement (IDPs flee to safer areas).",
        "yr": "SS1"
      },
      {
        "q": "Population distribution is described as 'uneven' in Nigeria because:",
        "o": ["The government controls where people live", "Some areas are densely populated while others are sparsely populated", "All states in Nigeria have the same number of people", "Nigeria has no rural areas"],
        "a": 1,
        "e": "Nigeria's population is unevenly distributed — some areas have very high densities (Lagos, Kano) while others have very low densities (Niger, Taraba, Borno states).",
        "h": "Uneven distribution: some areas dense (Lagos), some sparse (Niger State).",
        "yr": "SS1"
      },
      {
        "q": "If Nigeria has a population of 200 million and an area of 923,768 km², the average density is approximately:",
        "o": ["20 persons/km²", "100 persons/km²", "216 persons/km²", "2,000 persons/km²"],
        "a": 2,
        "e": "Population density = 200,000,000 ÷ 923,768 ≈ 216 persons/km². This is the national average — actual density varies enormously by state.",
        "h": "200 million ÷ 923,768 km² ≈ 216 persons/km².",
        "yr": "SS1"
      },
      {
        "q": "Rural-urban migration in Nigeria increases urban population density because:",
        "o": ["People move from cities to villages", "People move from rural areas to cities seeking jobs and better services", "The government relocates rural people to cities", "Urban areas have more rainfall"],
        "a": 1,
        "e": "Rural-urban migration is the movement of people from villages to cities. In Nigeria, millions migrate to Lagos, Abuja, and Kano seeking employment and better living conditions.",
        "h": "Rural-urban migration: people move to cities for jobs → increases urban density.",
        "yr": "SS1"
      },
      {
        "q": "Moving Nigeria's capital to Abuja led to:",
        "o": ["Decreased population in Abuja", "Rapid population growth in the FCT due to government employment and services", "No change in population distribution", "Only military personnel living in Abuja"],
        "a": 1,
        "e": "Moving the capital to Abuja triggered rapid population growth in the FCT. Government employment, construction, and services attracted millions of migrants from across Nigeria.",
        "h": "Capital relocation to Abuja → rapid population growth in the FCT.",
        "yr": "SS1"
      },
      {
        "q": "Why are swampy areas like the Niger Delta relatively sparsely settled despite their resources?",
        "o": ["They have no economic resources to attract people", "The waterlogged, flood-prone terrain and malaria make dense settlement difficult", "The government has restricted settlement there", "The Niger Delta has the coldest climate in Nigeria"],
        "a": 1,
        "e": "Despite petroleum wealth, much of the Niger Delta remains sparsely settled because waterlogged terrain makes construction difficult, flooding is frequent, and malaria is a major health risk.",
        "h": "Niger Delta: waterlogged terrain + flooding + malaria → sparse settlement.",
        "yr": "SS1"
      },
      {
        "q": "A population census is important for Nigeria's planning because:",
        "o": ["It tells us about weather patterns", "It provides accurate population data for planning services and infrastructure", "It counts only urban populations", "It is only for international organisations"],
        "a": 1,
        "e": "A census provides accurate data on how many people live where — essential for planning schools, hospitals, roads, food production, and resource allocation.",
        "h": "Census: accurate population data → informs planning.",
        "yr": "SS1"
      },
      {
        "q": "Over-population refers to a situation where:",
        "o": ["A country has too many resources for its population", "The population exceeds the capacity of available resources to support it adequately", "The birth rate equals the death rate", "Immigration exceeds emigration"],
        "a": 1,
        "e": "Over-population occurs when a population exceeds the carrying capacity of its environment — resources (land, food, water, jobs) are insufficient to provide an adequate standard of living.",
        "h": "Over-population = population exceeds available resources.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is an effect of rapid population growth in Nigeria?",
        "o": ["Reduced pressure on schools and hospitals", "Increased demand for food, housing, infrastructure, and services", "Decreased traffic congestion in cities", "Lower unemployment rates"],
        "a": 1,
        "e": "Rapid population growth creates enormous pressure on resources — schools, hospitals, housing, water, energy, and jobs. Nigeria's infrastructure struggles to keep up.",
        "h": "Rapid growth → increased demand for food, housing, services.",
        "yr": "SS1"
      },
      {
        "q": "Which geopolitical zone of Nigeria is the most densely populated?",
        "o": ["North-East", "North-West", "South-West (particularly Lagos)", "North-Central"],
        "a": 2,
        "e": "The South-West geopolitical zone, particularly Lagos State, is the most densely populated zone in Nigeria. Lagos alone has an estimated 15–20 million people.",
        "h": "South-West (Lagos) = most densely populated geopolitical zone.",
        "yr": "SS1"
      },
      {
        "q": "The carrying capacity of an area refers to:",
        "o": ["The weight of goods a vehicle can carry", "The maximum population an area can support sustainably given its resources", "The military strength of a region", "The number of schools an area can build"],
        "a": 1,
        "e": "Carrying capacity is the maximum population an area can support sustainably, taking into account available land, water, food, and other resources.",
        "h": "Carrying capacity = maximum population an area can sustainably support.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Resources of Nigeria",
    "topicCode": "SS1-GEO-17",
    "module": "Human Geography",
    "term": "Third Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  Nigeria is endowed with abundant <span class=\"learn-keyword\">natural resources</span> (oil, gas, solid minerals, land, water, forests) and significant <span class=\"learn-keyword\">human resources</span>. Proper management of these resources is crucial for national development.\n</div>\n\n<h3 class=\"learn-subheading\">1. Types of Resources</h3>\n<ul class=\"learn-list\">\n  <li><strong>Natural Resources:</strong> Land, water, forests, minerals, wildlife.</li>\n  <li><strong>Human Resources:</strong> People and their skills, knowledge, and labour.</li>\n  <li><strong>Capital Resources:</strong> Machinery, money, and infrastructure.</li>\n</ul>\n\n<h3 class=\"learn-subheading\">2. Nigeria's Key Natural Resources</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Resource</th><th>Location</th><th>Use</th></tr></thead>\n    <tbody>\n      <tr><td>Petroleum (Crude Oil)</td><td>Niger Delta, offshore</td><td>~80% of government revenue</td></tr>\n      <tr><td>Natural Gas</td><td>Niger Delta</td><td>Energy, LNG export</td></tr>\n      <tr><td>Coal</td><td>Enugu, Kogi</td><td>Energy, industry</td></tr>\n      <tr><td>Tin</td><td>Jos Plateau</td><td>Manufacturing</td></tr>\n      <tr><td>Iron Ore</td><td>Itakpe, Kogi</td><td>Steel (Ajaokuta)</td></tr>\n      <tr><td>Limestone</td><td>Cross River, Ogun, Sokoto</td><td>Cement production</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">3. Human Resources</h3>\n<p class=\"learn-p\">Nigeria has a large, youthful population — about 60% under 25. Challenges: unemployment, inadequate education, brain drain (skilled emigration).</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Petroleum (Niger Delta) = ~80% revenue. Tin = Jos Plateau. Coal = Enugu. Iron ore = Itakpe, Kogi. Limestone = cement. Brain drain = skilled Nigerians emigrating.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Natural resources: petroleum, gas, coal, tin, iron ore, limestone. Human resources: large youthful workforce. Oil dominates revenue. Challenges: oil dependence, brain drain.\n</div>\n  ",
    "questions": [
      {
        "q": "Natural resources are defined as:",
        "o": ["Resources created by human technology", "Resources that occur naturally without human creation", "Only petroleum and gas", "Resources found only in Africa"],
        "a": 1,
        "e": "Natural resources are materials occurring naturally in the environment — minerals, land, water, forests, and wildlife — before human modification or extraction.",
        "h": "Natural resources = occur naturally (not created by humans).",
        "yr": "SS1"
      },
      {
        "q": "Nigeria's most important mineral resource in terms of export revenue is:",
        "o": ["Coal", "Tin", "Limestone", "Petroleum (crude oil)"],
        "a": 3,
        "e": "Petroleum accounts for about 80% of government revenue and over 90% of export earnings — making it by far Nigeria's most important mineral resource.",
        "h": "Petroleum = Nigeria's most important resource (~80% of government revenue).",
        "yr": "SS1"
      },
      {
        "q": "Petroleum in Nigeria is found mainly in:",
        "o": ["The Jos Plateau region", "The Niger Delta and offshore in the Gulf of Guinea", "Northern Nigeria near Lake Chad", "The Adamawa Highlands"],
        "a": 1,
        "e": "Nigeria's petroleum is found mainly in the Niger Delta states (Rivers, Bayelsa, Delta, Cross River, Akwa Ibom) and offshore in the Gulf of Guinea.",
        "h": "Petroleum: Niger Delta + offshore Gulf of Guinea.",
        "yr": "SS1"
      },
      {
        "q": "Tin ore in Nigeria is mined mainly on the:",
        "o": ["Niger Delta", "Coastal lowlands", "Jos Plateau", "Sokoto Plains"],
        "a": 2,
        "e": "The Jos Plateau is Nigeria's major tin-mining area. Tin and columbite have been mined there since the early 20th century.",
        "h": "Tin mining = Jos Plateau.",
        "yr": "SS1"
      },
      {
        "q": "Coal is found in Nigeria mainly in:",
        "o": ["Kano and Kaduna", "Enugu and Kogi states", "Lagos and Ogun states", "Bayelsa and Delta states"],
        "a": 1,
        "e": "Nigeria's coal is found mainly in Enugu State (Udi coalfields) and Kogi State. It is bituminous coal used for energy and industrial purposes.",
        "h": "Coal = Enugu State (Udi coalfields) and Kogi State.",
        "yr": "SS1"
      },
      {
        "q": "Iron ore is found in Nigeria at:",
        "o": ["Itakpe, Kogi State", "Lagos, Lagos State", "Aba, Abia State", "Maiduguri, Borno State"],
        "a": 0,
        "e": "The major iron ore deposit is at Itakpe in Kogi State, meant to supply the Ajaokuta Steel Company — Nigeria's biggest steel project.",
        "h": "Iron ore = Itakpe, Kogi State (for Ajaokuta Steel).",
        "yr": "SS1"
      },
      {
        "q": "Limestone in Nigeria is important mainly for:",
        "o": ["Petroleum refining", "Gold and silver extraction", "Cement production", "Tin smelting"],
        "a": 2,
        "e": "Limestone is the main raw material for cement production. Nigeria has large deposits in Cross River, Ogun, and Sokoto states used by major cement companies.",
        "h": "Limestone = raw material for cement production.",
        "yr": "SS1"
      },
      {
        "q": "Human resources refer to:",
        "o": ["Natural oil and gas reserves", "People and their skills, knowledge, and labour available for production", "Machinery and capital equipment", "Agricultural land and water bodies"],
        "a": 1,
        "e": "Human resources are the people in an economy — their number, skills, education, health, and work capacity. Nigeria's large population is a significant human resource if properly educated.",
        "h": "Human resources = people's skills, knowledge, and labour.",
        "yr": "SS1"
      },
      {
        "q": "'Brain drain' in Nigeria refers to:",
        "o": ["Loss of intelligence due to poor nutrition", "Emigration of highly skilled and educated Nigerians to other countries", "Students failing examinations in large numbers", "Reduction in university enrolment"],
        "a": 1,
        "e": "Brain drain is the emigration of Nigeria's highly educated professionals (doctors, engineers, scientists) to developed countries offering better pay and conditions.",
        "h": "Brain drain = skilled Nigerians emigrating to other countries.",
        "yr": "SS1"
      },
      {
        "q": "Nigeria is said to have a 'youthful population' because:",
        "o": ["Nigerians live much longer than others", "About 60% of Nigeria's population is under 25 years of age", "Nigeria has no elderly people", "Most Nigerians start working at age 5"],
        "a": 1,
        "e": "About 60% of Nigeria's population is under 25 years old, making it one of the world's youngest nations. This creates a large potential workforce but also pressure on schools and jobs.",
        "h": "Nigeria's youthful population ≈ 60% under 25 years.",
        "yr": "SS1"
      },
      {
        "q": "Nigeria's over-dependence on petroleum is a problem because:",
        "o": ["Petroleum is very cheap to extract", "It makes the economy vulnerable to oil price fluctuations and neglects other sectors", "Petroleum is a renewable resource", "Oil production creates unlimited employment"],
        "a": 1,
        "e": "Over-dependence on petroleum makes Nigeria's economy vulnerable to global oil price drops and leads to neglect of agriculture, solid minerals, and manufacturing.",
        "h": "Oil dependence = vulnerable to price fluctuations; neglects other sectors.",
        "yr": "SS1"
      },
      {
        "q": "The Kainji Dam on the Niger River is an example of using which type of resource?",
        "o": ["Solid mineral extraction", "Agricultural land use", "Water resources for hydroelectric power", "Forest exploitation"],
        "a": 2,
        "e": "The Kainji Dam harnesses the Niger River's water flow to generate hydroelectric power — an example of using water as a natural energy resource.",
        "h": "Kainji Dam = hydroelectric power from Niger River (water resource).",
        "yr": "SS1"
      },
      {
        "q": "Nigeria's agricultural resources include all EXCEPT:",
        "o": ["Cocoa in the southwest", "Palm oil in the south", "Groundnuts in the north", "Petroleum in the Niger Delta"],
        "a": 3,
        "e": "Petroleum is a mineral resource, not an agricultural resource. Agricultural resources include cocoa, palm oil, groundnuts, and cotton.",
        "h": "Petroleum = mineral resource, NOT agricultural resource.",
        "yr": "SS1"
      },
      {
        "q": "Nigeria exports natural gas mainly as:",
        "o": ["Solid blocks of frozen gas", "Liquefied Natural Gas (LNG) through the Bonny Island plant", "Piped gas directly to Europe", "Compressed gas in cylinders"],
        "a": 1,
        "e": "Nigeria exports natural gas as LNG through the Nigeria LNG facility on Bonny Island, Rivers State — one of the world's largest LNG exporters.",
        "h": "Nigeria exports gas as LNG (Bonny Island LNG plant).",
        "yr": "SS1"
      },
      {
        "q": "Forests in Nigeria provide:",
        "o": ["Petroleum and natural gas", "Tin and columbite", "Timber, rubber, fruits, medicines, and watershed protection", "Iron ore for steel"],
        "a": 2,
        "e": "Nigerian forests provide timber, wild rubber, fruits, medicinal plants, and ecosystem services like watershed protection and carbon storage.",
        "h": "Forests: timber, rubber, fruits, medicines, watershed protection.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is a renewable resource?",
        "o": ["Petroleum", "Coal", "Natural gas", "Agricultural land and solar energy"],
        "a": 3,
        "e": "Agricultural land (when properly managed) and solar energy are renewable. Petroleum, coal, and natural gas are non-renewable fossil fuels.",
        "h": "Renewable: agricultural land, solar, wind. Non-renewable: petroleum, coal, gas.",
        "yr": "SS1"
      },
      {
        "q": "The Ajaokuta Steel Company in Kogi State was established to:",
        "o": ["Refine petroleum from the Niger Delta", "Process iron ore from Itakpe into steel for national development", "Generate electricity for central Nigeria", "Produce cement from limestone"],
        "a": 1,
        "e": "The Ajaokuta Steel Company was designed to use iron ore from Itakpe (Kogi) to produce steel, reducing dependence on imported steel for construction and manufacturing.",
        "h": "Ajaokuta = processes Itakpe iron ore into steel.",
        "yr": "SS1"
      },
      {
        "q": "Resource management is important in Nigeria because:",
        "o": ["Resources are unlimited and need no management", "Proper management ensures resources are used sustainably for present and future generations", "Only foreign countries should manage Nigeria's resources", "Resource management only applies to farmland"],
        "a": 1,
        "e": "Resource management ensures Nigeria's resources are used efficiently and sustainably so they remain available for future generations and benefit the entire population.",
        "h": "Resource management: efficient + sustainable use for present and future.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is an example of water resources used for agriculture?",
        "o": ["Kainji Dam generating electricity", "Lagos Harbour for shipping", "Irrigation schemes along the Sokoto-Rima River for rice and wheat farming", "Lake Chad as a tourist attraction"],
        "a": 2,
        "e": "Irrigation schemes along the Sokoto-Rima River allow dry-season farming of rice, wheat, and vegetables in northern Nigeria — an agricultural use of water resources.",
        "h": "Irrigation = agricultural use of water (Sokoto-Rima Valley).",
        "yr": "SS1"
      },
      {
        "q": "Nigeria's economic diversification aims to:",
        "o": ["Stop all oil production immediately", "Develop agriculture, manufacturing, and solid minerals to reduce dependence on volatile oil revenues", "Export all remaining oil reserves quickly", "Focus only on renewable energy"],
        "a": 1,
        "e": "Economic diversification aims to reduce Nigeria's dangerous over-dependence on crude oil by developing other revenue-generating sectors — agriculture, manufacturing, technology, and solid mineral mining.",
        "h": "Diversification = develop other sectors to reduce oil dependence.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Basic Concepts in Map Reading",
    "topicCode": "SS1-GEO-18",
    "module": "Cartography",
    "term": "Third Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  A <span class=\"learn-keyword\">map</span> is a representation of the Earth's surface on a flat surface. Map reading requires understanding <span class=\"learn-keyword\">scale</span>, <span class=\"learn-keyword\">direction</span>, <span class=\"learn-keyword\">symbols</span>, and <span class=\"learn-keyword\">conventional signs</span>.\n</div>\n\n<h3 class=\"learn-subheading\">1. Scale</h3>\n<ul class=\"learn-list\">\n  <li><strong>Statement scale:</strong> \"1 cm represents 1 km\".</li>\n  <li><strong>Representative Fraction (RF):</strong> 1:100,000 = 1 unit on map = 100,000 units on ground.</li>\n  <li><strong>Linear (graphical) scale:</strong> A bar divided into units on the map.</li>\n  <li>Large scale (e.g., 1:10,000) = small area, more detail.</li>\n  <li>Small scale (e.g., 1:1,000,000) = large area, less detail.</li>\n</ul>\n\n<h3 class=\"learn-subheading\">2. Direction and Bearing</h3>\n<p class=\"learn-p\">Four cardinal points: <strong>N, S, E, W</strong>. Four intermediate: NE, NW, SE, SW.</p>\n<p class=\"learn-p\"><strong>Bearing:</strong> Measured clockwise from North (000°–360°). E=090°; S=180°; W=270°.</p>\n\n<h3 class=\"learn-subheading\">3. Conventional Signs</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Feature</th><th>Symbol</th></tr></thead>\n    <tbody>\n      <tr><td>Main road</td><td>Double solid line</td></tr>\n      <tr><td>Railway</td><td>Line with cross-hatch marks</td></tr>\n      <tr><td>River</td><td>Blue wavy line</td></tr>\n      <tr><td>Forest</td><td>Green shading or tree symbols</td></tr>\n      <tr><td>Contour lines</td><td>Brown lines of equal elevation</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Large scale = more detail, smaller area. Small scale = less detail, larger area. E=090°, S=180°, W=270°, NW=315°. Railway = cross-hatch; river = blue wavy; contours = brown lines.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Scale (statement, RF, linear). Direction (N,S,E,W + bearings). Conventional signs represent real features. Contours = equal elevation lines.\n</div>\n  ",
    "questions": [
      {
        "q": "A map scale of 1:50,000 means:",
        "o": ["1 metre on the map = 50 metres on the ground", "1 cm on the map = 50,000 cm (500 m) on the ground", "50,000 metres on the map = 1 metre on the ground", "The map covers 50,000 km²"],
        "a": 1,
        "e": "RF of 1:50,000 means every 1 unit on the map represents 50,000 units on the ground. So 1 cm = 50,000 cm = 500 m = 0.5 km on the ground.",
        "h": "1:50,000 = 1 cm on map = 50,000 cm (500 m) on ground.",
        "yr": "SS1"
      },
      {
        "q": "A 'large scale' map compared to a 'small scale' map:",
        "o": ["Shows a larger area with less detail", "Shows a smaller area with more detail", "Has a smaller representative fraction number only", "Is always 1:1,000,000"],
        "a": 1,
        "e": "A large scale map (e.g., 1:10,000) shows a small area in great detail. A small scale map (e.g., 1:1,000,000) shows a large area with little detail.",
        "h": "Large scale = small area, more detail (e.g., 1:10,000).",
        "yr": "SS1"
      },
      {
        "q": "The Representative Fraction (RF) expresses scale as:",
        "o": ["A statement like '1 cm represents 5 km'", "A bar divided into units", "A ratio such as 1:100,000", "A colour code"],
        "a": 2,
        "e": "The RF expresses scale as a dimensionless ratio — e.g., 1:100,000 — meaning one unit on the map equals 100,000 of the same units on the ground.",
        "h": "RF = ratio (e.g., 1:100,000).",
        "yr": "SS1"
      },
      {
        "q": "On most maps, North is located:",
        "o": ["At the bottom", "At the left side", "At the top", "In the centre"],
        "a": 2,
        "e": "By cartographic convention, North is at the top of most maps. A north arrow or compass rose confirms this orientation.",
        "h": "North is conventionally at the TOP of a map.",
        "yr": "SS1"
      },
      {
        "q": "The four cardinal points of the compass are:",
        "o": ["North, North-East, South, West", "North, South, East, West", "North, South, North-East, South-West", "Up, Down, Left, Right"],
        "a": 1,
        "e": "The four cardinal compass points are North, South, East, and West. The intermediate points are NE, NW, SE, and SW.",
        "h": "Four cardinal points: N, S, E, W.",
        "yr": "SS1"
      },
      {
        "q": "A bearing of 090° represents which direction?",
        "o": ["North", "East", "South", "West"],
        "a": 1,
        "e": "Bearings are measured clockwise from North. East = 090°; South = 180°; West = 270°; North = 000°/360°.",
        "h": "Bearing 090° = East.",
        "yr": "SS1"
      },
      {
        "q": "A bearing of 270° represents which direction?",
        "o": ["North", "East", "South", "West"],
        "a": 3,
        "e": "Bearings: N=000°, E=090°, S=180°, W=270°. A bearing of 270° is West.",
        "h": "Bearing 270° = West.",
        "yr": "SS1"
      },
      {
        "q": "Contour lines on a map represent:",
        "o": ["Roads and railways", "Rivers and lakes", "Lines connecting points of equal elevation above sea level", "Boundaries between countries"],
        "a": 2,
        "e": "Contour lines connect all points at the same elevation. Close contours = steep slope; widely spaced = gentle slope.",
        "h": "Contour lines = connect equal elevation points.",
        "yr": "SS1"
      },
      {
        "q": "Closely spaced contour lines indicate:",
        "o": ["A flat, gently sloping plain", "A steep slope", "A river valley", "A lowland depression"],
        "a": 1,
        "e": "Closely spaced contour lines show rapid elevation change over a short distance — indicating a steep slope. Widely spaced contours indicate a gentle slope.",
        "h": "Closely spaced contours = steep slope.",
        "yr": "SS1"
      },
      {
        "q": "The conventional sign for a railway on a map is:",
        "o": ["A blue wavy line", "A double solid line", "A line with cross-hatch marks", "A dotted line"],
        "a": 2,
        "e": "Railways are shown as a line with regular cross-hatch (perpendicular tick) marks — representing railway sleepers.",
        "h": "Railway = line with cross-hatch marks.",
        "yr": "SS1"
      },
      {
        "q": "On a 1:200,000 map, a road measures 5 cm. The actual length is:",
        "o": ["1 km", "5 km", "10 km", "20 km"],
        "a": 2,
        "e": "Actual = 5 cm × 200,000 = 1,000,000 cm = 10,000 m = 10 km.",
        "h": "5 × 200,000 = 1,000,000 cm = 10 km.",
        "yr": "SS1"
      },
      {
        "q": "A map legend (key) is used to:",
        "o": ["Show the direction of North", "Explain the symbols and conventional signs used on the map", "Mark the scale", "Show elevation of hills"],
        "a": 1,
        "e": "A map legend explains what each symbol and sign on the map represents. Without it, map users cannot correctly interpret the information.",
        "h": "Legend/key = explains what symbols mean.",
        "yr": "SS1"
      },
      {
        "q": "Rivers and water bodies on topographic maps are usually shown in:",
        "o": ["Red", "Green", "Blue", "Brown"],
        "a": 2,
        "e": "Rivers, lakes, and other water bodies are conventionally shown in blue. Contour lines are brown; vegetation is green.",
        "h": "Rivers/water = blue on topographic maps.",
        "yr": "SS1"
      },
      {
        "q": "A triangulation (trig) point on a map indicates:",
        "o": ["A river source", "A benchmark of known, precise elevation used in surveying", "A market location", "A settlement boundary"],
        "a": 1,
        "e": "A trig point is a point of known, precisely measured elevation used in surveying. It is shown as a triangle with a dot on maps.",
        "h": "Trig point = benchmark of known precise elevation.",
        "yr": "SS1"
      },
      {
        "q": "The bearing of South-West (SW) is:",
        "o": ["045°", "135°", "225°", "315°"],
        "a": 2,
        "e": "SW = halfway between S(180°) and W(270°) = 225°. Intercardinal bearings: NE=045°, SE=135°, SW=225°, NW=315°.",
        "h": "SW = 225°.",
        "yr": "SS1"
      },
      {
        "q": "The purpose of a north arrow on a map is:",
        "o": ["To show the highest point", "To indicate the direction of North for correct orientation", "To mark international boundaries", "To show river flow direction"],
        "a": 1,
        "e": "A north arrow indicates which direction is North, allowing the user to correctly orient the map and determine directions.",
        "h": "North arrow = indicates North for orientation.",
        "yr": "SS1"
      },
      {
        "q": "A scale of 1:25,000 is larger or smaller than 1:250,000?",
        "o": ["Smaller — it shows more area", "Larger — it shows more detail of a smaller area", "They are the same", "1:25,000 shows the whole country"],
        "a": 1,
        "e": "1:25,000 is a larger scale (smaller denominator) showing a smaller area in greater detail than 1:250,000.",
        "h": "Smaller denominator = larger scale = more detail.",
        "yr": "SS1"
      },
      {
        "q": "The contour interval is:",
        "o": ["The distance between two contour lines on the ground", "The difference in elevation between adjacent contour lines", "The spacing between grid squares", "The length of contour lines on the map"],
        "a": 1,
        "e": "The contour interval is the difference in height between consecutive contour lines. If contours are at 100m, 120m, 140m, the interval is 20m.",
        "h": "Contour interval = height difference between adjacent contours.",
        "yr": "SS1"
      },
      {
        "q": "If you face North and turn 90° clockwise, you face:",
        "o": ["South", "East", "West", "Northeast"],
        "a": 1,
        "e": "Starting from North (000°) and turning 90° clockwise = East (090°).",
        "h": "North + 90° clockwise = East.",
        "yr": "SS1"
      },
      {
        "q": "Which scale would be used for a map of all of Africa?",
        "o": ["1:10,000", "1:50,000", "1:500,000", "1:50,000,000"],
        "a": 3,
        "e": "A map of an entire continent requires a very small scale (large denominator) like 1:50,000,000. Scales like 1:10,000 are for small local areas.",
        "h": "Continent map = very small scale (large denominator).",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Map Distances",
    "topicCode": "SS1-GEO-19",
    "module": "Cartography",
    "term": "Third Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  Measuring distances on maps and converting them to actual ground distances using the map <span class=\"learn-keyword\">scale</span> is a fundamental cartographic skill.\n</div>\n\n<h3 class=\"learn-subheading\">1. Straight-line Distance</h3>\n<p class=\"learn-p\">Use a ruler to measure map distance in cm, then: <strong>Actual distance = Map distance × Scale denominator</strong>.</p>\n<p class=\"learn-p\"><strong>Example:</strong> 3 cm on a 1:100,000 map = 3 × 100,000 = 300,000 cm = 3 km.</p>\n\n<h3 class=\"learn-subheading\">2. Winding/Curved Distance</h3>\n<ul class=\"learn-list\">\n  <li><strong>String method:</strong> Lay thread along the route; measure thread length; apply scale.</li>\n  <li><strong>Paperedge method:</strong> Mark bends on paper edge; straighten and measure.</li>\n  <li><strong>Dividers method:</strong> Walk fixed intervals along route; count steps × interval.</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. Key Formulas</h3>\n<ul class=\"learn-list\">\n  <li>Actual = Map distance × Denominator</li>\n  <li>Map distance = Actual ÷ Denominator</li>\n  <li>1 km = 1,000 m = 100,000 cm</li>\n</ul>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Always convert to same units before calculating. 1 km = 100,000 cm. Curved routes: string or paperedge. Actual = map × denominator; Map = actual ÷ denominator.\n</div>\n  ",
    "questions": [
      {
        "q": "To find the actual distance from a map, you need:",
        "o": ["The colour of the route", "The map scale and the distance measured on the map", "The number of contour lines crossed", "The year the map was made"],
        "a": 1,
        "e": "To convert a map measurement to actual ground distance, you need the map distance (measured with a ruler) and the map scale. Actual = Map distance × Scale denominator.",
        "h": "Actual distance requires: map distance + map scale.",
        "yr": "SS1"
      },
      {
        "q": "On a 1:50,000 map, two villages are 4 cm apart. The actual distance is:",
        "o": ["2 km", "4 km", "20 km", "200 km"],
        "a": 0,
        "e": "Actual = 4 × 50,000 = 200,000 cm = 2,000 m = 2 km.",
        "h": "4 × 50,000 = 200,000 cm = 2 km.",
        "yr": "SS1"
      },
      {
        "q": "The best method to measure distance along a winding road on a map is:",
        "o": ["Using a straight ruler only", "Counting bends in the road", "Using a piece of thread laid along the road, then measuring it", "Counting grid squares the road passes through"],
        "a": 2,
        "e": "The string method is ideal for curved routes. Thread is laid carefully along the road, then straightened and measured with a ruler, then converted using the scale.",
        "h": "Winding route: string method (lay thread → measure → convert).",
        "yr": "SS1"
      },
      {
        "q": "If a road is 10 km long, how long on a 1:500,000 map?",
        "o": ["2 cm", "5 cm", "10 cm", "20 cm"],
        "a": 0,
        "e": "10 km = 1,000,000 cm. Map = 1,000,000 ÷ 500,000 = 2 cm.",
        "h": "10 km = 1,000,000 cm ÷ 500,000 = 2 cm.",
        "yr": "SS1"
      },
      {
        "q": "On a 1:100,000 map, a river measures 6.5 cm. The actual length is:",
        "o": ["6.5 km", "65 km", "650 km", "0.65 km"],
        "a": 0,
        "e": "Actual = 6.5 × 100,000 = 650,000 cm = 6,500 m = 6.5 km.",
        "h": "6.5 × 100,000 = 650,000 cm = 6.5 km.",
        "yr": "SS1"
      },
      {
        "q": "The paperedge method involves:",
        "o": ["Drawing a straight line between two points", "Marking the route along a paper strip edge, then measuring against the scale", "Using contour lines to estimate distance", "Counting grid squares"],
        "a": 1,
        "e": "Place paper edge along the road marking bends with pencil marks. Straighten the paper and measure the total length against the map scale bar.",
        "h": "Paperedge: mark route on paper edge → straighten → measure.",
        "yr": "SS1"
      },
      {
        "q": "1 km equals how many centimetres?",
        "o": ["1,000 cm", "10,000 cm", "100,000 cm", "1,000,000 cm"],
        "a": 2,
        "e": "1 km = 1,000 m = 100,000 cm. This conversion is essential for map distance calculations.",
        "h": "1 km = 1,000 m = 100,000 cm.",
        "yr": "SS1"
      },
      {
        "q": "A settlement is 15 km from a school. On a 1:200,000 map, the map distance is:",
        "o": ["3 cm", "7.5 cm", "15 cm", "30 cm"],
        "a": 1,
        "e": "15 km = 1,500,000 cm. Map = 1,500,000 ÷ 200,000 = 7.5 cm.",
        "h": "15 km = 1,500,000 cm ÷ 200,000 = 7.5 cm.",
        "yr": "SS1"
      },
      {
        "q": "The dividers method measures distance by:",
        "o": ["Laying thread along the route", "Setting a fixed interval on dividers and walking them along the route, counting steps", "Drawing a straight line", "Using coloured pens to trace the route"],
        "a": 1,
        "e": "With dividers set to a fixed distance, 'walk' them along the route and count the number of steps. Total = steps × fixed interval.",
        "h": "Dividers: fixed interval × steps = distance.",
        "yr": "SS1"
      },
      {
        "q": "If the scale says '1 cm represents 5 km', what type of scale is this?",
        "o": ["Representative Fraction (RF)", "Statement (verbal) scale", "Linear (graphical) scale", "Ratio scale"],
        "a": 1,
        "e": "A statement (verbal) scale expresses the relationship in words: '1 cm represents 5 km'.",
        "h": "Statement scale = expressed in words.",
        "yr": "SS1"
      },
      {
        "q": "A linear (graphical) scale is useful because:",
        "o": ["It shows scale as a fraction", "It remains accurate even if the map is enlarged or reduced", "It requires a calculator", "It only works for straight lines"],
        "a": 1,
        "e": "A linear scale printed on the map is automatically rescaled if the map is photocopied larger or smaller — unlike RF or statement scales.",
        "h": "Linear scale stays correct even if map size changes.",
        "yr": "SS1"
      },
      {
        "q": "Two points are 8 cm apart on a 1:25,000 map. The actual distance is:",
        "o": ["200 m", "1 km", "2 km", "8 km"],
        "a": 2,
        "e": "Actual = 8 × 25,000 = 200,000 cm = 2,000 m = 2 km.",
        "h": "8 × 25,000 = 200,000 cm = 2 km.",
        "yr": "SS1"
      },
      {
        "q": "When calculating actual distance from map distance, you must ensure:",
        "o": ["The map is coloured", "Both measurements are in the same units", "You use only metric units", "The map is a topographic map"],
        "a": 1,
        "e": "Units must be consistent. If you measure in cm and the denominator is in cm, the answer is in cm. Always convert to km at the end.",
        "h": "Units must match before calculating.",
        "yr": "SS1"
      },
      {
        "q": "Which conversion is correct?",
        "o": ["1 km = 100 m", "1 km = 1,000 m = 100,000 cm", "1 km = 10,000 cm", "1 m = 1,000 cm"],
        "a": 1,
        "e": "1 km = 1,000 m = 100,000 cm. This is the key conversion chain.",
        "h": "1 km = 1,000 m = 100,000 cm.",
        "yr": "SS1"
      },
      {
        "q": "If a road is 3.5 cm on a 1:200,000 map, the actual length is:",
        "o": ["3.5 km", "7 km", "14 km", "35 km"],
        "a": 1,
        "e": "Actual = 3.5 × 200,000 = 700,000 cm = 7,000 m = 7 km.",
        "h": "3.5 × 200,000 = 700,000 cm = 7 km.",
        "yr": "SS1"
      },
      {
        "q": "For accurate measurement of a curved river, which method is recommended?",
        "o": ["Straight ruler only", "Counting grid squares", "String or paperedge method", "Estimating by eye"],
        "a": 2,
        "e": "The string or paperedge method is most accurate for curved features because they follow all bends precisely.",
        "h": "Curved features: string or paperedge is most accurate.",
        "yr": "SS1"
      },
      {
        "q": "A distance of 500 m on the ground equals what on a 1:100,000 map?",
        "o": ["0.5 cm", "5 cm", "50 cm", "500 cm"],
        "a": 0,
        "e": "500 m = 50,000 cm. Map = 50,000 ÷ 100,000 = 0.5 cm.",
        "h": "500 m = 50,000 cm ÷ 100,000 = 0.5 cm.",
        "yr": "SS1"
      },
      {
        "q": "What does RF 1:1,000,000 mean?",
        "o": ["1 km on the map = 1,000 km", "1 cm on the map represents 10 km on the ground", "The map is very detailed", "The map covers only a small area"],
        "a": 1,
        "e": "1:1,000,000 means 1 cm = 1,000,000 cm = 10 km on the ground — a small-scale map.",
        "h": "1:1,000,000 → 1 cm = 10 km.",
        "yr": "SS1"
      },
      {
        "q": "Scale changes from 1:50,000 to 1:25,000. Distances on the new map appear:",
        "o": ["Twice as long", "Half as long", "The same length", "Three times as long"],
        "a": 0,
        "e": "Halving the denominator doubles the linear scale — features appear twice as large.",
        "h": "Denominator halved → distances appear twice as long.",
        "yr": "SS1"
      },
      {
        "q": "On a 1:100,000 map, 500 m on the ground = how many cm on the map?",
        "o": ["0.5 cm", "5 cm", "50 cm", "500 cm"],
        "a": 0,
        "e": "500 m = 50,000 cm. Map distance = 50,000 ÷ 100,000 = 0.5 cm.",
        "h": "500 m ÷ 100,000 = 0.5 cm.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Map Reduction and Enlargement",
    "topicCode": "SS1-GEO-20",
    "module": "Cartography",
    "term": "Third Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Map enlargement</span> and <span class=\"learn-keyword\">reduction</span> change the scale of a map while maintaining proportional accuracy.\n</div>\n\n<h3 class=\"learn-subheading\">1. Key Concepts</h3>\n<ul class=\"learn-list\">\n  <li><strong>Enlargement:</strong> Smaller RF denominator → bigger map, more detail.</li>\n  <li><strong>Reduction:</strong> Larger RF denominator → smaller map, less detail.</li>\n</ul>\n\n<h3 class=\"learn-subheading\">2. Scale Factor</h3>\n<p class=\"learn-p\">Linear Scale Factor (SF) = Old denominator ÷ New denominator.</p>\n<p class=\"learn-p\"><strong>Example:</strong> 1:50,000 → 1:25,000: SF = 50,000 ÷ 25,000 = 2 (enlarged ×2).</p>\n\n<h3 class=\"learn-subheading\">3. Effect on Area</h3>\n<p class=\"learn-p\">Area changes by linear SF². If SF=2 (linear): area × 4. If SF=3: area × 9.</p>\n\n<h3 class=\"learn-subheading\">4. Grid Method</h3>\n<p class=\"learn-p\">Draw a proportionally larger/smaller grid on the new map and transfer features square by square.</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Linear SF = old ÷ new denominator. Area SF = linear SF². Enlargement: smaller denominator. Reduction: larger denominator.\n</div>\n  ",
    "questions": [
      {
        "q": "When a map is enlarged, the resulting map has:",
        "o": ["A smaller scale (larger denominator)", "A larger scale (smaller denominator) showing more detail", "The same scale as before", "An unrelated scale"],
        "a": 1,
        "e": "Enlarging a map produces a larger scale (smaller RF denominator) — showing the same area in more detail on a larger sheet.",
        "h": "Enlargement → larger scale (smaller denominator, more detail).",
        "yr": "SS1"
      },
      {
        "q": "A map originally at 1:100,000 is enlarged to 1:50,000. The linear scale factor is:",
        "o": ["0.5 (halved)", "2 (doubled)", "4 (quadrupled)", "100"],
        "a": 1,
        "e": "Linear SF = old ÷ new = 100,000 ÷ 50,000 = 2. All distances are doubled on the new map.",
        "h": "SF = 100,000 ÷ 50,000 = 2.",
        "yr": "SS1"
      },
      {
        "q": "If a map is enlarged by linear scale factor 3, areas change by:",
        "o": ["×3", "×6", "×9 (3²)", "×27"],
        "a": 2,
        "e": "Area changes by linear SF² = 3² = 9.",
        "h": "Area factor = linear SF² = 3² = 9.",
        "yr": "SS1"
      },
      {
        "q": "The grid/square method of map enlargement works by:",
        "o": ["Tracing the map onto larger paper", "Drawing a proportionally larger grid and transferring features square by square", "Photographing and magnifying the map", "Using a ruler to stretch all distances"],
        "a": 1,
        "e": "Draw a grid of squares on both original and new paper in correct proportion. Carefully redraw features square by square.",
        "h": "Grid method: draw proportional grid → transfer features square by square.",
        "yr": "SS1"
      },
      {
        "q": "A map is reduced from 1:25,000 to 1:75,000. The linear reduction factor is:",
        "o": ["1/3 (reduced to one-third)", "3 (tripled)", "1/9", "9"],
        "a": 0,
        "e": "Linear SF = 25,000 ÷ 75,000 = 1/3. Reduced to one-third linear dimensions.",
        "h": "SF = 25,000 ÷ 75,000 = 1/3.",
        "yr": "SS1"
      },
      {
        "q": "When enlarged by linear SF=2, a feature covering 10 cm² now covers:",
        "o": ["10 cm²", "20 cm²", "40 cm²", "100 cm²"],
        "a": 2,
        "e": "Area SF = 2² = 4. New area = 10 × 4 = 40 cm².",
        "h": "Area factor = 2² = 4; 10 × 4 = 40 cm².",
        "yr": "SS1"
      },
      {
        "q": "What is the new scale if a 1:200,000 map is enlarged by linear factor 4?",
        "o": ["1:800,000", "1:50,000", "1:200,000", "1:4,000"],
        "a": 1,
        "e": "New denominator = 200,000 ÷ 4 = 50,000. New scale = 1:50,000.",
        "h": "200,000 ÷ 4 = 50,000 → 1:50,000.",
        "yr": "SS1"
      },
      {
        "q": "Why must proportional accuracy be maintained when enlarging or reducing maps?",
        "o": ["To make the map more attractive", "So distances, areas, and shapes remain correctly represented relative to each other", "To reduce printing costs", "To fit standard paper sizes"],
        "a": 1,
        "e": "Proportional accuracy ensures spatial relationships remain correct after enlargement or reduction — without it, distances and areas would be distorted.",
        "h": "Proportional accuracy: all features maintain correct spatial relationships.",
        "yr": "SS1"
      },
      {
        "q": "A distance of 8 cm on a 1:50,000 map becomes what on a 1:100,000 map (same area)?",
        "o": ["4 cm", "8 cm", "16 cm", "20 cm"],
        "a": 0,
        "e": "Going from 1:50,000 to 1:100,000 doubles the denominator, halving linear distances. 8 ÷ 2 = 4 cm.",
        "h": "Denominator doubles → distances halved → 8 ÷ 2 = 4 cm.",
        "yr": "SS1"
      },
      {
        "q": "New grid squares for map enlargement should be:",
        "o": ["Same size as original", "Proportionally larger by the scale factor", "Drawn randomly", "Smaller than original"],
        "a": 1,
        "e": "For enlargement, new grid squares must be proportionally larger by the linear scale factor. If SF=2, each 1×1 cm square becomes a 2×2 cm square.",
        "h": "New grid squares = original × scale factor.",
        "yr": "SS1"
      },
      {
        "q": "A map is reduced by linear factor 2 (1:50,000 → 1:100,000). Area measurements will:",
        "o": ["Stay the same", "Be reduced to half", "Be reduced to one-quarter", "Be reduced to one-eighth"],
        "a": 2,
        "e": "Area changes by linear factor² = 2² = 4. Reduced to one-quarter.",
        "h": "Linear factor 2 reduction → area ÷ 4.",
        "yr": "SS1"
      },
      {
        "q": "Scale factor when converting from 1:10,000 to 1:40,000:",
        "o": ["4 (enlarged)", "1/4 (reduced to quarter)", "2", "1/2"],
        "a": 1,
        "e": "SF = 10,000 ÷ 40,000 = 1/4. The map is reduced to one-quarter.",
        "h": "SF = 10,000 ÷ 40,000 = 1/4.",
        "yr": "SS1"
      },
      {
        "q": "A forest covers 25 cm². Map enlarged by factor 3. Forest now covers:",
        "o": ["75 cm²", "150 cm²", "225 cm²", "675 cm²"],
        "a": 2,
        "e": "Area factor = 3² = 9. New area = 25 × 9 = 225 cm².",
        "h": "3² = 9; 25 × 9 = 225 cm².",
        "yr": "SS1"
      },
      {
        "q": "New scale after reducing 1:25,000 by factor 5:",
        "o": ["1:5,000", "1:125,000", "1:25,005", "1:30,000"],
        "a": 1,
        "e": "New denominator = 25,000 × 5 = 125,000. New scale = 1:125,000.",
        "h": "25,000 × 5 = 125,000 → 1:125,000.",
        "yr": "SS1"
      },
      {
        "q": "An advantage of the grid method for map enlargement is:",
        "o": ["Requires sophisticated computer equipment", "Can be done by hand with just a ruler and pencil", "Automatically maintains colour", "Only works for rectangular maps"],
        "a": 1,
        "e": "The grid method requires only a ruler and pencil. It is inexpensive and accessible — ideal for students.",
        "h": "Grid method: manual (ruler + pencil only).",
        "yr": "SS1"
      },
      {
        "q": "1:10,000 map reduced to 1:20,000. The town on the reduced map appears:",
        "o": ["Larger", "Same size", "Smaller (half linear size, quarter area)", "Unaffected"],
        "a": 2,
        "e": "Denominator doubles → linear dimensions halved, areas quartered. The town appears smaller.",
        "h": "1:10,000 → 1:20,000: linear ÷2, area ÷4 → smaller.",
        "yr": "SS1"
      },
      {
        "q": "When reducing a map, what happens to visible features?",
        "o": ["More features become visible", "Fewer features shown (smaller scale = less detail)", "Same number always shown", "Only rivers disappear"],
        "a": 1,
        "e": "Reducing a map (smaller scale) means less detail can be shown. Minor roads and small features may be omitted.",
        "h": "Reduced map = smaller scale = less detail → fewer features.",
        "yr": "SS1"
      },
      {
        "q": "'Map enlarged by factor of 2' means:",
        "o": ["Map covers twice the land area", "All distances on the new map are twice those on the original", "New map has twice as many contour lines", "Original was drawn at twice the size"],
        "a": 1,
        "e": "Enlarged by factor 2 means all linear distances on the new map are twice the original. A 3 cm road becomes 6 cm.",
        "h": "Enlarged ×2 → all distances ×2.",
        "yr": "SS1"
      },
      {
        "q": "The purpose of reducing a map is:",
        "o": ["To make it less accurate", "To fit a larger area onto smaller paper while maintaining proportional representation", "To increase detail", "To change its shape"],
        "a": 1,
        "e": "Map reduction allows a large area to be represented on manageable paper, with all spatial relationships maintained proportionally.",
        "h": "Map reduction: fit larger area on smaller paper proportionally.",
        "yr": "SS1"
      },
      {
        "q": "When enlarging a map from 1:100,000 to 1:25,000, the scale factor is:",
        "o": ["0.25", "2", "4", "25"],
        "a": 2,
        "e": "SF = 100,000 ÷ 25,000 = 4. All linear distances are multiplied by 4; areas are multiplied by 16.",
        "h": "SF = 100,000 ÷ 25,000 = 4.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Interpretation of Physical and Cultural Features",
    "topicCode": "SS1-GEO-21",
    "module": "Cartography",
    "term": "Third Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  Map interpretation means reading <span class=\"learn-keyword\">physical features</span> (relief, drainage) and <span class=\"learn-keyword\">cultural features</span> (settlement, roads, land use) on topographic maps to understand the landscape.\n</div>\n\n<h3 class=\"learn-subheading\">1. Physical Features</h3>\n<ul class=\"learn-list\">\n  <li><strong>Relief:</strong> Steep = closely spaced contours. Flat = widely spaced.</li>\n  <li><strong>Hill:</strong> Concentric rings; higher values inside.</li>\n  <li><strong>Valley:</strong> V-shaped contours pointing upstream.</li>\n  <li><strong>Plateau:</strong> Wide flat top (wide contours) + steep edges (close contours).</li>\n  <li><strong>Rivers:</strong> Flow from high to low contour values (blue wavy lines).</li>\n</ul>\n\n<h3 class=\"learn-subheading\">2. Cultural Features</h3>\n<ul class=\"learn-list\">\n  <li>Settlements: dots, names, or shaded built-up areas.</li>\n  <li>Roads: main = double line; minor = single; footpath = dotted.</li>\n  <li>Railways: line with cross-hatch marks.</li>\n  <li>Boundaries: dashed/dotted lines.</li>\n  <li>Forest: green shading or tree symbols.</li>\n</ul>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Rivers flow HIGH → LOW contour values. V-shaped contours point UPSTREAM. Hill = concentric rings. Steep = closely spaced contours. Settlement on flat fertile valley = physical geography controls human activity.\n</div>\n  ",
    "questions": [
      {
        "q": "On a topographic map, a hill is represented by:",
        "o": ["Widely spaced parallel contours", "Contours forming concentric rings with higher values inside", "Blue wavy lines", "A dashed boundary line"],
        "a": 1,
        "e": "A hill is shown by concentric (roughly circular) contour lines with higher elevation values toward the centre.",
        "h": "Hill = concentric contour rings; higher values toward centre.",
        "yr": "SS1"
      },
      {
        "q": "Rivers flow on a map from:",
        "o": ["Lower to higher contour values", "Higher contour values toward lower ones", "The sea toward the source", "East to west always"],
        "a": 1,
        "e": "Rivers always flow from high ground to low ground — from higher contour values toward lower values.",
        "h": "Rivers flow HIGH → LOW contour values.",
        "yr": "SS1"
      },
      {
        "q": "V-shaped contours crossing a river valley point toward:",
        "o": ["The sea (downstream)", "Upstream (toward source, uphill)", "The nearest town", "The steepest slope"],
        "a": 1,
        "e": "V-shaped contours in a valley point upstream because the valley cuts into rising ground — the tip always points uphill.",
        "h": "V-shaped contours point UPSTREAM (uphill).",
        "yr": "SS1"
      },
      {
        "q": "An escarpment is shown on a topographic map by:",
        "o": ["Very widely spaced contours", "Very closely spaced contours on one side indicating a steep drop", "Blue wavy lines", "Dotted boundary lines"],
        "a": 1,
        "e": "An escarpment shows very closely spaced or nearly touching contour lines on the steep side.",
        "h": "Escarpment = very closely spaced contours (steep side).",
        "yr": "SS1"
      },
      {
        "q": "Settlements on a topographic map are shown by:",
        "o": ["Blue wavy lines", "Contour rings", "Dots, names, or shaded built-up areas", "Cross-hatch lines"],
        "a": 2,
        "e": "Settlements are shown as dots (villages), written names, or shaded areas (towns/cities).",
        "h": "Settlements: dots (villages), names, or shaded areas (towns).",
        "yr": "SS1"
      },
      {
        "q": "Roads are distinguished from railways on maps because:",
        "o": ["Roads are blue; railways are red", "Roads are plain lines; railways have cross-hatch tick marks", "Roads are dotted lines", "No difference in symbols"],
        "a": 1,
        "e": "Roads are plain double or single lines; railways are distinguished by cross-hatch (perpendicular tick) marks.",
        "h": "Roads = plain lines; Railways = lines with cross-hatch marks.",
        "yr": "SS1"
      },
      {
        "q": "A col (saddle) on a map is identified by:",
        "o": ["Concentric rings pointing to a peak", "A low point between two hills with hourglass-shaped contours", "A wide flat plateau", "A steep cliff face"],
        "a": 1,
        "e": "A col or saddle is a low point between two hills, shown by an hourglass or figure-eight contour pattern.",
        "h": "Col = low point between hills; hourglass contour shape.",
        "yr": "SS1"
      },
      {
        "q": "Swampy ground on a topographic map is indicated by:",
        "o": ["Dense contour lines", "Special symbols like small tufts or dashes representing waterlogged terrain", "A solid blue filled area", "Red hatching"],
        "a": 1,
        "e": "Swampy/marshy ground is shown by special conventional signs — small tufts or reeds representing waterlogged terrain.",
        "h": "Swamp = special symbols (tufts/reed-like marks).",
        "yr": "SS1"
      },
      {
        "q": "Dense settlement along a valley bottom suggests the valley has:",
        "o": ["Steep slopes unsuitable for farming", "Flat, fertile land near water — attractive for settlement and farming", "Dense impenetrable forest", "No roads or transport links"],
        "a": 1,
        "e": "Valley bottoms attract settlement because of flat land, fertile alluvial soils, and access to fresh water.",
        "h": "Valley bottom settlement: flat + fertile alluvial soil + water.",
        "yr": "SS1"
      },
      {
        "q": "A plateau on a topographic map is shown by:",
        "o": ["Many closely packed contours throughout", "Widely spaced contours on top with closely spaced at the steep edges", "Concentric rings pointing to a peak", "V-shapes pointing downstream"],
        "a": 1,
        "e": "A plateau shows widely spaced contours on the flat elevated top, then suddenly closely spaced at the steep edges (escarpments).",
        "h": "Plateau: flat top (wide) + steep edges (close contours).",
        "yr": "SS1"
      },
      {
        "q": "Administrative boundaries on maps are shown by:",
        "o": ["Solid red lines", "Blue lines following rivers", "Dashed or dotted lines", "Contour lines"],
        "a": 2,
        "e": "Administrative boundaries are shown as dashed, dotted, or dash-dot lines to distinguish them from physical features.",
        "h": "Administrative boundaries = dashed or dotted lines.",
        "yr": "SS1"
      },
      {
        "q": "Forest on a topographic map is shown by:",
        "o": ["Brown contour lines", "Blue wavy lines", "Green shading or tree symbols", "Cross-hatch lines"],
        "a": 2,
        "e": "Forests are conventionally shown in green — as green shading or small tree symbols.",
        "h": "Forest = green shading or tree symbols.",
        "yr": "SS1"
      },
      {
        "q": "A ridge on a topographic map is identified by:",
        "o": ["Circular closed contours", "Elongated parallel contours along an elevated spine of land", "V-shaped contours in a valley", "Widely spaced contours on flat land"],
        "a": 1,
        "e": "A ridge is an elongated elevated landform shown by elongated parallel contours running along its length.",
        "h": "Ridge = elongated hill; parallel contours along elevated spine.",
        "yr": "SS1"
      },
      {
        "q": "Absence of settlements in steep, forested terrain suggests:",
        "o": ["The government removed people", "Physical conditions make settlement difficult", "Only rich people live there", "The map was made incorrectly"],
        "a": 1,
        "e": "Steep land and dense forest discourage settlement — difficult to build, clear, or farm.",
        "h": "No settlement in steep/forested areas = unfavourable physical conditions.",
        "yr": "SS1"
      },
      {
        "q": "A bridge symbol on a map indicates:",
        "o": ["A railway crossing over a main road", "A road or railway crossing over a river or valley", "A ford where a river can be crossed on foot", "The highest point of a settlement"],
        "a": 1,
        "e": "A bridge symbol shows where a road or railway crosses over a river or valley — an important transport infrastructure feature.",
        "h": "Bridge = road/railway crossing over a river.",
        "yr": "SS1"
      },
      {
        "q": "Understanding settlement patterns from a topographic map requires examining:",
        "o": ["Only contour lines", "Only rivers", "Relief, drainage, vegetation, soil, and accessibility together", "Only roads and railways"],
        "a": 2,
        "e": "Settlement interpretation requires examining how relief, drainage, soils, vegetation, and accessibility together influence where people choose to live.",
        "h": "Settlement patterns: relief + drainage + soil + accessibility.",
        "yr": "SS1"
      },
      {
        "q": "A footpath on a topographic map is shown as:",
        "o": ["A double solid red line", "A cross-hatched railway symbol", "A thin dotted or dashed single line", "A broad green stripe"],
        "a": 2,
        "e": "Footpaths are shown as thin dotted or dashed single lines — informal walking routes.",
        "h": "Footpath = thin dotted or dashed single line.",
        "yr": "SS1"
      },
      {
        "q": "Which combination suggests an area suitable for dense farming settlement?",
        "o": ["Steep slopes + dense forest + no rivers", "Flat relief + fertile alluvial soil near rivers + good roads", "Desert + no water + extreme relief", "Swampy terrain + high mountains + no roads"],
        "a": 1,
        "e": "Flat relief allows farming and building; alluvial soils are fertile; good roads enable market access. This attracts dense agricultural settlement.",
        "h": "Dense farming: flat + fertile + rivers + roads.",
        "yr": "SS1"
      },
      {
        "q": "A lake or pond on a topographic map is shown by:",
        "o": ["Brown contour lines filling a closed area", "A blue filled area", "Green shading", "A double dashed line"],
        "a": 1,
        "e": "Lakes, ponds, and reservoirs are shown as solid blue filled areas — consistent with the convention of blue for water.",
        "h": "Lake/pond = solid blue filled area.",
        "yr": "SS1"
      },
      {
        "q": "A map shows a stream getting wider downstream. This indicates:",
        "o": ["The stream is losing water through the banks", "The stream is gaining water from tributaries as it flows downhill", "The mapmaker made an error", "The stream flows uphill"],
        "a": 1,
        "e": "Rivers widen downstream as tributaries add more water — increasing discharge. A widening river line shows the stream gaining water as it flows toward lower ground.",
        "h": "River widens downstream: tributaries add water, increasing discharge.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Transportation",
    "topicCode": "SS1-GEO-22",
    "module": "Human Geography",
    "term": "Third Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Transportation</span> is the movement of people, goods, and services from one place to another. Nigeria has four main modes: road, rail, water, and air.\n</div>\n\n<h3 class=\"learn-subheading\">1. Road Transport</h3>\n<p class=\"learn-p\">The most common mode in Nigeria. Over 195,000 km of roads. Flexible, door-to-door. Problems: poor conditions, congestion, accidents.</p>\n\n<h3 class=\"learn-subheading\">2. Rail Transport</h3>\n<p class=\"learn-p\">Cheap for bulk goods. Modernised lines: Lagos-Ibadan, Abuja-Kaduna. Problems: limited network, high construction cost.</p>\n\n<h3 class=\"learn-subheading\">3. Water Transport</h3>\n<p class=\"learn-p\">Cheapest for bulk goods. Rivers: Niger, Benue, Cross River. Ports: Lagos (Apapa), Port Harcourt, Warri. Problems: slow, seasonal, limited to waterways.</p>\n\n<h3 class=\"learn-subheading\">4. Air Transport</h3>\n<p class=\"learn-p\">Fastest, most expensive. International airports: Lagos (Murtala Muhammed), Abuja (Nnamdi Azikiwe), Port Harcourt, Kano. Good for perishables and high-value goods.</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Road = most common. Water = cheapest (bulk). Air = fastest + most expensive. Major ports: Apapa (Lagos), Port Harcourt, Warri. Lagos airport = Murtala Muhammed; Abuja = Nnamdi Azikiwe.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Road (flexible, widespread), Rail (bulk, cheap), Water (cheapest, rivers/coasts), Air (fastest, expensive). Transport promotes trade and development.\n</div>\n  ",
    "questions": [
      {
        "q": "The most common mode of transportation in Nigeria is:",
        "o": ["Rail transport", "Water transport", "Air transport", "Road transport"],
        "a": 3,
        "e": "Road transport is by far the most common mode in Nigeria. Over 90% of passenger and freight movement is by road.",
        "h": "Road transport = most common mode in Nigeria.",
        "yr": "SS1"
      },
      {
        "q": "Which mode of transport is fastest but most expensive?",
        "o": ["Road", "Rail", "Water", "Air"],
        "a": 3,
        "e": "Air transport is the fastest mode, connecting cities in hours. However, it is also the most expensive.",
        "h": "Air transport = fastest + most expensive.",
        "yr": "SS1"
      },
      {
        "q": "Which mode is cheapest for transporting bulk goods?",
        "o": ["Air", "Road", "Water (ocean/river)", "Rail"],
        "a": 2,
        "e": "Water transport is the cheapest for bulk goods — tankers and bulk carriers move vast quantities at very low cost per tonne-kilometre.",
        "h": "Water transport = cheapest for bulk goods.",
        "yr": "SS1"
      },
      {
        "q": "Lagos Murtala Muhammed International Airport is:",
        "o": ["Nigeria's only airport", "Nigeria's busiest airport connecting the country worldwide", "Used only for domestic flights", "Located in Abuja"],
        "a": 1,
        "e": "Murtala Muhammed Airport in Lagos is Nigeria's busiest airport, handling the largest share of international and domestic passengers and cargo.",
        "h": "Murtala Muhammed Airport Lagos = Nigeria's busiest.",
        "yr": "SS1"
      },
      {
        "q": "Nigeria's major seaports include:",
        "o": ["Kano, Kaduna, Sokoto", "Apapa (Lagos), Port Harcourt, Warri", "Enugu, Owerri, Aba", "Abuja, Lokoja, Makurdi"],
        "a": 1,
        "e": "Nigeria's major seaports are Apapa (Lagos), Port Harcourt, and Warri — handling container cargo, petroleum exports, and imports.",
        "h": "Major ports: Apapa (Lagos), Port Harcourt, Warri.",
        "yr": "SS1"
      },
      {
        "q": "A major problem with road transport in Nigeria is:",
        "o": ["It is too expensive", "Most roads are in poor condition with potholes", "There are no roads in rural areas", "Road transport is too slow for any use"],
        "a": 1,
        "e": "A major challenge is poor road conditions — potholes, inadequate drainage, and lack of maintenance cause accidents and delays.",
        "h": "Nigerian road problem: poor condition (potholes, poor maintenance).",
        "yr": "SS1"
      },
      {
        "q": "Rail transport is economical for bulk cargo because:",
        "o": ["Trains are faster than trucks", "Trains carry much larger quantities at lower cost per unit", "Rail lines cost less than roads", "Trains use no fuel"],
        "a": 1,
        "e": "A single freight train can carry hundreds of containers at far lower cost per tonne-km than road — making rail ideal for bulk goods.",
        "h": "Rail: large capacity + low cost per tonne = economical for bulk.",
        "yr": "SS1"
      },
      {
        "q": "An advantage of water transport in Nigeria is:",
        "o": ["It is the fastest mode", "It serves all parts of Nigeria", "It is cheap for heavy goods along rivers and coasts", "It is unaffected by weather"],
        "a": 2,
        "e": "Water transport along Nigeria's rivers (Niger, Benue, Cross River) and coastline is cheap for bulk/heavy goods — especially for coastal and river communities.",
        "h": "Water transport: cheap for heavy goods along rivers and coasts.",
        "yr": "SS1"
      },
      {
        "q": "The Abuja-Kaduna standard gauge railway is significant because:",
        "o": ["It is the only railway in Nigeria", "It is a modern, fast link reducing road congestion between the capital and a major northern city", "It runs between Lagos and Port Harcourt", "It was built in the colonial era"],
        "a": 1,
        "e": "The Abuja-Kaduna line is a modern standard gauge link that significantly reduced travel time and eased highway congestion.",
        "h": "Abuja-Kaduna: modern; reduces road congestion.",
        "yr": "SS1"
      },
      {
        "q": "Nigeria's inland waterway transport uses which rivers?",
        "o": ["Nile and Congo", "Niger, Benue, and Cross Rivers", "Volta and Senegal", "Limpopo and Zambezi"],
        "a": 1,
        "e": "Nigeria's inland waterway transport uses the Niger, Benue, and Cross Rivers managed by the National Inland Waterways Authority (NIWA).",
        "h": "Nigeria's inland waterways: Niger, Benue, Cross Rivers.",
        "yr": "SS1"
      },
      {
        "q": "Air transport is weather-dependent because:",
        "o": ["Aircraft only fly in summer", "Thunderstorms, fog, and low visibility can ground flights", "Air transport only operates in the dry season", "Rainfall improves flying"],
        "a": 1,
        "e": "Severe weather — thunderstorms, fog, strong crosswinds — can delay or cancel flights, making air transport less reliable.",
        "h": "Air transport: storms/fog can ground flights.",
        "yr": "SS1"
      },
      {
        "q": "Transport's importance to economic development includes all EXCEPT:",
        "o": ["Opening remote areas for trade", "Moving goods from farms to markets", "Ensuring goods are sold at the same price everywhere", "Connecting people to employment"],
        "a": 2,
        "e": "Transport does not ensure uniform prices — prices vary with market forces. Transport does open remote areas, facilitate trade, and connect people to jobs.",
        "h": "Transport does NOT ensure uniform prices.",
        "yr": "SS1"
      },
      {
        "q": "The Lagos-Ibadan Expressway is an example of:",
        "o": ["A rail route", "An inland waterway", "A federal highway (road infrastructure)", "An air transport corridor"],
        "a": 2,
        "e": "The Lagos-Ibadan Expressway is a federal highway — a major road artery connecting Nigeria's commercial capital to Southwest's largest inland city.",
        "h": "Lagos-Ibadan Expressway = federal highway.",
        "yr": "SS1"
      },
      {
        "q": "Traffic congestion in Lagos is mainly caused by:",
        "o": ["Too many railway lines crossing roads", "Over-dependence on roads + large population + inadequate infrastructure", "Too many airports", "No water transport"],
        "a": 1,
        "e": "Lagos has severe congestion because millions depend on road transport in a city with inadequate road infrastructure and limited rail/water alternatives.",
        "h": "Lagos traffic: too many road users + inadequate infrastructure.",
        "yr": "SS1"
      },
      {
        "q": "Which goods are MOST appropriately transported by air?",
        "o": ["Iron ore from mines", "Crude petroleum", "Fresh flowers and medical supplies (perishable/high-value)", "Sand and gravel for construction"],
        "a": 2,
        "e": "Air is ideal for perishable goods (fresh flowers, medical organs), time-sensitive items, and high-value lightweight goods where speed justifies cost.",
        "h": "Air cargo: perishables, high-value, time-sensitive goods.",
        "yr": "SS1"
      },
      {
        "q": "Water transport is least useful for communities:",
        "o": ["Near major rivers", "Along the coastline", "In inland savanna far from rivers", "Near the Niger Delta"],
        "a": 2,
        "e": "Water transport is only near rivers and coasts. Inland communities in northern Nigeria's savanna depend entirely on road and rail.",
        "h": "Water transport unavailable in inland areas far from rivers/coast.",
        "yr": "SS1"
      },
      {
        "q": "Modern transportation promotes globalisation because:",
        "o": ["It makes all countries use the same currency", "Fast shipping and air links allow goods, people, and ideas to move quickly between countries", "Transport only benefits local trade", "Global transport is no faster than 100 years ago"],
        "a": 1,
        "e": "Container shipping and jet aviation have dramatically reduced the cost and time of international movement — enabling global trade, tourism, and cultural exchange.",
        "h": "Fast transport = global movement of goods, people, ideas = globalisation.",
        "yr": "SS1"
      },
      {
        "q": "Nnamdi Azikiwe International Airport is located in:",
        "o": ["Lagos", "Port Harcourt", "Kano", "Abuja"],
        "a": 3,
        "e": "Nnamdi Azikiwe International Airport is in Abuja (FCT), named after Nigeria's first President.",
        "h": "Nnamdi Azikiwe Airport = Abuja (FCT).",
        "yr": "SS1"
      },
      {
        "q": "An advantage of road over rail transport is that roads:",
        "o": ["Are cheaper to build per km", "Carry more cargo per journey", "Offer flexible door-to-door service anywhere including villages", "Are always faster"],
        "a": 2,
        "e": "Roads offer flexible door-to-door service — goods can be picked up and delivered anywhere. Rail only serves fixed stations.",
        "h": "Road advantage: flexible door-to-door service anywhere.",
        "yr": "SS1"
      },
      {
        "q": "Improving transport infrastructure in Nigeria would help the economy by:",
        "o": ["Reducing the need for foreign trade", "Reducing agricultural production", "Reducing transport costs and connecting regions for better trade and development", "Making Nigeria self-sufficient in oil"],
        "a": 2,
        "e": "Better transport reduces costs, connects farmers to markets, manufacturers to customers, and different regions — stimulating economic growth.",
        "h": "Better transport: lower costs + connect regions = economic growth.",
        "yr": "SS1"
      }
    ]
  }
];
